import type {
  CameraPose,
  InteractiveObject,
  LocationId,
  PresenceSpace,
  SpawnPoint,
  TravelHubNode,
  WorldSceneryLayer,
} from '@/world-prototype/types';
import { getLocationDisplayName } from '@/world-prototype/graph/worldGraph';
import type { WorldNavigator } from '@/world-prototype/navigation/WorldNavigator';
import {
  PresenceController,
  type PresenceState,
} from '@/world-prototype/movement/PresenceController';
import {
  isRabbitVisible,
  noticeRabbit,
  pokeRabbit,
  rabbitDistance,
  requestRabbitSleep,
  requestRabbitWake,
  stepRabbit,
  RABBIT_COMFORT_RADIUS,
  RABBIT_NOTICE_RADIUS,
  type RabbitState,
  type RabbitYardConfig,
} from '@/world-prototype/creatures/rabbitBehavior';
import { rabbitConfigForYard } from '@/world-prototype/creatures/rabbitYards';
import {
  RABBIT_CANVAS_FEET,
  RABBIT_HIT_MIN_CSS_PX,
  RABBIT_POSE_IMAGE,
  RABBIT_POSE_SRC,
  rabbitStateForYard,
} from '@/world-prototype/creatures/rabbitRuntime';
import {
  pokeBird,
  requestBirdSleep,
  requestBirdWake,
  stepBird,
  BIRD_RADIUS,
  type BirdState,
  type BirdYardConfig,
} from '@/world-prototype/creatures/birdBehavior';
import {
  birdConfigForYard,
  resolveBirdConfig,
} from '@/world-prototype/creatures/birdYards';
import {
  birdStateForYard,
  BIRD_CANVAS_FEET,
  BIRD_HIT_MIN_CSS_PX,
  BIRD_POSE_IMAGE,
  BIRD_POSE_SRC,
} from '@/world-prototype/creatures/birdRuntime';
import {
  beginHeadJump,
  isFrogHeadSequencePhase,
  requestFrogSleep,
  requestFrogWake,
  stepFrog,
  FROG_HIT_MIN_CSS_PX,
  type FrogState,
  type FrogStepContext,
  type FrogYardConfig,
} from '@/world-prototype/creatures/frogBehavior';
import { frogConfigForYard } from '@/world-prototype/creatures/frogYards';
import {
  frogExclusionZonesForYard,
  pointInFrogExclusionZone,
} from '@/world-prototype/creatures/frogExclusionZones';
import {
  frogSleepTarget,
  wildlifeSleepConfigForYard,
} from '@/world-prototype/creatures/wildlifeSleepYards';
import {
  FROG_CANVAS_FEET,
  FROG_POSE_IMAGE,
  FROG_POSE_SRC,
  cancelFrogHeadSequencesForYard,
  frogHeadPerchCallbacks,
  frogStatesForYard,
} from '@/world-prototype/creatures/frogRuntime';
import { characterHeadPerchAnchor, characterHeadPerchAnchorFromDom } from '@/world-prototype/player/characterHeadAnchor';
import {
  fireflyConfigForYard,
  HUBERT_FIREFLY_HOME_BOUNDS,
} from '@/world-prototype/ambience/fireflyYards';
import {
  stepFireflyGroup,
  fireflyOpacity,
  fireflyGlowScale,
  fireflySizeScale,
  type FireflyGroupState,
} from '@/world-prototype/ambience/fireflyBehavior';
import {
  clearFireflyGroupForYard,
  FIREFLY_GLOW_BASE_FRAC,
  FIREFLY_GLOW_MAX_PX,
  FIREFLY_GLOW_MIN_PX,
  fireflyGroupForYard,
} from '@/world-prototype/ambience/fireflyRuntime';
import {
  isTimeOfDayYard,
  timeOfDayForYard,
  TIME_OF_DAY_TRANSITION_MS,
  toggleTimeOfDayForYard,
} from '@/world-prototype/ambience/timeOfDayRuntime';
import {
  largestPineconeDisplaySize,
  pickUnusedPoolIds,
  pineconeCanopyOrigins,
  pineconeDropPositions,
  PINECONE_TREE_FALL_MS,
  PINECONE_TREE_FALL_STAGGER_MS,
  PINECONE_TREE_ID,
  PINECONE_TREE_SHAKE_MS,
  PINECONE_TREE_YARD_ID,
  replacementDropCount,
  replacementEligibleCount,
  samplePineconeTreeFall,
} from '@/world-prototype/interaction/pineconeTree';
import {
  pineconeTreeOnCooldown,
  rememberReplenishedPinecone,
  replenishedPineconesForYard,
  startPineconeTreeCooldown,
  syncReplenishedPineconePose,
} from '@/world-prototype/interaction/pineconeTreeRuntime';
import { placeNatureScatter } from '@/world-prototype/objects/natureScatter';
import { BACKYARD_NATURE_SCATTER_DEFS } from '@/world-prototype/worlds/jacksonvilleBackyardNatureScatter';
import { withResolvedLayout } from '@/world-prototype/camera/backyardPresentation';
import { applyObjectLayoutOverride } from '@/world-prototype/ownerEdit/layoutOverrideTypes';
import { getLayoutOverrides } from '@/world-prototype/ownerEdit/layoutOverrides';
import { creatureDistance } from '@/world-prototype/creatures/creatureGeometry';
import {
  clampToBounds,
  clampToPolygon,
  pointInAnyObstacle,
  pointInPolygon,
} from '@/world-prototype/movement/walkArea';
import { depthFromY } from '@/world-prototype/objects/placeObject';
import { BACKYARD_WALK_REACH_MARKERS } from '@/world-prototype/worlds/jacksonvilleHouse';
import {
  createBackToMapButton,
  isBackToMapUtility,
} from '@/world-prototype/ui/createBackToMapButton';
import { createFriendsButton } from '@/world-prototype/ui/createFriendsButton';
import { createDayNightButton } from '@/world-prototype/ui/createDayNightButton';
import { syncLocationBadge } from '@/world-prototype/ui/syncLocationBadge';
import {
  coverCropInsets,
  explorePanFactor,
  cameraPanTranslatePercent,
  clampCoverPanOffsetPercent,
  resolveExploreZoom,
  visibleWorldHalfExtents,
  type ExplorationCameraMode,
} from '@/world-prototype/camera/responsiveExploreZoom';
import {
  getBackyardPresentationMode as resolveBackyardPresentationMode,
  backyardMasterFor,
  containFrameImagePointToViewport,
  isMobilePresentationPlayfield,
  placesMapContainFrame,
  placesMapContainMaxFraction,
  placesMapMasterFor,
  placesMapMasterSizeFor,
  placesMapOrientationFor,
  placesMapPinBox,
  placesMapPinsFor,
  separatePlacesMapPinBoxes,
  PLACES_MAP_PIN_LABEL_GAP,
  raleighMasterFor,
  spanishForkMasterFor,
  resolveDeviceClass,
  resolvePlacesMapFit,
  resolvePlacesMapPinMetrics,
  usesMobileCoverCamera,
  PLACES_MAP_LETTERBOX_COLOR,
  PLACES_MAP_TITLE,
  type BackyardPresentationMode,
  type DeviceClass,
  type PlacesMapOrientation,
} from '@/world-prototype/camera/backyardPresentation';
import {
  isBackyardLocationId,
  resolveBackyardSceneWorld,
  type SceneWorld,
} from '@/world-prototype/camera/sceneWorld';
import {
  BACKYARD_CAMERA_FOLLOW,
  MOBILE_CAMERA,
  clampFixedWorldPanOffsetPercent,
  fixedWorldPanOffsetPercent,
  frameEditorOverview,
  frameSceneWorld,
  resolveMobileCameraMode,
  type MobileCameraMode,
} from '@/world-prototype/camera/worldCamera';
import {
  applyBackyardPresentation,
  applyRaleighPresentation,
  applySpanishForkPresentation,
} from '@/world-prototype/worlds/jacksonvilleHouse';
import { roundedQuadClipPathPercent, roundedTopQuadClipPathPercent } from '@/world-prototype/objects/quadShape';
import {
  classifyHorizontalSwipe,
  prefersReducedMotion,
  type PointerSample,
} from '@/world-prototype/input/gestures';
import {
  beginTravelLoad,
  finishTravelLoad,
  markTravelPhase,
  preloadImageUrl,
  waitForImageReady,
} from '@/world-prototype/perf/travelLoadProfiler';
import {
  resetPlacesBackyardPreload,
  startPlacesBackyardPreload,
} from '@/world-prototype/perf/placesAssetPreload';
import {
  resolveRuntimeArtUrl,
  runtimeCssUrl,
} from '@/world-prototype/perf/runtimeArtUrl';
import { applySpriteFrameBackground } from '@/world-prototype/objects/spriteFrame';
import { resolveClosestPineconeAtPoint } from '@/world-prototype/objects/objectHotspot';
import {
  BUCKET_APPROACH_DISTANCE,
  BUCKET_HIT_MIN_CSS_PX,
  BUCKET_OBJECT_ID,
  BUCKET_PICKUP_CONTACT_MS,
  BUCKET_PICKUP_TOTAL_MS,
  BUCKET_PLACE_TOTAL_MS,
  CarriedObjectSlot,
  carriedObjectWorldPose,
  BURN_PINECONE_MS,
  createPineconeKickLaunch,
  createPineconeThrowPlan,
  DEPOSIT_PINECONE_MS,
  FIRE_FLARE_MS,
  FIRE_PIT_APPROACH_DISTANCE,
  FIRE_PIT_OBJECT_ID,
  isBucketObject,
  isFirePitObject,
  isKickablePinecone,
  isShedObject,
  kickApproachPoint,
  KICK_ANTICIPATION_MS,
  KICK_APPROACH_DISTANCE,
  KICK_TOTAL_MS,
  LIGHT_FIRE_MS,
  PICKUP_ANTICIPATION_MS,
  PICKUP_APPROACH_DISTANCE,
  PICKUP_CONTACT_MS,
  PICKUP_TOTAL_MS,
  PINECONE_HIT_MIN_CSS_PX,
  PINECONE_LONG_PRESS_MOVE_CANCEL_PX,
  PINECONE_LONG_PRESS_MS,
  PlayerInteractionController,
  samplePineconeThrow,
  SHED_BUCKET_REVEAL_MS,
  SHED_DOOR_CLOSE_MS,
  SHED_DOOR_OPEN_MS,
  SHED_OBJECT_ID,
  SHED_RETRIEVE_ATTACH_MS,
  SHED_STORE_PUT_MS,
  stepPineconeMotion,
  THROW_ANTICIPATION_MS,
  THROW_BOUNCE_MS,
  THROW_TOTAL_CHAR_MS,
  type BucketInstanceRuntime,
  type PineconeInstanceRuntime,
  type PineconeMotionState,
  type PineconeThrowPlan,
  type ShedSequenceState,
} from '@/world-prototype/interaction/playerInteraction';
import {
  burnedPineconeIdsForYard,
  firepitStateForYard,
  markPineconeBurned,
  type FirepitState,
} from '@/world-prototype/interaction/firepitRuntime';
import {
  GARDEN_FOOD_HEIGHT_FEET,
  GARDEN_FOOD_IMAGE,
  GARDEN_FOOD_SRC,
  GARDEN_PULL_MS,
  GARDEN_THROW_CHAR_MS,
  gardenFoodKindForYard,
  gardenHotspotLayout,
  isGardenFoodYard,
  isGardenObject,
  pickGardenThrowTarget,
  pointInGardenHotspot,
} from '@/world-prototype/interaction/gardenFood';
import {
  beginGardenEat,
  beginGardenHarvest,
  beginGardenThrow,
  canHarvestGarden,
  finishGardenEat,
  gardenFoodStateForYard,
  isGardenFoodAvailableToRabbit,
  landGardenFood,
  markGardenFoodTargeted,
  reconcileGardenFoodIdleState,
  tickGardenFoodCooldown,
  isGardenFoodActiveForRabbit,
} from '@/world-prototype/interaction/gardenFoodRuntime';
import {
  isRabbitSleepPhase,
  type GardenFoodTarget,
} from '@/world-prototype/creatures/rabbitBehavior';
import {
  characterBaseWidthFrac,
  feetToHeightFrac,
  yardScaleFor,
} from '@/world-prototype/scale/worldScale';
import {
  getActivePlayableCharacter,
  getActivePlayerCharacterId,
  setActivePlayerCharacterId,
  applyCharacterForBackyard,
  defaultCharacterForBackyard,
  subscribeActivePlayer,
} from '@/world-prototype/player/activePlayer';
import {
  listPlayableCharacters,
} from '@/world-prototype/player/playableCharacters';

const FADE_MS = 160;
/** Soft fade only — never used to mask multi-second loads. */
const TRAVEL_SOFT_FADE_MS = 220;
const FOCUS_MS = 520;
/** Max distance from stand point to open the interaction card after a walk. */
const ARRIVE_FOCUS_SLOP = 0.035;

/** In-bucket pinecone tips reuse the yard scatter atlas (visual only). */
const BUCKET_PINECONE_ATLAS = `${import.meta.env.BASE_URL}art/environments/shared/scatter/pinecones-v1.png`;
const BUCKET_PINECONE_FRAME = { x: 377, y: 37, width: 99, height: 107 };
const BUCKET_PINECONE_ATLAS_W = 1190;
const BUCKET_PINECONE_ATLAS_H = 630;
/** Slight tip rotations per slot for a playful cluster. */
const BUCKET_PINECONE_TIP_ROTS = [-14, 10, -4, 12, -8];

/**
 * Exploration view shell.
 * Storybook spaces: click to walk, calm camera, objects live in the world.
 */
export class WorldPrototypeView {
  private readonly root: HTMLElement;
  private readonly navigator: WorldNavigator;
  private readonly sceneEl: HTMLElement;
  private readonly cameraLabelEl: HTMLElement;
  private readonly locationBadgeEl: HTMLImageElement;
  private readonly cameraPlaceEl: HTMLElement;
  private readonly hudLeftEl: HTMLElement;
  private readonly utilityEl: HTMLElement;
  private readonly playfieldEl: HTMLElement;
  private readonly worldLayerEl: HTMLElement;
  private readonly sceneryEl: HTMLElement;
  private readonly worldArtworkEl: HTMLElement;
  private readonly worldArtworkImgEl: HTMLImageElement;
  private readonly artworkBleedEl: HTMLElement;
  private readonly artworkBleedImgEl: HTMLElement;
  private readonly landscapeBleedEl: HTMLElement;
  private readonly landscapeBleedImgEl: HTMLImageElement;
  private readonly bgCoverDebugEl: HTMLElement;
  private readonly bgCoverDebugReadoutEl: HTMLElement;
  private readonly sceneryLayersEl: HTMLElement;
  /** DEV + `?bgdebug` only — outline cover crop + edge insets. */
  private readonly bgCoverDebugEnabled: boolean;
  private readonly worldHintsEl: HTMLElement;
  private readonly propsEl: HTMLElement;
  private readonly creaturesEl: HTMLElement;
  private readonly porchEl: HTMLElement;
  private readonly porchLabelEl: HTMLElement;
  private readonly characterEl: HTMLElement;
  private readonly characterBodyEl: HTMLImageElement;
  private readonly interactCardEl: HTMLElement;
  private readonly statusEl: HTMLElement;
  private readonly hubPanelEl: HTMLElement;
  private readonly mapTitleEl: HTMLElement;
  private readonly mapPinsEl: HTMLElement;
  private readonly navClusterEl: HTMLElement;
  private readonly friendsBtnEl: HTMLButtonElement;
  private readonly dayNightBtnEl: HTMLButtonElement;
  private readonly friendsPickerEl: HTMLElement;
  private readonly friendsTrackEl: HTMLElement;
  private friendsOpen = false;
  private unsubActivePlayer: (() => void) | null = null;
  private readonly debugEl: HTMLElement;
  private readonly debugWorldBoundsEl: HTMLElement;
  private readonly debugWalkBoundsEl: HTMLElement;
  private readonly debugWalkPolygonEl: SVGElement;
  private readonly debugObstaclesEl: SVGElement;
  private readonly debugVerticesEl: HTMLElement;
  private readonly debugReachMarkersEl: HTMLElement;
  private readonly debugOriginsEl: HTMLElement;
  private readonly debugClickEl: HTMLElement;
  private readonly debugClampEl: HTMLElement;
  private readonly debugObjectsEl: HTMLElement;
  private readonly debugFireflyHomeEl: HTMLElement;
  private readonly debugFireflyPointsEl: HTMLElement;
  private readonly debugCharacterEl: HTMLElement;
  private readonly debugCameraViewportEl: HTMLElement;
  private readonly debugToggleEl: HTMLInputElement;
  private debugDestEl: HTMLElement | null = null;
  private readonly modeBadgeEl: HTMLElement;
  private readonly propTogglesEl: HTMLElement;
  private pointerStart: PointerSample | null = null;
  private gestureConsumed = false;
  private unsubNav: (() => void) | null = null;
  private unsubPresence: (() => void) | null = null;
  private presence: PresenceController | null = null;
  private previousLocationId: LocationId | null = null;
  private currentSpace: PresenceSpace | null = null;
  private lastPose: CameraPose | null = null;
  private exploreZoom = 0.84;
  private exploreMode: ExplorationCameraMode = 'follow';
  /** Locked framing center for landscape phones (patio bias). */
  private lockedFrameCenter: { x: number; y: number } | null = null;
  /** Effective world size factors used for pan math (aspect-aware). */
  private worldPanWidth = 1;
  private worldPanHeight = 1;
  private focusZoomTarget = 0.84 * 1.26;
  private focusZoom = 0.84;
  private focusObject: InteractiveObject | null = null;
  private pendingFocusId: string | null = null;
  /** Last pointer classification for debug (`ground` | `object`). */
  private lastInputKind: 'ground' | 'object' | null = null;
  private lastHitObjectId: string | null = null;
  private focusAnim: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private unbindResize: (() => void) | null = null;
  private debugEnabled = false;
  private interactionLocked = false;
  private readonly spaceChangeListeners = new Set<
    (spaceId: string | null) => void
  >();
  private lastNotifiedSpaceId: string | null | undefined = undefined;
  /** Active backyard visual layout (desktop plate vs mobile plate). */
  private backyardPresentationMode: BackyardPresentationMode | null = null;
  /** Places-map plate orientation (landscape vs portrait masters). */
  private placesMapOrientation: PlacesMapOrientation | null = null;
  /** Device class from playfield size (DESKTOP / TABLET / MOBILE). */
  private deviceClass: DeviceClass | null = null;
  /** World Editor override — null means resolve from viewport. */
  private editorPresentationOverride: BackyardPresentationMode | null = null;
  /**
   * Fixed SceneWorld currently mounted (backyard desktop/mobile).
   * Pixel size is applied on load / plate swap only — never on resize.
   */
  private activeSceneWorld: SceneWorld | null = null;
  /** Last presentation mode logged (for switch / unchanged viewport logs). */
  private lastLoggedPresentationMode: BackyardPresentationMode | null = null;
  private lastLoggedViewportW = 0;
  private lastLoggedViewportH = 0;
  /**
   * Once true, backyard camera has been seeded to the default composition.
   * Cleared on leave / presentation swap so the new world can reseed.
   */
  private backyardCameraSeeded = false;
  /**
   * After a desktop↔mobile plate swap, reseed the camera on the character
   * instead of the default composition (orientation / tablet rotate).
   */
  private plateSwapPreserveCharacter = false;
  /** Last mobile camera mode — detects portrait ↔ landscape switches. */
  private lastMobileCameraMode: MobileCameraMode | null = null;
  /**
   * During map→yard travel, defer decorative prop/decal images until the
   * background plate is decoded so the master isn't bandwidth-starved.
   */
  private deferDecorativeArt = false;
  /** Small reusable interaction state (kick + pick-up pinecone). */
  private readonly playerInteraction = new PlayerInteractionController();
  /** Exactly one carried world object (pinecone OR bucket). */
  private readonly carriedSlot = new CarriedObjectSlot();
  /** Runtime bucket — authored scatter pose stays untouched. */
  private bucketRuntime: BucketInstanceRuntime | null = null;
  /** Atomic shed↔bucket sequence lock (prevents Look / double-trigger). */
  private shedSequence: ShedSequenceState = 'idle';
  private shedClosedSrc: string | null = null;
  /** Fires once when all Hubert pinecones are deposited. */
  private hubertBucketCompleteFired = false;
  /** Pending kick target id while walking to the approach point. */
  private pendingKickId: string | null = null;
  /** Pending pickup target id while walking to the approach point. */
  private pendingPickupId: string | null = null;
  /** Pending non-shed bucket choreography target. */
  private pendingBucketAction:
    | null
    | 'pickup'
    | 'deposit'
    | 'place' = null;
  private pendingDepositBucketId: string | null = null;
  private pendingPlacePoint: { x: number; y: number } | null = null;
  /** Pending firepit light or pinecone burn while walking over. */
  private pendingFirepitAction: null | 'light' | 'burn' = null;
  private firepitFlareTimer: number | null = null;
  /** Walk-to-garden harvest while approaching the stand point. */
  private pendingGardenHarvest = false;
  private gardenFoodEl: HTMLElement | null = null;
  private gardenFoodImgEl: HTMLImageElement | null = null;
  private gardenFoodThrow: {
    plan: PineconeThrowPlan;
    releasedAt: number;
    yardId: string;
    landRot: number;
  } | null = null;
  private gardenFoodThrowRaf: number | null = null;
  private gardenDebugEnabled = false;
  /** Runtime pinecone instances — authored defs stay untouched. */
  private readonly pineconeRuntime = new Map<string, PineconeInstanceRuntime>();
  /** Independent in-flight slides keyed by pinecone id. */
  private readonly pineconeMotions = new Map<
    string,
    PineconeMotionState & { id: string }
  >();
  private pineconeMotionRaf: number | null = null;
  /**
   * Ambient rabbit for the current yard. Null in yards without one.
   * The state object itself is owned per-yard by `rabbitRuntime`, so leaving
   * and returning (or switching Friends) does not restart the rabbit.
   */
  private rabbitConfig: RabbitYardConfig | null = null;
  private rabbitState: RabbitState | null = null;
  private rabbitVisualEl: HTMLElement | null = null;
  private rabbitBodyEl: HTMLImageElement | null = null;
  private rabbitHitEl: HTMLButtonElement | null = null;
  private rabbitRaf: number | null = null;
  private rabbitLastTs: number | null = null;
  private rabbitPoseSrc = '';

  /**
   * Nesting bird for the current yard, with the same per-yard ownership as the
   * rabbit: `birdRuntime` holds the state so a Friends switch or a trip to the
   * map does not reset the bird or move its nest.
   */
  private birdConfig: BirdYardConfig | null = null;
  private birdState: BirdState | null = null;
  private birdVisualEl: HTMLElement | null = null;
  private birdBodyEl: HTMLImageElement | null = null;
  private birdHitEl: HTMLButtonElement | null = null;
  private birdRaf: number | null = null;
  private birdLastTs: number | null = null;
  private birdPoseSrc = '';

  /** Two ambient frogs per yard (Hubert + Holly Springs). */
  private frogConfig: FrogYardConfig | null = null;
  private frogStates: FrogState[] = [];
  private readonly frogVisuals = new Map<
    string,
    {
      visual: HTMLElement;
      body: HTMLImageElement;
      hit: HTMLButtonElement;
      poseSrc: string;
    }
  >();
  private frogRaf: number | null = null;
  private frogLastTs: number | null = null;

  /** Night veil, moon, and fireflies — per backyard session. */
  private readonly ambienceEl: HTMLElement;
  private readonly nightVeilEl: HTMLElement;
  private readonly moonEl: HTMLElement;
  private readonly firefliesEl: HTMLElement;
  private fireflyGroupState: FireflyGroupState | null = null;
  private readonly fireflyVisuals = new Map<string, HTMLElement>();
  private fireflyRaf: number | null = null;
  private fireflyLastTs: number | null = null;
  private pineconeTreeHitEl: HTMLButtonElement | null = null;
  private readonly replenishedPineconeObjects: InteractiveObject[] = [];
  /** Tree-drop falls keyed by pinecone id. */
  private readonly pineconeFalls = new Map<
    string,
    {
      from: SpawnPoint;
      land: SpawnPoint;
      rotation0: number;
      startedAt: number;
      durationMs: number;
    }
  >();
  private pineconeFallRaf: number | null = null;

  private kickTimers: number[] = [];
  /** In-flight throw arcs keyed by pinecone id. */
  private readonly pineconeThrows = new Map<
    string,
    { plan: PineconeThrowPlan; releasedAt: number; bouncing: boolean }
  >();
  private pineconeThrowRaf: number | null = null;
  /** Long-press → pickup (tap still kicks). */
  private pineconeLongPressTimer: number | null = null;
  private pineconeLongPressStart: {
    x: number;
    y: number;
    objectId: string;
  } | null = null;
  private pineconeLongPressConsumed = false;
  /**
   * Command waiting until the current kick/pickup animation finishes
   * (mid-perform: finish, then honor next command).
   */
  private postKickCommand:
    | null
    | { kind: 'ground'; x: number; y: number }
    | { kind: 'object'; obj: InteractiveObject }
    | { kind: 'pickup'; obj: InteractiveObject }
    | { kind: 'throw'; x: number; y: number }
    | { kind: 'placeBucket'; x: number; y: number } = null;
  /**
   * World Editor camera (EDITOR_OVERVIEW). Independent of runtime framing.
   * Does not affect saved prop coordinates.
   */
  private editorOverviewBaseZoom = 1;
  private editorCamZoomFactor = 1;
  private editorCamCenter = { x: 0.5, y: 0.5 };

  constructor(mount: HTMLElement, navigator: WorldNavigator) {
    this.navigator = navigator;
    this.root = document.createElement('div');
    this.root.className = 'wp-app';
    this.root.innerHTML = `
      <section
        class="wp-scene"
        tabindex="0"
        role="application"
        aria-roledescription="place you are standing"
      >
        <div class="wp-hud">
          <div class="wp-hud-left">
            <div class="wp-camera-label">
              <img
                class="wp-location-badge"
                alt=""
                decoding="async"
                draggable="false"
                hidden
              />
              <p class="wp-camera-place"></p>
            </div>
          </div>
          <div class="wp-nav-cluster" aria-label="Navigation">
            <div class="wp-utility" aria-label="Travel"></div>
          </div>
        </div>

        <div class="wp-playfield">
          <div class="wp-artwork-bleed" aria-hidden="true" hidden>
            <div class="wp-artwork-bleed-img"></div>
          </div>
          <div class="wp-landscape-bleed" aria-hidden="true" hidden>
            <img class="wp-landscape-bleed-img" alt="" decoding="async" />
          </div>
          <div class="wp-bg-cover-debug" hidden aria-hidden="true">
            <div class="wp-bg-cover-debug-frame"></div>
            <pre class="wp-bg-cover-debug-readout"></pre>
          </div>
          <div class="wp-world-layer">
            <div class="wp-scenery" aria-hidden="true">
              <div class="wp-world-artwork" hidden>
                <img
                  class="wp-world-artwork-img"
                  alt=""
                  decoding="async"
                  draggable="false"
                />
              </div>
              <div class="wp-scenery-layers"></div>
            </div>
            <div class="wp-world-visual" aria-hidden="true">
              <ul class="wp-world-hints"></ul>
            </div>
            <div class="wp-props"></div>
            <div class="wp-ambience" aria-hidden="true">
              <div class="wp-night-veil"></div>
              <div class="wp-fireflies"></div>
              <div class="wp-moon" hidden aria-hidden="true"></div>
            </div>
            <div class="wp-creatures"></div>
            <div class="wp-map-title" hidden aria-hidden="true"></div>
            <div class="wp-map-pins" hidden aria-label="Places on the map"></div>
            <div class="wp-character" hidden aria-hidden="true">
              <span class="wp-character-shadow"></span>
              <span class="wp-character-facing">
                <img
                  class="wp-character-body"
                  alt=""
                  draggable="false"
                  decoding="async"
                />
              </span>
            </div>
            <div class="wp-world-debug" aria-hidden="true">
              <div class="wp-debug-world-bounds"></div>
              <div class="wp-debug-walk-bounds"></div>
              <svg class="wp-debug-walk-polygon" viewBox="0 0 1 1" preserveAspectRatio="none"></svg>
              <svg class="wp-debug-obstacles" viewBox="0 0 1 1" preserveAspectRatio="none"></svg>
              <div class="wp-debug-vertices"></div>
              <div class="wp-debug-reach-markers"></div>
              <div class="wp-debug-origins"></div>
              <div class="wp-debug-click" hidden></div>
              <div class="wp-debug-clamp" hidden></div>
              <div class="wp-debug-objects"></div>
              <div class="wp-debug-firefly-home"></div>
              <div class="wp-debug-firefly-points"></div>
              <div class="wp-debug-character-ground"></div>
              <div class="wp-debug-camera-viewport"></div>
            </div>
          </div>
          <div class="wp-porch" hidden>
            <span class="wp-porch-label"></span>
          </div>
          <div class="wp-interact-card" hidden></div>
          <div class="wp-hub-panel" hidden></div>
          <div class="wp-friends-picker" hidden>
            <div class="wp-friends-picker-inner">
              <p class="wp-friends-prompt">Who do you want to be?</p>
              <div class="wp-friends-track" role="list"></div>
            </div>
          </div>
        </div>

        <p class="wp-status sr-only" aria-live="polite"></p>
      </section>
      <aside class="wp-mode-badge" hidden aria-hidden="true"></aside>
      <div class="wp-dev-tools" hidden>
        <label class="wp-dev-tools-debug">
          <input type="checkbox" />
          <span>Show world debug</span>
        </label>
        <div class="wp-dev-prop-toggles" aria-label="Toggle props"></div>
      </div>
    `;
    mount.append(this.root);

    this.sceneEl = this.root.querySelector('.wp-scene')!;
    this.hudLeftEl = this.root.querySelector('.wp-hud-left')!;
    this.cameraLabelEl = this.root.querySelector('.wp-camera-label')!;
    this.locationBadgeEl = this.root.querySelector('.wp-location-badge')!;
    this.cameraPlaceEl = this.root.querySelector('.wp-camera-place')!;
    this.utilityEl = this.root.querySelector('.wp-utility')!;
    this.navClusterEl = this.root.querySelector('.wp-nav-cluster')!;
    this.friendsBtnEl = createFriendsButton({
      onActivate: () => this.openFriendsPicker(),
    });
    this.friendsBtnEl.hidden = true;
    this.navClusterEl.append(this.friendsBtnEl);
    this.dayNightBtnEl = createDayNightButton({
      onActivate: () => this.onDayNightTapped(),
    });
    this.dayNightBtnEl.hidden = true;
    this.hudLeftEl.append(this.dayNightBtnEl);
    this.playfieldEl = this.root.querySelector('.wp-playfield')!;
    this.worldLayerEl = this.root.querySelector('.wp-world-layer')!;
    this.sceneryEl = this.root.querySelector('.wp-scenery')!;
    this.worldArtworkEl = this.root.querySelector('.wp-world-artwork')!;
    this.worldArtworkImgEl = this.root.querySelector('.wp-world-artwork-img')!;
    this.artworkBleedEl = this.root.querySelector('.wp-artwork-bleed')!;
    this.artworkBleedImgEl = this.root.querySelector('.wp-artwork-bleed-img')!;
    this.landscapeBleedEl = this.root.querySelector('.wp-landscape-bleed')!;
    this.landscapeBleedImgEl = this.root.querySelector(
      '.wp-landscape-bleed-img',
    )!;
    this.bgCoverDebugEl = this.root.querySelector('.wp-bg-cover-debug')!;
    this.bgCoverDebugReadoutEl = this.root.querySelector(
      '.wp-bg-cover-debug-readout',
    )!;
    this.sceneryLayersEl = this.root.querySelector('.wp-scenery-layers')!;
    this.bgCoverDebugEnabled =
      import.meta.env.DEV &&
      new URLSearchParams(window.location.search).has('bgdebug');
    this.gardenDebugEnabled =
      import.meta.env.DEV &&
      new URLSearchParams(window.location.search).has('gardenDebug');
    this.landscapeBleedImgEl.addEventListener('load', () => {
      const play = this.playfieldEl.getBoundingClientRect();
      if (play.width > 0 && play.height > 0) {
        this.syncBgCoverDebug(play.width, play.height);
      }
    });
    this.worldHintsEl = this.root.querySelector('.wp-world-hints')!;
    this.propsEl = this.root.querySelector('.wp-props')!;
    this.ambienceEl = this.root.querySelector('.wp-ambience')!;
    this.nightVeilEl = this.root.querySelector('.wp-night-veil')!;
    this.firefliesEl = this.root.querySelector('.wp-fireflies')!;
    this.moonEl = this.root.querySelector('.wp-moon')!;
    this.creaturesEl = this.root.querySelector('.wp-creatures')!;
    this.porchEl = this.root.querySelector('.wp-porch')!;
    this.porchLabelEl = this.root.querySelector('.wp-porch-label')!;
    this.characterEl = this.root.querySelector('.wp-character')!;
    this.characterBodyEl = this.root.querySelector('.wp-character-body')!;
    this.syncPresencePlayerVisual();
    this.characterBodyEl.addEventListener('error', () => {
      const active = getActivePlayableCharacter();
      const current = this.characterBodyEl.getAttribute('src') ?? '';
      // Prefer PNG if the WebP derivative failed.
      if (current !== active.playerSrc) {
        this.characterBodyEl.src = active.playerSrc;
      }
    });
    this.interactCardEl = this.root.querySelector('.wp-interact-card')!;
    this.statusEl = this.root.querySelector('.wp-status')!;
    this.hubPanelEl = this.root.querySelector('.wp-hub-panel')!;
    this.mapTitleEl = this.root.querySelector('.wp-map-title')!;
    this.mapPinsEl = this.root.querySelector('.wp-map-pins')!;
    this.friendsPickerEl = this.root.querySelector('.wp-friends-picker')!;
    this.friendsTrackEl = this.root.querySelector('.wp-friends-track')!;
    this.unsubActivePlayer = subscribeActivePlayer(() => {
      this.cancelFrogHeadJumpSequences();
      this.syncPresencePlayerVisual();
      if (this.friendsOpen) this.renderFriendsPickerCards();
      if (this.presence) {
        this.syncCharacterBaseWidth(
          this.presence.getSpace(),
          this.presence.getState().characterScale,
        );
        this.syncCarriedObjectVisual();
      }
    });
    this.debugEl = this.root.querySelector('.wp-world-debug')!;
    this.debugWorldBoundsEl = this.root.querySelector('.wp-debug-world-bounds')!;
    this.debugWalkBoundsEl = this.root.querySelector('.wp-debug-walk-bounds')!;
    this.debugWalkPolygonEl = this.root.querySelector('.wp-debug-walk-polygon')!;
    this.debugObstaclesEl = this.root.querySelector('.wp-debug-obstacles')!;
    this.debugVerticesEl = this.root.querySelector('.wp-debug-vertices')!;
    this.debugReachMarkersEl = this.root.querySelector(
      '.wp-debug-reach-markers',
    )!;
    this.debugOriginsEl = this.root.querySelector('.wp-debug-origins')!;
    this.debugClickEl = this.root.querySelector('.wp-debug-click')!;
    this.debugClampEl = this.root.querySelector('.wp-debug-clamp')!;
    this.debugObjectsEl = this.root.querySelector('.wp-debug-objects')!;
    this.debugFireflyHomeEl = this.root.querySelector('.wp-debug-firefly-home')!;
    this.debugFireflyPointsEl = this.root.querySelector('.wp-debug-firefly-points')!;
    this.debugCharacterEl = this.root.querySelector(
      '.wp-debug-character-ground',
    )!;
    this.debugCameraViewportEl = this.root.querySelector(
      '.wp-debug-camera-viewport',
    )!;
    this.debugToggleEl = this.root.querySelector('.wp-dev-tools-debug input')!;
    this.modeBadgeEl = this.root.querySelector('.wp-mode-badge')!;
    this.propTogglesEl = this.root.querySelector('.wp-dev-prop-toggles')!;

    const debugTools = this.root.querySelector<HTMLElement>('.wp-dev-tools')!;
    // Never expose world-debug chrome in production builds.
    const debugRequested = new URLSearchParams(window.location.search).has(
      'debug',
    );
    debugTools.hidden = !(import.meta.env.DEV && debugRequested);
    // Mode badge: DEV builds only (always on in local vite; stripped in prod).
    this.modeBadgeEl.hidden = !import.meta.env.DEV;
    this.modeBadgeEl.setAttribute(
      'aria-hidden',
      import.meta.env.DEV ? 'false' : 'true',
    );
    this.debugToggleEl.addEventListener('change', () => {
      this.debugEnabled = this.debugToggleEl.checked;
      this.root.classList.toggle('wp-debug-visible', this.debugEnabled);
      this.updateDebugCameraViewport();
      this.syncFireflyDebugOverlay();
    });

    this.bindPlayfieldPointer();
    this.bindResize();

    this.unsubNav = this.navigator.subscribe((event) => {
      if (event.reason === 'travel') {
        void this.renderWithTravelFade();
      } else {
        this.render();
      }
    });

    this.render();
  }

  destroy(): void {
    this.unbindResize?.();
    this.unbindResize = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.clearFocusAnim();
    this.teardownPresence();
    this.unsubActivePlayer?.();
    this.unsubActivePlayer = null;
    this.unsubNav?.();
    this.spaceChangeListeners.clear();
    this.root.remove();
  }

  /** Root app element — used by the World Editor host bridge. */
  getRootEl(): HTMLElement {
    return this.root;
  }

  getWorldLayerEl(): HTMLElement {
    return this.worldLayerEl;
  }

  getPlayfieldEl(): HTMLElement {
    return this.playfieldEl;
  }

  getCurrentSpace(): PresenceSpace | null {
    return this.currentSpace;
  }

  clientToWorldPublic(
    clientX: number,
    clientY: number,
  ): { x: number; y: number } | null {
    return this.clientToWorld(clientX, clientY);
  }

  /** Soft-lock walk / object focus while the World Editor is active. */
  setInteractionLocked(locked: boolean): void {
    this.interactionLocked = locked;
    if (locked) {
      this.dismissFocus(true);
      this.pendingFocusId = null;
      this.resetEditorCameraState();
    }
    // Editor on: lock composition framing. Editor off: restore follow.
    if (this.currentSpace) {
      this.syncExploreZoom(this.currentSpace);
      this.focusZoom = this.exploreZoom;
      if (locked && this.lockedFrameCenter) {
        this.presence?.setCameraCenter(
          this.lockedFrameCenter.x,
          this.lockedFrameCenter.y,
        );
      }
      if (this.lastPose) {
        this.applyCamera(this.currentSpace, this.lastPose);
      }
    }
  }

  getBackyardPresentationMode(): BackyardPresentationMode | null {
    return this.backyardPresentationMode;
  }

  /**
   * Editor-selected layout profile (override), not the runtime resolver result.
   * Falls back to the mounted backyard mode when no override is set.
   */
  getEditorLayoutProfile(): BackyardPresentationMode | null {
    return this.editorPresentationOverride ?? this.backyardPresentationMode;
  }

  /**
   * World Editor: force desktop/tablet/mobile layout. Pass null to follow viewport.
   * Rebuilds backyard art + prop transforms without tearing down presence.
   * Does not consult the runtime device resolver while override is set.
   */
  setEditorPresentationOverride(mode: BackyardPresentationMode | null): void {
    this.editorPresentationOverride = mode;
    if (this.interactionLocked) {
      this.resetEditorCameraState();
    }
    if (this.currentSpace) {
      if (
        this.currentSpace.id !== 'hubert' &&
        this.currentSpace.id !== 'raleigh'
      ) {
        return;
      }
      this.syncBackyardPresentation(this.currentSpace, true);
      this.refreshExploreZoom();
      return;
    }
    const hub = this.navigator.getCurrentHub();
    if (hub?.id === 'places-map') {
      this.syncPlacesMapArtwork(hub, true);
      this.renderPlacesMapPins(hub);
    }
  }

  /**
   * After Save / Vite HMR: re-mount the editor-selected layout from module defs
   * so the live world matches disk without falling back to the runtime resolver.
   */
  reapplyEditorLayout(): void {
    if (!this.interactionLocked || !this.editorPresentationOverride) return;
    if (!this.currentSpace) return;
    if (
      this.currentSpace.id !== 'hubert' &&
      this.currentSpace.id !== 'raleigh'
    ) {
      return;
    }
    this.syncBackyardPresentation(this.currentSpace, true);
    this.refreshExploreZoom();
  }

  /**
   * World Editor: playfield stays full-bleed. Layout buttons only switch
   * artwork + prop transforms — never resize the canvas to a device frame.
   */
  setEditorViewportPreset(_mode: BackyardPresentationMode | null): void {
    this.playfieldEl.classList.remove(
      'wp-editor-viewport-framed',
      'wp-editor-viewport-desktop',
      'wp-editor-viewport-tablet',
      'wp-editor-viewport-mobile',
    );
    this.playfieldEl.style.removeProperty('--wp-editor-vp-w');
    this.playfieldEl.style.removeProperty('--wp-editor-vp-h');
    this.playfieldEl.style.width = '';
    this.playfieldEl.style.height = '';
    this.refreshExploreZoom();
  }

  /** Reset EDITOR_OVERVIEW to default fit (contain on desktop, cover on mobile). */
  resetEditorCamera(): void {
    this.resetEditorCameraState();
    if (this.currentSpace && this.interactionLocked) {
      this.syncExploreZoom(this.currentSpace);
      this.focusZoom = this.exploreZoom;
      if (this.lockedFrameCenter) {
        this.presence?.setCameraCenter(
          this.lockedFrameCenter.x,
          this.lockedFrameCenter.y,
        );
      }
      if (this.lastPose) {
        this.applyCamera(this.currentSpace, this.lastPose);
      }
    }
  }

  /** Cmd/Ctrl+wheel editor zoom. Does not touch prop transforms. */
  nudgeEditorZoom(direction: 1 | -1): void {
    if (!this.interactionLocked) return;
    const step = 1.12;
    this.editorCamZoomFactor = Math.min(
      4,
      Math.max(0.5, this.editorCamZoomFactor * (direction > 0 ? step : 1 / step)),
    );
    this.applyEditorCameraFromState();
  }

  /** Space/middle-mouse pan in CSS pixels. Does not touch prop transforms. */
  panEditorByPixels(dxPx: number, dyPx: number): void {
    if (!this.interactionLocked || !this.activeSceneWorld) return;
    const world = this.activeSceneWorld;
    const z = Math.max(0.01, this.exploreZoom);
    const spanX = world.widthPx * z;
    const spanY = world.heightPx * z;
    if (spanX <= 0 || spanY <= 0) return;
    this.editorCamCenter = {
      x: this.editorCamCenter.x - dxPx / spanX,
      y: this.editorCamCenter.y - dyPx / spanY,
    };
    this.applyEditorCameraFromState();
  }

  private resetEditorCameraState(): void {
    this.editorCamZoomFactor = 1;
    this.editorCamCenter = { x: 0.5, y: 0.5 };
  }

  private applyEditorCameraFromState(): void {
    if (!this.currentSpace || !this.interactionLocked) return;
    const base = Math.max(0.01, this.editorOverviewBaseZoom);
    this.exploreZoom = base * this.editorCamZoomFactor;
    this.focusZoom = this.exploreZoom;
    this.focusZoomTarget = this.exploreZoom;
    this.lockedFrameCenter = { ...this.editorCamCenter };
    this.presence?.setCameraCenter(
      this.editorCamCenter.x,
      this.editorCamCenter.y,
    );
    this.presence?.setExploreFraming({
      zoom: this.exploreZoom,
      worldWidth: this.worldPanWidth,
      worldHeight: this.worldPanHeight,
      followDeadZone: false,
    });
    if (this.lastPose) {
      this.applyCamera(this.currentSpace, this.lastPose);
    }
    if (import.meta.env.DEV) {
      const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
      this.updateModeBadge(playW, playH);
    }
  }

  subscribeSpaceChange(cb: (spaceId: string | null) => void): () => void {
    this.spaceChangeListeners.add(cb);
    cb(this.currentSpace?.id ?? null);
    return () => this.spaceChangeListeners.delete(cb);
  }

  private notifySpaceChange(spaceId: string | null): void {
    if (this.lastNotifiedSpaceId === spaceId) return;
    this.lastNotifiedSpaceId = spaceId;
    for (const listener of this.spaceChangeListeners) listener(spaceId);
  }

  private bindResize(): void {
    const onResize = (): void => {
      this.syncMobileViewportShell();
      this.refreshExploreZoom();
    };
    // Always listen to window resize — ResizeObserver alone can miss some
    // desktop window chrome changes; both paths must recompute framing.
    window.addEventListener('resize', onResize);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(onResize);
      this.resizeObserver.observe(this.playfieldEl);
      // Also observe the app shell in case the playfield's box lags a frame.
      this.resizeObserver.observe(this.root);
    }
    // Safari toolbar show/hide changes visualViewport without always
    // resizing the layout playfield — reframe from the visible area.
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }
    // Initial mobile shell bind (Safari dvh vs visualViewport).
    this.syncMobileViewportShell();
    this.unbindResize = () => {
      window.removeEventListener('resize', onResize);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }

  /**
   * Mobile-only: size the app shell to the *visible* viewport.
   * Safari's 100dvh can outgrow visualViewport when browser chrome is showing,
   * which left cream below a cover-framed world. Desktop styles stay untouched.
   */
  private syncMobileViewportShell(): void {
    const vv = window.visualViewport;
    const layoutW = Math.max(1, this.root.getBoundingClientRect().width || window.innerWidth);
    const layoutH = Math.max(1, this.root.getBoundingClientRect().height || window.innerHeight);
    const vvW = vv?.width ?? window.innerWidth;
    const vvH = vv?.height ?? window.innerHeight;
    // Classify from the visible area so short desktop windows stay desktop.
    const probeW = Math.min(layoutW, vvW);
    const probeH = Math.min(layoutH, vvH);
    const mobile = isMobilePresentationPlayfield(probeW, probeH);

    if (!mobile) {
      this.root.classList.remove('wp-mobile-vv-shell');
      this.root.style.removeProperty('width');
      this.root.style.removeProperty('height');
      document.documentElement.classList.remove('wp-has-mobile-shell');
      document.documentElement.style.removeProperty('--wp-shell-width');
      document.documentElement.style.removeProperty('--wp-shell-height');
      return;
    }

    const w = Math.max(1, Math.round(vvW));
    const h = Math.max(1, Math.round(vvH));
    document.documentElement.classList.add('wp-has-mobile-shell');
    document.documentElement.style.setProperty('--wp-shell-width', `${w}px`);
    document.documentElement.style.setProperty('--wp-shell-height', `${h}px`);
    this.root.classList.add('wp-mobile-vv-shell');
    this.root.style.width = `${w}px`;
    this.root.style.height = `${h}px`;
  }

  private refreshExploreZoom(): void {
    // Places map hub swaps landscape/portrait plates on resize + orientation.
    if (!this.currentSpace) {
      const hub = this.navigator.getCurrentHub();
      if (hub?.id === 'places-map') {
        const prevMode = this.backyardPresentationMode;
        const prevOrientation = this.placesMapOrientation;
        this.syncPlacesMapArtwork(hub, false);
        if (
          this.backyardPresentationMode !== prevMode ||
          this.placesMapOrientation !== prevOrientation
        ) {
          this.renderPlacesMapPins(hub);
        } else {
          this.layoutPlacesMapPins();
        }
      }
      return;
    }
    const wasFocused = Boolean(this.focusObject);
    this.syncExploreZoom(this.currentSpace);
    if (wasFocused) {
      // Preserve relative lean-in against the new responsive explore zoom.
      if (Math.abs(this.focusZoom - this.focusZoomTarget) > 0.01) {
        this.animateFocusZoom(this.focusZoomTarget);
      } else {
        this.focusZoom = this.focusZoomTarget;
        this.applyCamera(
          this.currentSpace,
          this.lastPose ?? { centerX: 0.5, centerY: 0.5, characterScale: 1 },
        );
      }
      return;
    }
    this.focusZoom = this.exploreZoom;
    // Always re-apply camera after resize — even before presence has posed —
    // so scale/offset never stay stuck on the previous viewport.
    this.applyCamera(
      this.currentSpace,
      this.lastPose ?? { centerX: 0.5, centerY: 0.5, characterScale: 1 },
    );
    this.ensurePlayfieldCovered();
  }

  /**
   * Safety net: if the scaled world does not fully cover the playfield
   * (cream gutters), recenter and/or bump zoom slightly.
   * Does not change world pixel size or prop data. Does not permanently
   * re-lock gameplay follow framing.
   */
  private ensurePlayfieldCovered(): void {
    // Desktop EDITOR_OVERVIEW letterboxes on purpose; mobile uses cover fill.
    if (this.interactionLocked) return;
    if (!this.activeSceneWorld || !this.currentSpace) return;
    const play = this.playfieldEl.getBoundingClientRect();
    const world = this.worldLayerEl.getBoundingClientRect();
    if (play.width < 2 || play.height < 2 || world.width < 2 || world.height < 2) {
      return;
    }
    const uncovered =
      world.top > play.top + 1.25 ||
      world.bottom < play.bottom - 1.25 ||
      world.left > play.left + 1.25 ||
      world.right < play.right - 1.25;
    if (!uncovered) return;

    const followGameplay =
      this.exploreMode === 'follow' && !this.interactionLocked;
    if (followGameplay) {
      this.presence?.setCameraCenter(0.5, 0.5);
    } else {
      this.lockedFrameCenter = { x: 0.5, y: 0.5 };
    }
    this.applyCamera(
      this.currentSpace,
      this.lastPose ?? { centerX: 0.5, centerY: 0.5, characterScale: 1 },
    );

    const world2 = this.worldLayerEl.getBoundingClientRect();
    const stillUncovered =
      world2.top > play.top + 1.25 ||
      world2.bottom < play.bottom - 1.25 ||
      world2.left > play.left + 1.25 ||
      world2.right < play.right - 1.25;
    if (!stillUncovered) return;

    const bump = Math.max(
      play.width / Math.max(1, world2.width),
      play.height / Math.max(1, world2.height),
      1.01,
    );
    this.exploreZoom *= bump;
    this.focusZoom = this.exploreZoom;
    this.focusZoomTarget = this.exploreZoom;
    this.applyCamera(
      this.currentSpace,
      this.lastPose ?? { centerX: 0.5, centerY: 0.5, characterScale: 1 },
    );
  }

  private syncExploreZoom(space: PresenceSpace): void {
    const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
    this.syncBackyardPresentation(space, false);
    const mode = this.backyardPresentationMode ?? 'desktop';

    // Backyard: fixed SceneWorld + WorldCamera. Resize updates the camera only.
    if (isBackyardLocationId(space.id)) {
      const sceneWorld =
        this.activeSceneWorld ??
        resolveBackyardSceneWorld(space.id, mode);
      this.activeSceneWorld = sceneWorld;
      // Always re-assert fixed pixel box — hub→yard and vv shell changes can
      // leave stale fluid/% sizing that under-covers the playfield.
      this.applyWorldLayerSize(space);

      const frame = this.interactionLocked
        ? frameEditorOverview(sceneWorld, { width: playW, height: playH })
        : frameSceneWorld(sceneWorld, { width: playW, height: playH });

      if (this.interactionLocked) {
        // EDITOR_OVERVIEW — full world visible in the full browser workspace.
        this.editorOverviewBaseZoom = frame.zoom;
        this.exploreZoom = frame.zoom * this.editorCamZoomFactor;
        this.focusZoomTarget = this.exploreZoom;
        this.exploreMode = 'fit';
        this.lockedFrameCenter = { ...this.editorCamCenter };
        this.root.dataset.exploreMode = 'fit';
        this.root.dataset.deviceClass = this.deviceClass ?? 'desktop';
        this.root.dataset.viewportMode = 'editor';
        this.root.dataset.backyardLayout = mode;
        this.root.dataset.worldMode = 'fixed';
        this.root.dataset.cameraPolicy = 'editor-overview';
        this.root.dataset.cameraFollow = 'off';
        this.root.dataset.editorCamera = 'overview';
        this.root.dataset.orientation =
          playH >= playW ? 'portrait' : 'landscape';
        this.root.removeAttribute('data-mobile-camera');
        this.root.removeAttribute('data-mobile-orientation');

        this.presence?.setExploreFraming({
          zoom: this.exploreZoom,
          worldWidth: frame.framingWidth,
          worldHeight: frame.framingHeight,
          followDeadZone: false,
        });
        this.worldPanWidth = frame.framingWidth;
        this.worldPanHeight = frame.framingHeight;
        this.presence?.setCameraCenter(
          this.editorCamCenter.x,
          this.editorCamCenter.y,
        );
        this.lastMobileCameraMode = null;
        // Desktop contain overview: blur-bleed fills letterbox gutters.
        // Mobile/tablet cover overview: world already fills — no side bars.
        if (
          sceneWorld.cameraPolicy === 'mobile-cover' ||
          !sceneWorld.artworkSrc
        ) {
          this.clearArtworkBleed();
        } else {
          this.syncArtworkBleed(sceneWorld.artworkSrc);
        }
        // No runtime landscape bleed / phone crop while editing.
        this.landscapeBleedEl.hidden = true;
        this.landscapeBleedEl.classList.remove('is-active');
        this.playfieldEl.classList.remove('wp-landscape-cover-active');
        this.logViewportWithoutModeChange(playW, playH);
        this.updateModeBadge(playW, playH);
        return;
      }

      this.root.removeAttribute('data-editor-camera');
      this.exploreZoom = frame.zoom;
      this.focusZoomTarget = frame.focusZoom;

      // Desktop: keep existing follow-when-not-editing behavior (LOCKED).
      // Mobile + tablet (Mobile art): portrait FOLLOW, landscape FIT.
      const coverLayout = usesMobileCoverCamera(mode);
      const mobileCameraMode: MobileCameraMode | null = coverLayout
        ? resolveMobileCameraMode(playW, playH)
        : null;
      const followGameplay =
        !this.interactionLocked &&
        (!coverLayout || mobileCameraMode === 'follow');

      this.exploreMode = followGameplay ? 'follow' : frame.mode;
      this.lockedFrameCenter = followGameplay ? null : frame.frameCenter;
      this.root.dataset.exploreMode = this.exploreMode;
      this.root.dataset.deviceClass = this.deviceClass ?? 'desktop';
      this.root.dataset.viewportMode =
        this.deviceClass === 'mobile'
          ? 'mobile'
          : this.deviceClass === 'tablet'
            ? 'tablet'
            : 'desktop';
      this.root.dataset.backyardLayout = mode;
      this.root.dataset.worldMode = 'fixed';
      this.root.dataset.cameraPolicy = sceneWorld.cameraPolicy;
      this.root.dataset.cameraFollow = followGameplay ? 'on' : 'off';
      const orientation = playH >= playW ? 'portrait' : 'landscape';
      this.root.dataset.orientation = orientation;
      if (mobileCameraMode) {
        this.root.dataset.mobileCamera = mobileCameraMode;
        this.root.dataset.mobileOrientation =
          mobileCameraMode === 'follow' ? 'portrait' : 'landscape';
      } else {
        this.root.removeAttribute('data-mobile-camera');
        this.root.removeAttribute('data-mobile-orientation');
      }

      const dead = coverLayout
        ? MOBILE_CAMERA.portraitFollow
        : BACKYARD_CAMERA_FOLLOW.desktop;
      this.presence?.setExploreFraming({
        zoom: frame.zoom,
        worldWidth: frame.framingWidth,
        worldHeight: frame.framingHeight,
        followDeadZone: followGameplay,
        deadZoneViewportFracX: dead.deadZoneViewportFracX,
        deadZoneViewportFracY: dead.deadZoneViewportFracY,
      });
      this.worldPanWidth = frame.framingWidth;
      this.worldPanHeight = frame.framingHeight;

      // Seed camera: enter / world swap / mobile orientation change.
      // Desktop seed behavior unchanged (composition once per enter).
      if (this.presence) {
        const orientationChanged =
          coverLayout &&
          mobileCameraMode != null &&
          this.lastMobileCameraMode != null &&
          this.lastMobileCameraMode !== mobileCameraMode;

        if (followGameplay) {
          if (!this.backyardCameraSeeded) {
            if (this.plateSwapPreserveCharacter) {
              // Plate swap (e.g. tablet rotate desktop↔tablet): keep framing
              // on the character's normalized position.
              const pos = this.presence.getCharacterPosition();
              this.presence.setCameraCenter(pos.x, pos.y);
            } else if (frame.frameCenter) {
              // First enter: default composition.
              this.presence.setCameraCenter(
                frame.frameCenter.x,
                frame.frameCenter.y,
              );
            }
            this.backyardCameraSeeded = true;
            this.plateSwapPreserveCharacter = false;
          } else if (orientationChanged && mobileCameraMode === 'follow') {
            // Landscape → portrait: frame around the character.
            const pos = this.presence.getCharacterPosition();
            this.presence.setCameraCenter(pos.x, pos.y);
          }
        } else if (
          coverLayout &&
          mobileCameraMode === 'fit' &&
          frame.frameCenter &&
          (orientationChanged ||
            !this.backyardCameraSeeded ||
            this.plateSwapPreserveCharacter)
        ) {
          // Portrait → landscape (or first landscape enter / plate swap):
          // lock to fit center. Character world position is preserved.
          this.presence.setCameraCenter(
            frame.frameCenter.x,
            frame.frameCenter.y,
          );
          this.backyardCameraSeeded = true;
          this.plateSwapPreserveCharacter = false;
        }

        if (coverLayout) {
          this.lastMobileCameraMode = mobileCameraMode;
        } else {
          this.lastMobileCameraMode = null;
        }
      }

      this.syncLandscapeBleed(space, playW, playH);
      this.clearArtworkBleed();
      this.logViewportWithoutModeChange(playW, playH);
      this.updateModeBadge(playW, playH);
      this.logMobileBackyardDebug(
        space,
        playW,
        playH,
        frame.framingWidth,
        frame.framingHeight,
        frame,
      );
      return;
    }

    // Non-backyard / legacy fluid worlds (map hubs excluded — no presence space).
    this.activeSceneWorld = null;
    this.root.dataset.worldMode = 'fluid';
    this.root.removeAttribute('data-camera-policy');
    this.root.removeAttribute('data-camera-follow');
    if (space.worldAspectRatio) {
      this.applyWorldLayerSize(space);
    }
    const { worldWidth, worldHeight } = resolveWorldSizeFactors(
      space,
      playW,
      playH,
    );
    const resolved = resolveExploreZoom({
      playfieldWidth: playW,
      playfieldHeight: playH,
      worldWidth,
      worldHeight,
    });
    this.exploreZoom = resolved.zoom;
    this.focusZoomTarget = resolved.focusZoom;
    this.exploreMode = resolved.mode;
    this.lockedFrameCenter = resolved.frameCenter;
    this.root.dataset.exploreMode = resolved.mode;
    this.root.dataset.viewportMode =
      resolved.mode === 'balanced'
        ? 'tablet'
        : resolved.mode === 'fit'
          ? 'desktop'
          : 'mobile';
    this.root.dataset.backyardLayout = '';

    this.presence?.setExploreFraming({
      zoom: resolved.zoom,
      worldWidth,
      worldHeight,
      followDeadZone: resolved.mode === 'follow',
    });
    this.worldPanWidth = worldWidth;
    this.worldPanHeight = worldHeight;
    this.syncLandscapeBleed(space, playW, playH);
    this.syncArtworkBleed(space.scenery.masterArtwork?.src ?? null);
  }

  /** Temporary mobile backyard projection diagnostics (`?debug`). */
  private logMobileBackyardDebug(
    space: PresenceSpace,
    playW: number,
    playH: number,
    worldWidth: number,
    worldHeight: number,
    resolved: { zoom: number; mode: ExplorationCameraMode },
  ): void {
    if (
      this.backyardPresentationMode !== 'mobile' ||
      (space.id !== 'hubert' && space.id !== 'raleigh')
    ) {
      return;
    }
    if (!this.bgCoverDebugEnabled && !this.debugEnabled) {
      const debugRequested =
        typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).has('debug');
      if (!debugRequested) return;
    }
    const master = space.scenery.masterArtwork;
    const zoom = resolved.zoom;
    const visW = 1 / (worldWidth * zoom);
    const visH = 1 / (worldHeight * zoom);
    // eslint-disable-next-line no-console
    console.info('[porchlight mobile backyard]', {
      viewportCss: { w: playW, h: playH },
      devicePixelRatio:
        typeof window !== 'undefined' ? window.devicePixelRatio : 1,
      layout: 'mobile',
      framing: resolved.mode,
      masterSrc: master?.src ?? null,
      scale: zoom,
      renderedWorld: {
        w: worldWidth * zoom,
        h: worldHeight * zoom,
      },
      visibleImageFrac: { w: visW, h: visH },
    });
  }

  /**
   * Swap desktop/mobile SceneWorld (artwork + props) when presentation changes.
   * Does not run on ordinary viewport resize — only on device-class changes
   * or World Editor force.
   */
  private syncBackyardPresentation(
    space: PresenceSpace,
    force: boolean,
  ): void {
    if (
      space.id !== 'hubert' &&
      space.id !== 'raleigh' &&
      space.id !== 'spanish-fork'
    ) {
      this.backyardPresentationMode = null;
      this.deviceClass = null;
      this.activeSceneWorld = null;
      return;
    }
    const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
    this.deviceClass = resolveDeviceClass(playW, playH);
    const mode =
      this.editorPresentationOverride ??
      resolveBackyardPresentationMode(playW, playH);
    const expectedWorldId = `${space.id}-${mode}` as
      | 'hubert-desktop'
      | 'hubert-tablet'
      | 'hubert-mobile'
      | 'raleigh-desktop'
      | 'raleigh-tablet'
      | 'raleigh-mobile'
      | 'spanish-fork-desktop'
      | 'spanish-fork-tablet'
      | 'spanish-fork-mobile';
    // Places-map also stores desktop/mobile in backyardPresentationMode.
    // Require the matching SceneWorld before skipping — otherwise map→yard
    // with the same mode never swaps artwork / props.
    if (
      !force &&
      mode === this.backyardPresentationMode &&
      this.activeSceneWorld?.id === expectedWorldId
    ) {
      return;
    }

    const previousMode = this.backyardPresentationMode;
    const previousWorldId = this.activeSceneWorld?.id ?? null;
    this.backyardPresentationMode = mode;
    this.activeSceneWorld = resolveBackyardSceneWorld(space.id, mode);
    // New plate → reseed; keep framing on the character across tablet rotates.
    if (previousWorldId !== this.activeSceneWorld.id) {
      this.backyardCameraSeeded = false;
      this.plateSwapPreserveCharacter = previousWorldId != null;
    }
    if (space.id === 'hubert') {
      applyBackyardPresentation(space, mode);
    } else if (space.id === 'raleigh') {
      applyRaleighPresentation(space, mode);
    } else if (space.id === 'spanish-fork') {
      applySpanishForkPresentation(space, mode);
    }
    // Plate swap → new fixed world pixel box (not a resize reflow).
    this.applyWorldLayerSize(space);
    // Re-capture pinecone centers for the new layout profile (runtime toy state resets).
    this.seedPineconeRuntime(space);
    this.renderScenery(space);
    this.restoreReplenishedPinecones(space);
    this.syncFirepitVisual(space);
    this.syncBurnedPineconeLayers();
    this.renderWorldDebug(space);
    this.renderProps(space);

    if (import.meta.env.DEV) {
      const background = this.resolveActiveBackgroundFilename();
      const switchedMode = previousMode && previousMode !== mode;
      const switchedWorld =
        previousWorldId != null && previousWorldId !== expectedWorldId;
      if (switchedMode) {
        // eslint-disable-next-line no-console
        console.info(
          `[Backyard] layout ${previousMode.toUpperCase()} → ${mode.toUpperCase()} (${(this.deviceClass ?? 'desktop').toUpperCase()})`,
        );
        // eslint-disable-next-line no-console
        console.info(`[Backyard] background = ${background}`);
      } else if (!previousMode || switchedWorld) {
        // eslint-disable-next-line no-console
        console.info(
          `[Backyard] device=${(this.deviceClass ?? 'desktop').toUpperCase()} layout=${mode.toUpperCase()}`,
        );
        // eslint-disable-next-line no-console
        console.info(`[Backyard] background = ${background}`);
      }
      this.lastLoggedPresentationMode = mode;
      this.updateModeBadge(playW, playH);
    }
  }

  /** Filename of the img currently targeted for the backyard plate. */
  private resolveActiveBackgroundFilename(): string {
    // Prefer SceneWorld / attribute src — currentSrc can lag until load,
    // leaving a stale map filename on the badge after travel.
    const src =
      this.activeSceneWorld?.artworkSrc ||
      this.worldArtworkImgEl?.getAttribute('src') ||
      this.worldArtworkImgEl?.src ||
      this.currentSpace?.scenery.masterArtwork?.src ||
      '';
    if (!src) return '';
    return decodeURIComponent((src.split('/').pop() ?? src).split('?')[0] ?? '');
  }

  /** DEV-only badge: device class, layout plate, orientation, camera. */
  private updateModeBadge(playW: number, playH: number): void {
    if (!import.meta.env.DEV || this.modeBadgeEl.hidden) return;
    const isBackyard = isBackyardLocationId(this.currentSpace?.id);
    const isMap = this.navigator.getCurrentHub()?.id === 'places-map';
    if ((!isBackyard && !isMap) || !this.backyardPresentationMode) {
      this.modeBadgeEl.textContent = '';
      return;
    }
    const layout = this.backyardPresentationMode;
    const background = this.resolveActiveBackgroundFilename() || '(loading)';
    const camW = Math.round(playW);
    const camH = Math.round(playH);

    if (this.interactionLocked && isBackyard) {
      const device = this.deviceClass ?? resolveDeviceClass(playW, playH);
      const editorLayout =
        this.editorPresentationOverride ?? this.backyardPresentationMode;
      this.modeBadgeEl.textContent = [
        `Presentation: ${device.toUpperCase()}`,
        `Editor Layout: ${(editorLayout ?? layout).toUpperCase()}`,
        `Background: ${background}`,
        `Editor Camera: OVERVIEW`,
        `Editor Viewport: ${camW}×${camH}`,
        `Editor Zoom: ${this.editorCamZoomFactor.toFixed(2)}×`,
        `Runtime Camera: not active`,
      ].join('\n');
      return;
    }

    const device = this.deviceClass ?? resolveDeviceClass(playW, playH);
    const orientation = playH >= playW ? 'PORTRAIT' : 'LANDSCAPE';
    const lines = [
      `Presentation: ${device.toUpperCase()}`,
      `Scene layout: ${layout}`,
      `Orientation: ${orientation}`,
      `Background: ${background}`,
      `${camW}×${camH}`,
    ];

    if (isMap) {
      const mapFit =
        this.root.dataset.mapFit?.toUpperCase() ??
        resolvePlacesMapFit(playW, playH, device).toUpperCase();
      lines.push(`Map fit: ${mapFit}`);
      this.modeBadgeEl.textContent = lines.join('\n');
      return;
    }

    if (usesMobileCoverCamera(layout)) {
      const mobileCam =
        this.root.dataset.mobileCamera?.toUpperCase() ??
        resolveMobileCameraMode(playW, playH).toUpperCase();
      lines.push(`Camera Mode: ${mobileCam}`);
    }

    lines.push(`camera ${camW}×${camH}`);

    const pose = this.lastPose ?? this.presence?.getState().camera;
    const char = this.presence?.getCharacterPosition();
    const world = this.activeSceneWorld;
    if (char) {
      lines.push(`player ${char.x.toFixed(3)}, ${char.y.toFixed(3)}`);
    }
    if (pose) {
      lines.push(`cam ${pose.centerX.toFixed(3)}, ${pose.centerY.toFixed(3)}`);
    }
    if (world) {
      lines.push(`world ${world.widthPx}×${world.heightPx}`);
      const halfW = Math.min(
        0.5,
        0.5 /
          (Math.max(0.01, this.worldPanWidth) *
            Math.max(0.01, this.exploreZoom)),
      );
      const halfH = Math.min(
        0.5,
        0.5 /
          (Math.max(0.01, this.worldPanHeight) *
            Math.max(0.01, this.exploreZoom)),
      );
      lines.push(
        `bounds ${halfW.toFixed(3)}–${(1 - halfW).toFixed(3)}, ${halfH.toFixed(3)}–${(1 - halfH).toFixed(3)}`,
      );
    }
    lines.push(
      this.interactionLocked
        ? 'follow OFF (edit)'
        : this.exploreMode === 'follow'
          ? 'follow ON'
          : 'follow OFF',
    );
    this.modeBadgeEl.textContent = lines.join('\n');
  }

  private logViewportWithoutModeChange(playW: number, playH: number): void {
    if (!import.meta.env.DEV) return;
    const mode = this.backyardPresentationMode;
    if (!mode) return;
    const w = Math.round(playW);
    const h = Math.round(playH);
    if (w === this.lastLoggedViewportW && h === this.lastLoggedViewportH) {
      return;
    }
    this.lastLoggedViewportW = w;
    this.lastLoggedViewportH = h;
    if (this.lastLoggedPresentationMode === mode) {
      // eslint-disable-next-line no-console
      console.info(
        `[Backyard] viewport ${w}×${h} (mode ${mode.toUpperCase()} unchanged)`,
      );
    }
  }

  /**
   * Places-map contain letterboxing only. Backyard fills via explore zoom —
   * never use blurred bleed behind Hubert/Raleigh (resize gutters).
   */
  private syncArtworkBleed(src: string | null): void {
    if (!src) {
      this.clearArtworkBleed();
      return;
    }
    this.artworkBleedEl.hidden = false;
    // Full-playfield cover bleed (reliable gutter fill) + soft blur.
    this.artworkBleedImgEl.style.backgroundImage = `url("${src}")`;
    this.artworkBleedImgEl.style.width = '100%';
    this.artworkBleedImgEl.style.height = '100%';
    this.artworkBleedImgEl.style.left = '0';
    this.artworkBleedImgEl.style.top = '0';
    this.artworkBleedImgEl.style.aspectRatio = '';
    this.artworkBleedImgEl.style.transform = 'none';
    this.root.classList.add('wp-has-bleed');
  }

  private clearArtworkBleed(): void {
    this.artworkBleedEl.hidden = true;
    this.artworkBleedImgEl.style.backgroundImage = '';
    this.artworkBleedImgEl.style.transform = '';
    this.artworkBleedImgEl.style.width = '';
    this.artworkBleedImgEl.style.height = '';
    this.artworkBleedImgEl.style.left = '';
    this.artworkBleedImgEl.style.top = '';
    this.artworkBleedImgEl.style.aspectRatio = '';
    this.root.classList.remove('wp-has-bleed');
  }

  private layoutArtworkBleed(): void {
    // Cover bleed is playfield-sized; nothing to re-layout per camera frame.
  }

  private setWorldArtwork(src: string, alt: string): void {
    this.worldArtworkEl.hidden = false;
    this.worldArtworkEl.setAttribute('role', 'img');
    this.worldArtworkEl.setAttribute('aria-label', alt);
    const runtimeSrc = resolveRuntimeArtUrl(src);
    // Prefer the plate over decorative props when both request bandwidth.
    this.worldArtworkImgEl.fetchPriority = 'high';
    this.worldArtworkImgEl.decoding = 'async';
    if (this.worldArtworkImgEl.getAttribute('src') !== runtimeSrc) {
      markTravelPhase('background_request_start', runtimeSrc.split('/').pop());
      // WebP miss → fall back to the source PNG without blocking travel forever.
      const onError = (): void => {
        this.worldArtworkImgEl.removeEventListener('error', onError);
        if (runtimeSrc !== src && this.worldArtworkImgEl.getAttribute('src') === runtimeSrc) {
          this.worldArtworkImgEl.src = src;
        }
      };
      this.worldArtworkImgEl.addEventListener('error', onError);
      this.worldArtworkImgEl.src = runtimeSrc;
    }
    this.worldArtworkImgEl.alt = alt;
  }

  private clearWorldArtwork(): void {
    this.worldArtworkEl.hidden = true;
    this.worldArtworkEl.removeAttribute('aria-label');
    this.worldArtworkEl.removeAttribute('role');
    this.worldArtworkImgEl.removeAttribute('src');
    this.worldArtworkImgEl.alt = '';
    this.worldArtworkEl.style.backgroundImage = '';
  }

  /**
   * Legacy optional landscape bleed for non-backyard spaces.
   * Backyard dual masters replace the bleed experiment.
   */
  private syncLandscapeBleed(
    space: PresenceSpace,
    playW: number,
    playH: number,
  ): void {
    if (isBackyardLocationId(space.id)) {
      this.landscapeBleedEl.hidden = true;
      this.landscapeBleedEl.classList.remove('is-active');
      this.landscapeBleedImgEl.removeAttribute('src');
      this.playfieldEl.classList.remove('wp-landscape-cover-active');
      this.hideBgCoverDebug();
      // Ensure master plate stays visible (bleed path must never hide it).
      const master = space.scenery.masterArtwork;
      if (master) {
        this.setWorldArtwork(master.src, master.alt);
      }
      return;
    }

    const bleed = space.scenery.landscapeBleedArtwork;
    const master = space.scenery.masterArtwork;
    const bleedSrc = bleed?.src?.trim() ?? '';
    const isLandscape = playW > playH;
    const useBleed = Boolean(bleedSrc) && Boolean(bleed) && isLandscape;

    if (!useBleed || !bleed) {
      this.landscapeBleedEl.hidden = true;
      this.landscapeBleedEl.classList.remove('is-active');
      this.landscapeBleedImgEl.removeAttribute('src');
      this.landscapeBleedImgEl.alt = '';
      this.playfieldEl.classList.remove('wp-landscape-cover-active');
      this.hideBgCoverDebug();
      if (master) {
        this.setWorldArtwork(master.src, master.alt);
      }
      return;
    }

    this.landscapeBleedEl.hidden = false;
    this.landscapeBleedEl.classList.add('is-active');
    if (this.landscapeBleedImgEl.getAttribute('src') !== bleedSrc) {
      this.landscapeBleedImgEl.src = bleedSrc;
    }
    this.landscapeBleedImgEl.alt = bleed.alt;
    this.playfieldEl.classList.add('wp-landscape-cover-active');
    this.worldArtworkEl.hidden = true;
    this.syncBgCoverDebug(playW, playH);
  }

  private hideBgCoverDebug(): void {
    this.bgCoverDebugEl.hidden = true;
    this.bgCoverDebugReadoutEl.textContent = '';
  }

  private syncBgCoverDebug(playW: number, playH: number): void {
    if (!this.bgCoverDebugEnabled) {
      this.hideBgCoverDebug();
      return;
    }
    const img = this.landscapeBleedImgEl;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (nw <= 0 || nh <= 0) {
      this.bgCoverDebugEl.hidden = false;
      this.bgCoverDebugReadoutEl.textContent =
        'bg cover (DEV ?bgdebug)\nwaiting for image natural size…';
      return;
    }
    const imageAspect = nw / nh;
    const insets = coverCropInsets(imageAspect, playW / Math.max(1, playH));
    // Cover scale: image is enlarged so the shorter axis fills the viewport.
    const scale = Math.max(playW / nw, playH / nh);
    const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
    this.bgCoverDebugEl.hidden = false;
    this.bgCoverDebugReadoutEl.textContent = [
      'bg cover (DEV ?bgdebug)',
      `viewport ${Math.round(playW)}×${Math.round(playH)}`,
      `natural ${nw}×${nh}`,
      `client ${img.clientWidth}×${img.clientHeight}`,
      `cover scale ${(scale * 100).toFixed(1)}%`,
      `crop L ${pct(insets.left)} R ${pct(insets.right)} T ${pct(insets.top)} B ${pct(insets.bottom)}`,
    ].join('\n');
  }

  private bindPlayfieldPointer(): void {
    this.playfieldEl.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      this.pointerStart = {
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
      };
      this.gestureConsumed = false;
    });

    this.playfieldEl.addEventListener('pointerup', (event) => {
      if (!this.pointerStart) return;
      const end = {
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
      };
      if (classifyHorizontalSwipe(this.pointerStart, end)) {
        this.gestureConsumed = true;
        this.pointerStart = null;
        return;
      }
      this.pointerStart = null;
      if (this.gestureConsumed || this.navigator.isLocked()) return;
      if (this.interactionLocked) return;
      if (!this.presence) return;
      // Atomic shed↔bucket sequence — ignore other world input until complete.
      if (this.shedSequence !== 'idle') return;

      const target = event.target;
      // Input priority: UI / interactive world objects first — never throw/move
      // when the child tapped a prop, card, utility, or editor chrome.
      if (target instanceof HTMLElement) {
        if (
          target.closest(
            '.wp-prop, .wp-creature, .wp-interact-card, .wp-utility, .wp-nav-cluster, .wp-hud, .wp-hud-left, .wp-editor, .wp-editor-chrome, [data-editor-ui], .wp-friends-btn, .wp-friends-picker, .wp-day-night-btn, .wp-pinecone-tree-hit',
          )
        ) {
          return;
        }
      }

      const worldPoint = this.clientToWorld(event.clientX, event.clientY);
      if (!worldPoint) return;

      const space = this.presence.getSpace();
      const aspect =
        space.worldAspectRatio && space.worldAspectRatio > 0
          ? space.worldAspectRatio
          : 16 / 9;
      const worldRect = this.worldLayerEl.getBoundingClientRect();
      const worldPx = Math.max(1, worldRect.width);
      if (isGardenFoodYard(space.id)) {
        const gardenObj = space.interactiveObjects?.find((o) =>
          isGardenObject(o.id),
        );
        if (gardenObj) {
          const layer = space.scenery.layers.find(
            (l) => l.id === (gardenObj.sceneryId ?? gardenObj.id),
          );
          if (
            pointInGardenHotspot(
              worldPoint,
              gardenObj,
              layer ?? null,
              aspect,
              worldPx,
            )
          ) {
            this.logGardenDebug('garden pointerdown (ground resolve)');
            this.lastInputKind = 'object';
            this.lastHitObjectId = gardenObj.id;
            if (this.playerInteraction.isPerforming()) {
              this.postKickCommand = { kind: 'object', obj: gardenObj };
              this.updateDebugInteractionLabel();
              return;
            }
            this.approachObject(gardenObj);
            this.updateDebugInteractionLabel();
            return;
          }
        }
      }

      // Mid-perform: finish current anim, then honor this command.
      if (this.playerInteraction.isPerforming()) {
        if (this.carriedSlot.isCarryingPinecone()) {
          this.postKickCommand = {
            kind: 'throw',
            x: worldPoint.x,
            y: worldPoint.y,
          };
        } else if (this.carriedSlot.isCarryingBucket()) {
          this.postKickCommand = {
            kind: 'placeBucket',
            x: worldPoint.x,
            y: worldPoint.y,
          };
        } else {
          this.postKickCommand = {
            kind: 'ground',
            x: worldPoint.x,
            y: worldPoint.y,
          };
        }
        return;
      }

      // While carrying a pinecone: valid ground tap throws (does not walk first).
      // Bucket / UI / props never reach here — they return above via .wp-prop.
      if (this.carriedSlot.isCarryingPinecone()) {
        this.cancelPendingKick();
        this.dismissFocus();
        this.pendingFocusId = null;
        this.lastInputKind = 'ground';
        this.lastHitObjectId = null;
        this.throwCarriedPinecone(worldPoint.x, worldPoint.y);
        this.updateDebugInteractionLabel();
        return;
      }

      // While carrying the bucket: valid ground tap walks then places; shed tap returns.
      this.cancelPendingKick();
      this.dismissFocus();
      this.pendingFocusId = null;
      this.lastInputKind = 'ground';
      this.lastHitObjectId = null;
      if (this.carriedSlot.isCarryingBucket()) {
        if (!this.isValidBucketPlacement(worldPoint.x, worldPoint.y)) {
          const clamped = clampWalkTarget(
            worldPoint.x,
            worldPoint.y,
            space,
          );
          this.showDebugClickTargets(worldPoint, clamped);
          this.presence.walkTo(worldPoint.x, worldPoint.y);
          this.playerInteraction.noteMoving(true);
          this.updateDebugWalkDestination(space);
          this.updateDebugInteractionLabel();
          return;
        }
        this.placeCarriedBucketAt(worldPoint.x, worldPoint.y);
        this.updateDebugInteractionLabel();
        return;
      }

      const clamped = clampWalkTarget(
        worldPoint.x,
        worldPoint.y,
        space,
      );
      this.showDebugClickTargets(worldPoint, clamped);
      this.presence.walkTo(worldPoint.x, worldPoint.y);
      this.playerInteraction.noteMoving(true);
      this.updateDebugWalkDestination(space);
      this.updateDebugInteractionLabel();
    });

    this.playfieldEl.addEventListener('pointercancel', () => {
      this.pointerStart = null;
    });
  }

  private clientToWorld(
    clientX: number,
    clientY: number,
  ): { x: number; y: number } | null {
    const rect = this.worldLayerEl.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  }

  private teardownPresence(): void {
    this.teardownRabbit();
    this.teardownBird();
    this.teardownFrogs();
    this.teardownFireflies(false);
    this.teardownPineconeTreeHit();
    this.replenishedPineconeObjects.length = 0;
    this.stopPineconeFallLoop();
    this.pineconeFalls.clear();
    this.clearKickTimers();
    this.clearPineconeLongPress();
    this.stopPineconeMotionLoop();
    this.stopPineconeThrowLoop();
    this.pineconeThrows.clear();
    this.playerInteraction.reset();
    this.carriedSlot.clear();
    this.pendingKickId = null;
    this.pendingPickupId = null;
    this.pendingBucketAction = null;
    this.pendingDepositBucketId = null;
    this.pendingPlacePoint = null;
    this.pendingFirepitAction = null;
    if (this.firepitFlareTimer != null) {
      window.clearTimeout(this.firepitFlareTimer);
      this.firepitFlareTimer = null;
    }
    this.postKickCommand = null;
    this.pineconeRuntime.clear();
    this.pineconeMotions.clear();
    this.bucketRuntime = null;
    this.shedSequence = 'idle';
    this.characterEl.classList.remove(
      'is-kicking',
      'is-picking-up',
      'is-throwing',
      'is-walking',
      'is-carrying',
      'is-carrying-bucket',
      'is-depositing',
    );
    this.dismissFocus(true);
    this.unsubPresence?.();
    this.unsubPresence = null;
    this.presence?.destroy();
    this.presence = null;
    this.currentSpace = null;
    this.backyardCameraSeeded = false;
    this.plateSwapPreserveCharacter = false;
    this.lastMobileCameraMode = null;
    this.propsEl.innerHTML = '';
    this.sceneryLayersEl.innerHTML = '';
    this.interactCardEl.hidden = true;
  }

  private render(): void {
    const space = this.navigator.getCurrentSpace();
    const hub = this.navigator.getCurrentHub();

    if (space) {
      this.renderPresence(space);
      return;
    }
    if (hub) {
      this.renderHub(hub);
    }
  }

  private renderPresence(space: PresenceSpace): void {
    this.root.classList.add('wp-is-presence');
    this.root.classList.remove('wp-is-hub');
    this.hubPanelEl.hidden = true;
    this.clearPlacesMapPins();
    this.utilityEl.hidden = false;
    this.characterEl.hidden = false;
    this.resetFriendsPicker();
    this.syncFriendsNavButton();
    applyCharacterForBackyard(space.id);
    this.syncPresencePlayerVisual();
    this.worldHintsEl.innerHTML = '';
    // Restore centered world-layer anchors (hub mode pins to top-left).
    this.worldLayerEl.style.left = '';
    this.worldLayerEl.style.top = '';

    const placeEl = this.cameraPlaceEl;
    syncLocationBadge(
      this.cameraLabelEl,
      this.locationBadgeEl,
      placeEl,
      space.id,
      space.displayName,
    );

    this.worldLayerEl.dataset.visual = space.placeholderVisual;
    // Backyard SceneWorlds are sized inside syncExploreZoom (fixed px).
    // Other spaces keep playfield-relative sizing here.
    if (!isBackyardLocationId(space.id)) {
      this.applyWorldLayerSize(space);
    }

    this.setupPresence(space);
    this.renderScenery(space);
    this.restoreReplenishedPinecones(space);
    this.syncFirepitVisual(space);
    this.syncBurnedPineconeLayers();
    this.renderWorldDebug(space);
    this.renderProps(space);
    this.setupRabbit(space);
    this.setupBird(space);
    this.setupFrogs(space);
    this.syncTimeOfDayVisual(space);
    this.syncDayNightButton(space);
    this.renderUtility(space.utilityTravel);
    this.syncExploreZoom(space);
    this.focusZoom = this.exploreZoom;

    this.sceneEl.setAttribute(
      'aria-label',
      `${space.displayName}. ${space.description}`,
    );
    this.statusEl.textContent = `${space.displayName}.`;
    this.notifySpaceChange(space.id);
  }

  /** Apply the active Friends selection as the walking player sprite. */
  private syncPresencePlayerVisual(): void {
    const active = getActivePlayableCharacter();
    const runtimeSrc = resolveRuntimeArtUrl(active.playerSrc);
    const current = this.characterBodyEl.getAttribute('src') ?? '';
    if (current !== runtimeSrc && current !== active.playerSrc) {
      this.characterBodyEl.src = runtimeSrc;
    }
    this.characterBodyEl.alt = active.displayName;
    this.characterEl.dataset.characterId = active.id;
    this.characterEl.style.aspectRatio = `${active.imageWidth} / ${active.imageHeight}`;
  }

  private isFriendsAvailable(): boolean {
    if (this.navigator.getCurrentSpace()) return true;
    return this.navigator.getCurrentHub()?.id === 'places-map';
  }

  private syncFriendsNavButton(): void {
    this.friendsBtnEl.hidden = !this.isFriendsAvailable() || this.friendsOpen;
  }

  private resetFriendsPicker(): void {
    this.friendsOpen = false;
    this.friendsPickerEl.hidden = true;
    this.root.classList.remove('wp-friends-open');
  }

  private hideFriendsChrome(): void {
    this.resetFriendsPicker();
    this.friendsBtnEl.hidden = true;
  }

  private openFriendsPicker(): void {
    if (this.friendsOpen || !this.isFriendsAvailable()) return;
    this.friendsOpen = true;
    this.friendsBtnEl.hidden = true;
    this.friendsPickerEl.hidden = false;
    this.root.classList.add('wp-friends-open');
    this.renderFriendsPickerCards();
    this.statusEl.textContent = 'Who do you want to be?';
  }

  private closeFriendsPicker(): void {
    this.resetFriendsPicker();
    this.syncFriendsNavButton();
    const space = this.navigator.getCurrentSpace();
    if (space) {
      this.statusEl.textContent = `${space.displayName}.`;
    }
  }

  private renderFriendsPickerCards(): void {
    const activeId = getActivePlayerCharacterId();
    this.friendsTrackEl.innerHTML = '';
    for (const character of listPlayableCharacters()) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'wp-friends-card';
      card.dataset.characterId = character.id;
      card.setAttribute('role', 'listitem');
      card.setAttribute(
        'aria-label',
        character.id === activeId
          ? `${character.displayName}, selected`
          : `Play as ${character.displayName}`,
      );
      card.classList.toggle('is-selected', character.id === activeId);
      const stage = document.createElement('span');
      stage.className = 'wp-friends-card-stage';
      stage.style.setProperty(
        '--wp-friends-relative-height',
        String(character.relativeHeight),
      );
      const img = document.createElement('img');
      img.src = resolveRuntimeArtUrl(character.playerSrc);
      img.alt = '';
      img.draggable = false;
      img.decoding = 'async';
      img.addEventListener('error', () => {
        if (img.getAttribute('src') !== character.playerSrc) {
          img.src = character.playerSrc;
        }
      });
      stage.append(img);
      const check = document.createElement('span');
      check.className = 'wp-friends-card-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      card.append(stage, check);
      card.addEventListener('click', () => {
        const space = this.navigator.getCurrentSpace();
        // Only pass a yard id for real backyards — Places map must register as
        // a travel pick (null), not a no-op non-yard string.
        const yardId =
          space && defaultCharacterForBackyard(space.id) != null
            ? space.id
            : null;
        setActivePlayerCharacterId(character.id, yardId);
        this.closeFriendsPicker();
      });
      this.friendsTrackEl.append(card);
    }
  }

  private renderHub(hub: TravelHubNode): void {
    this.teardownPresence();
    this.activeSceneWorld = null;
    this.backyardPresentationMode = null;
    this.root.classList.add('wp-is-hub');
    this.root.classList.remove('wp-is-presence', 'wp-porch-visible');
    this.syncDayNightButton(null);
    this.root.dataset.worldMode = 'hub';
    this.root.removeAttribute('data-camera-policy');
    this.characterEl.hidden = true;
    this.porchEl.hidden = true;
    this.hubPanelEl.hidden = hub.id === 'places-map';
    this.interactCardEl.hidden = true;
    this.landscapeBleedEl.hidden = true;
    this.landscapeBleedEl.classList.remove('is-active');
    this.landscapeBleedImgEl.removeAttribute('src');
    this.landscapeBleedImgEl.alt = '';
    this.playfieldEl.classList.remove('wp-landscape-cover-active');
    this.hideBgCoverDebug();
    this.sceneryLayersEl.innerHTML = '';
    this.debugObjectsEl.innerHTML = '';
    this.debugEl.hidden = true;
    this.worldLayerEl.style.left = '0';
    this.worldLayerEl.style.top = '0';
    this.worldLayerEl.style.width = '100%';
    this.worldLayerEl.style.height = '100%';
    this.worldLayerEl.style.aspectRatio = '';
    this.worldLayerEl.style.transform = 'none';
    this.worldLayerEl.dataset.visual = hub.placeholderVisual;
    this.worldLayerEl.dataset.worldMode = 'hub';
    this.worldLayerEl.removeAttribute('data-world-id');
    this.propsEl.innerHTML = '';

    const placeEl = this.cameraPlaceEl;
    if (hub.id === 'places-map') {
      this.locationBadgeEl.hidden = true;
      this.locationBadgeEl.removeAttribute('src');
      placeEl.textContent = '';
      placeEl.hidden = true;
      this.cameraLabelEl.classList.remove('wp-has-location-badge');
      this.cameraLabelEl.hidden = true;
    } else {
      syncLocationBadge(
        this.cameraLabelEl,
        this.locationBadgeEl,
        placeEl,
        null,
        hub.displayName,
      );
    }

    this.worldHintsEl.innerHTML = '';
    if (hub.id !== 'places-map') {
      const li = document.createElement('li');
      li.className = 'wp-world-title';
      li.textContent = hub.placeholderLabel ?? hub.displayName;
      this.worldHintsEl.append(li);
      this.mapTitleEl.hidden = true;
      this.mapTitleEl.textContent = '';
      this.mapTitleEl.setAttribute('aria-hidden', 'true');
    } else {
      this.mapTitleEl.hidden = false;
      this.mapTitleEl.textContent = PLACES_MAP_TITLE;
      this.mapTitleEl.setAttribute('role', 'heading');
      this.mapTitleEl.setAttribute('aria-level', '1');
      this.mapTitleEl.removeAttribute('aria-hidden');
    }

    this.hubPanelEl.textContent = hub.description;
    if (hub.id === 'places-map') {
      // Painted map + destination pins are the travel UI; keep pills out of the way.
      this.utilityEl.hidden = true;
      this.utilityEl.innerHTML = '';
      this.syncPlacesMapArtwork(hub, true);
      this.renderPlacesMapPins(hub);
      this.resetFriendsPicker();
      this.syncFriendsNavButton();
    } else {
      this.clearPlacesMapPins();
      this.hideFriendsChrome();
      this.utilityEl.hidden = false;
      this.renderUtility(hub.utilityTravel);
      this.root.classList.remove('wp-has-artwork');
      this.clearWorldArtwork();
      this.clearArtworkBleed();
      this.backyardPresentationMode = null;
      this.deviceClass = null;
    }

    this.sceneEl.setAttribute(
      'aria-label',
      `${hub.id === 'places-map' ? PLACES_MAP_TITLE : hub.displayName}. ${hub.description}`,
    );
    this.statusEl.textContent = `${hub.id === 'places-map' ? PLACES_MAP_TITLE : hub.displayName}.`;
    this.notifySpaceChange(null);
  }

  /** Landscape/portrait places-map plate swap + orientation-aware fit. */
  private syncPlacesMapArtwork(hub: TravelHubNode, force: boolean): void {
    if (hub.id !== 'places-map') return;
    const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
    this.deviceClass = resolveDeviceClass(playW, playH);
    const mode =
      this.editorPresentationOverride ??
      resolveBackyardPresentationMode(playW, playH);
    const orientation = placesMapOrientationFor(playW, playH);
    const mapFit = resolvePlacesMapFit(
      playW,
      playH,
      this.deviceClass,
    );
    const modeChanged = mode !== this.backyardPresentationMode;
    const orientationChanged = orientation !== this.placesMapOrientation;
    if (!force && !modeChanged && !orientationChanged) {
      // Same plate — still refresh fit when resizing within orientation.
      this.applyPlacesMapFit(playW, playH, mode, mapFit);
      startPlacesBackyardPreload(playW, playH, { includeProps: true });
      if (import.meta.env.DEV) {
        this.updateModeBadge(playW, playH);
      }
      return;
    }

    this.backyardPresentationMode = mode;
    this.placesMapOrientation = orientation;
    const master = placesMapMasterFor(mode, orientation);
    this.root.classList.add('wp-has-artwork');
    this.root.dataset.backyardLayout = mode;
    this.root.dataset.deviceClass = this.deviceClass;
    this.root.dataset.mapOrientation = orientation;
    this.root.dataset.viewportMode =
      this.deviceClass === 'mobile'
        ? 'mobile'
        : this.deviceClass === 'tablet'
          ? 'tablet'
          : 'desktop';
    this.root.dataset.orientation = orientation;
    this.setWorldArtwork(master.src, master.alt);
    this.applyPlacesMapFit(playW, playH, mode, mapFit);
    if (modeChanged) {
      resetPlacesBackyardPreload();
    }
    // Warm backyard plates for the current D/M presentation (non-blocking).
    startPlacesBackyardPreload(playW, playH, { includeProps: true });
    if (import.meta.env.DEV) {
      this.updateModeBadge(playW, playH);
    }
  }

  /**
   * Apply places-map object-fit + ocean letterbox.
   * Always full-plate contain (never cover) so continental US stays visible.
   */
  private applyPlacesMapFit(
    playW: number,
    playH: number,
    _mode: BackyardPresentationMode,
    mapFit = resolvePlacesMapFit(
      playW,
      playH,
      this.deviceClass ?? resolveDeviceClass(playW, playH),
    ),
  ): void {
    this.root.dataset.mapFit = mapFit;
    const img = this.worldArtworkImgEl;
    const device = this.deviceClass ?? resolveDeviceClass(playW, playH);
    const orientation =
      this.placesMapOrientation ?? placesMapOrientationFor(playW, playH);
    const masterSize = placesMapMasterSizeFor(orientation);
    const maxFrac = placesMapContainMaxFraction(playW, playH, device);
    const frame = placesMapContainFrame(
      playW,
      playH,
      maxFrac,
      masterSize.width,
      masterSize.height,
    );

    // Solid ocean behind the plate — no cream gutters, no blurred bleed.
    this.clearArtworkBleed();
    this.root.style.background = PLACES_MAP_LETTERBOX_COLOR;
    this.worldLayerEl.style.background = PLACES_MAP_LETTERBOX_COLOR;
    this.playfieldEl.style.background = PLACES_MAP_LETTERBOX_COLOR;
    this.sceneEl.style.background = PLACES_MAP_LETTERBOX_COLOR;

    img.style.objectFit = 'fill';
    img.style.objectPosition = 'center';
    img.style.position = 'absolute';
    img.style.left = '0';
    img.style.top = '0';
    img.style.right = 'auto';
    img.style.bottom = 'auto';
    img.style.width = `${frame.displayWidth}px`;
    img.style.height = `${frame.displayHeight}px`;
    img.style.transform = `translate(${frame.offsetX}px, ${frame.offsetY}px)`;
    this.layoutArtworkBleed();
  }

  /** Destination markers over the map — independent scale + kid-sized hits. */
  private renderPlacesMapPins(hub: TravelHubNode): void {
    this.mapPinsEl.innerHTML = '';
    this.mapPinsEl.hidden = false;
    const mode = this.backyardPresentationMode ?? 'desktop';
    const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
    const orientation =
      this.placesMapOrientation ?? placesMapOrientationFor(playW, playH);
    let index = 0;
    for (const pin of placesMapPinsFor(mode, orientation)) {
      const utility = hub.utilityTravel.find((u) => u.id === pin.utilityId);
      if (!utility) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wp-map-pin';
      button.dataset.utilityId = pin.utilityId;
      button.dataset.labelPlacement = pin.labelPlacement;
      button.style.setProperty('--pin-color', pin.color);
      button.style.setProperty('--pin-delay', `${index * 0.35}s`);
      button.setAttribute('aria-label', `Go to ${pin.shortLabel}`);
      button.title = pin.label;

      const visual = document.createElement('span');
      visual.className = 'wp-map-pin-visual';
      visual.setAttribute('aria-hidden', 'true');

      const marker = document.createElement('span');
      marker.className = 'wp-map-pin-marker';

      const label = document.createElement('span');
      label.className = 'wp-map-pin-label';
      label.textContent = pin.shortLabel;

      // Leader stays anchored on the pin tip so a swung-out bubble still
      // names the spot the marker touches.
      const leader = document.createElement('span');
      leader.className = 'wp-map-pin-leader';
      leader.setAttribute('aria-hidden', 'true');

      visual.append(marker, label, leader);
      button.append(visual);

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        void this.activatePlacesMapPin(button, utility.id);
      });
      this.mapPinsEl.append(button);
      index += 1;
    }
    this.layoutPlacesMapPins();
    // Bubble widths settle once webfonts land; re-measure on the next frame.
    window.requestAnimationFrame(() => this.layoutPlacesMapPins());
  }

  /** Brief squash/bounce, then travel — respects reduced motion. */
  private async activatePlacesMapPin(
    button: HTMLButtonElement,
    utilityId: string,
  ): Promise<void> {
    if (button.classList.contains('is-selecting')) return;
    button.classList.add('is-selecting');
    beginTravelLoad(utilityId);
    // Start the destination plate during bounce/fade — don't wait for scene swap.
    this.prefetchDestinationBackground(utilityId);
    const delay = prefersReducedMotion() ? 0 : 280;
    if (delay > 0) {
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, delay);
      });
    }
    markTravelPhase('pin_bounce_done', `${delay}ms`);
    this.previousLocationId = this.navigator.getCurrentLocationId();
    this.navigator.activateUtilityTravel(utilityId);
  }

  /** Kick off the correct D/M backyard plate as soon as a pin is chosen. */
  private prefetchDestinationBackground(utilityId: string): void {
    const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
    const mode =
      this.editorPresentationOverride ??
      resolveBackyardPresentationMode(playW, playH);
    let src: string | null = null;
    if (utilityId === 'to-hubert') {
      src = backyardMasterFor(mode).src;
    } else if (utilityId === 'to-raleigh') {
      src = raleighMasterFor(mode).src;
    } else if (utilityId === 'to-spanish-fork') {
      src = spanishForkMasterFor(mode).src;
    }
    if (!src) return;
    const runtimeSrc = resolveRuntimeArtUrl(src);
    markTravelPhase('background_request_start', `prefetch:${runtimeSrc.split('/').pop()}`);
    void preloadImageUrl(runtimeSrc);
  }

  private layoutPlacesMapPins(): void {
    if (this.mapPinsEl.hidden) return;
    const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
    if (playW <= 0 || playH <= 0) return;
    const mode = this.backyardPresentationMode ?? 'desktop';
    const device = this.deviceClass ?? resolveDeviceClass(playW, playH);
    const orientation =
      this.placesMapOrientation ?? placesMapOrientationFor(playW, playH);
    const masterSize = placesMapMasterSizeFor(orientation);
    const maxFrac = placesMapContainMaxFraction(playW, playH, device);
    const frame = placesMapContainFrame(
      playW,
      playH,
      maxFrac,
      masterSize.width,
      masterSize.height,
    );
    const metrics = resolvePlacesMapPinMetrics(device, playW, playH);
    const pins = placesMapPinsFor(mode, orientation);
    const buttons = [
      ...this.mapPinsEl.querySelectorAll<HTMLButtonElement>('.wp-map-pin'),
    ];

    const centers = pins.map((pin) => {
      const n = containFrameImagePointToViewport(
        pin.x,
        pin.y,
        frame,
        playW,
        playH,
      );
      return {
        pin,
        xPx: n.x * playW,
        yPx: n.y * playH,
      };
    });

    // Pass 1 — chrome sizes must land before the parchment bubble is measured.
    const placed: {
      button: HTMLButtonElement;
      visual: HTMLElement;
      label: HTMLElement;
      leader: HTMLElement;
      entry: (typeof centers)[number];
    }[] = [];
    for (const button of buttons) {
      const entry = centers.find(
        (c) => c.pin.utilityId === button.dataset.utilityId,
      );
      const visual = button.querySelector<HTMLElement>('.wp-map-pin-visual');
      const label = button.querySelector<HTMLElement>('.wp-map-pin-label');
      const leader = button.querySelector<HTMLElement>('.wp-map-pin-leader');
      if (!entry || !visual || !label || !leader) {
        button.hidden = true;
        continue;
      }
      button.hidden = false;
      button.style.setProperty('--pin-marker', `${metrics.markerSize}px`);
      button.style.setProperty('--pin-label-size', `${metrics.labelFontSize}px`);
      button.style.setProperty('--pin-hit-min', `${metrics.hitMin}px`);
      button.style.setProperty(
        '--pin-label-gap',
        `${PLACES_MAP_PIN_LABEL_GAP}px`,
      );
      placed.push({ button, visual, label, leader, entry });
    }

    // Pass 2 — swing each bubble clear of its neighbours, then size the taps.
    const boxes = placed.map(({ visual, label, entry }) =>
      placesMapPinBox({
        anchorXPx: entry.xPx,
        anchorYPx: entry.yPx,
        metrics,
        placement: entry.pin.labelPlacement,
        label: { width: label.offsetWidth, height: label.offsetHeight },
        visualHeight: visual.offsetHeight,
        visualWidth: visual.offsetWidth,
        playfieldWidth: playW,
        playfieldHeight: playH,
      }),
    );
    const hits = separatePlacesMapPinBoxes(
      boxes.map((box) => ({
        hit: box.hit,
        markerXPx: box.markerXPx,
        markerYPx: box.markerYPx,
      })),
    );

    placed.forEach(({ button, visual, label, leader, entry }, i) => {
      const box = boxes[i]!;
      const hit = hits[i]!;
      button.style.left = `${(hit.left / playW) * 100}%`;
      button.style.top = `${(hit.top / playH) * 100}%`;
      button.style.width = `${(hit.width / playW) * 100}%`;
      button.style.height = `${(hit.height / playH) * 100}%`;

      // Anchor the visible marker on the geographic pin head inside the hit box.
      visual.style.left = `${entry.xPx - hit.left}px`;
      visual.style.top = `${entry.yPx - hit.top}px`;
      label.style.setProperty('--pin-label-dx', `${box.labelDx}px`);
      label.style.setProperty('--pin-label-dy', `${box.labelDy}px`);

      leader.hidden = !box.leader.visible;
      leader.style.left = `${box.leader.tipXPx}px`;
      leader.style.top = `${box.leader.tipYPx}px`;
      leader.style.width = `${box.leader.lengthPx}px`;
      leader.style.transform = `rotate(${box.leader.angleRad}rad)`;
    });
  }

  private clearPlacesMapPins(): void {
    this.mapPinsEl.innerHTML = '';
    this.mapPinsEl.hidden = true;
    this.mapTitleEl.hidden = true;
    this.mapTitleEl.textContent = '';
    this.mapTitleEl.setAttribute('aria-hidden', 'true');
    this.root.removeAttribute('data-map-fit');
    this.root.removeAttribute('data-map-orientation');
    this.placesMapOrientation = null;
    this.root.style.background = '';
    this.worldLayerEl.style.background = '';
    this.playfieldEl.style.background = '';
    this.sceneEl.style.background = '';
    this.worldArtworkEl.style.backgroundSize = '';
    this.worldArtworkImgEl.style.objectPosition = '';
    this.worldArtworkImgEl.style.objectFit = '';
    this.worldArtworkImgEl.style.left = '';
    this.worldArtworkImgEl.style.top = '';
    this.worldArtworkImgEl.style.width = '';
    this.worldArtworkImgEl.style.height = '';
    this.worldArtworkImgEl.style.right = '';
    this.worldArtworkImgEl.style.bottom = '';
    this.worldArtworkImgEl.style.transform = '';
    this.clearArtworkBleed();
  }

  /**
   * Size the world layer.
   *
   * Backyard SceneWorlds use a fixed pixel box (never tied to the browser).
   * Other spaces keep the legacy playfield-relative sizing until migrated.
   */
  private applyWorldLayerSize(space: PresenceSpace): void {
    const world =
      this.activeSceneWorld ??
      (isBackyardLocationId(space.id) && this.backyardPresentationMode
        ? resolveBackyardSceneWorld(space.id, this.backyardPresentationMode)
        : null);

    if (world) {
      this.activeSceneWorld = world;
      this.worldLayerEl.style.width = `${world.widthPx}px`;
      this.worldLayerEl.style.height = `${world.heightPx}px`;
      this.worldLayerEl.style.aspectRatio = '';
      this.worldLayerEl.dataset.worldMode = 'fixed';
      this.worldLayerEl.dataset.worldId = world.id;
      return;
    }

    this.worldLayerEl.dataset.worldMode = 'fluid';
    this.worldLayerEl.removeAttribute('data-world-id');
    this.worldLayerEl.style.width = `${space.worldWidth * 100}%`;
    if (space.worldAspectRatio && space.worldAspectRatio > 0) {
      this.worldLayerEl.style.aspectRatio = String(space.worldAspectRatio);
      this.worldLayerEl.style.height = 'auto';
    } else {
      this.worldLayerEl.style.aspectRatio = '';
      this.worldLayerEl.style.height = `${space.worldHeight * 100}%`;
    }
  }

  private renderScenery(space: PresenceSpace): void {
    const { scenery } = space;
    const master = scenery.masterArtwork;
    this.root.classList.toggle('wp-has-artwork', Boolean(master));
    this.sceneryEl.dataset.visual = space.placeholderVisual;

    if (!master) {
      this.clearWorldArtwork();
    } else {
      this.setWorldArtwork(master.src, master.alt);
    }

    const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
    this.syncLandscapeBleed(space, playW, playH);

    this.sceneryLayersEl.innerHTML = '';
    for (const layer of scenery.layers) {
      // Interactive props draw their own placeholders; skip duplicate empty slots.
      if (layer.kind === 'prop' && !layer.src) continue;
      // Master plate replaces CSS placeholder scenery for this prototype pass.
      if (master && !layer.src) continue;

      const el = document.createElement('div');
      el.className = `wp-scenery-layer${layer.placeholderClass ? ` ${layer.placeholderClass}` : ''}`;
      el.dataset.layerId = layer.id;
      el.dataset.layerKind = layer.kind;
      el.dataset.layerDepth = String(layer.depth ?? layer.zIndex);

      if (layer.src) {
        el.classList.add('wp-scenery-layer-image');
        if (layer.id === SHED_OBJECT_ID) {
          this.shedClosedSrc = layer.src;
          el.dataset.closedSrc = layer.src;
        }
        if (this.deferDecorativeArt) {
          el.dataset.pendingSrc = layer.src;
        } else {
          el.style.backgroundImage = runtimeCssUrl(layer.src);
        }
        if (layer.alt) el.setAttribute('aria-label', layer.alt);
        if (layer.spriteFrame) {
          applySpriteFrameBackground(
            el,
            layer.spriteFrame,
            layer.spriteFrame.imageWidth,
            layer.spriteFrame.imageHeight,
          );
        }
      }

      if (layer.x != null && layer.y != null) {
        el.classList.add('wp-scenery-layer-prop');
        if (layer.kind === 'ground-decal' || layer.kind === 'nature-scatter') {
          el.classList.add('wp-scenery-layer-ground-decal');
        }
        if (layer.kind === 'nature-scatter') {
          el.classList.add('wp-scenery-layer-nature-scatter');
        }
        el.style.left = `${layer.x * 100}%`;
        el.style.top = `${layer.y * 100}%`;
        // Y-depth sorting — same formula family as character / interactive props.
        // Ground decals use a lower band (see groundDecalDepthFromY).
        const paintDepth = layer.depth ?? depthFromY(layer.y);
        el.style.zIndex = String(paintDepth);
        el.dataset.layerDepth = String(paintDepth);
        const rot = layer.rotationDeg ?? 0;
        // Freeform quads bake rotation into shapeCorners — skip CSS rotate.
        if (layer.shapeCorners && layer.shapeCorners.length >= 3) {
          el.style.setProperty('--wp-prop-rot', '0deg');
          delete el.dataset.rotationDeg;
        } else {
          el.style.setProperty('--wp-prop-rot', `${rot}deg`);
          if (rot) el.dataset.rotationDeg = String(rot);
          else delete el.dataset.rotationDeg;
        }
        if (layer.flipX) {
          el.style.setProperty('--wp-prop-flip-x', '-1');
          el.dataset.flipX = '1';
        } else {
          el.style.setProperty('--wp-prop-flip-x', '1');
          delete el.dataset.flipX;
        }
      } else {
        // Full-bleed layers may carry an explicit depth (occluders that
        // re-paint plate regions above the character); zIndex otherwise.
        el.style.zIndex = String(layer.depth ?? layer.zIndex);
      }
      if (layer.width != null) {
        el.style.width = `${layer.width * 100}%`;
      }
      if (layer.height != null) {
        el.style.height = `${layer.height * 100}%`;
      }
      if (layer.shapeCorners && layer.shapeCorners.length >= 3 && layer.width && layer.height) {
        el.classList.add('wp-scenery-layer-shaped');
        const clipFn =
          layer.shapeCornerStyle === 'top'
            ? roundedTopQuadClipPathPercent
            : roundedQuadClipPathPercent;
        el.style.clipPath = `polygon(${clipFn(
          layer.shapeCorners,
          layer.width,
          layer.height,
        )})`;
      }
      if (layer.clipPolygon && layer.clipPolygon.length >= 3) {
        el.classList.add('wp-scenery-layer-occluder');
        if (layer.occlusionRole === 'occlusion') {
          el.classList.add('wp-scenery-layer-occlusion');
        }
        el.style.clipPath = `polygon(${layer.clipPolygon
          .map((p) => `${p.x * 100}% ${p.y * 100}%`)
          .join(', ')})`;
      }
      if (layer.occlusionRole) {
        el.dataset.occlusionRole = layer.occlusionRole;
      }
      if (layer.ambient) {
        el.classList.add(`wp-ambient-${layer.ambient}`);
        el.dataset.ambient = layer.ambient;
      }
      if (layer.groundShadow) {
        el.classList.add('wp-has-ground-shadow');
        el.dataset.groundShadow = layer.groundShadow;
        const shadow = document.createElement('span');
        shadow.className = `wp-ground-shadow wp-ground-shadow-${layer.groundShadow}`;
        shadow.setAttribute('aria-hidden', 'true');
        el.append(shadow);
      }

      const debugLabel = document.createElement('span');
      debugLabel.className = 'wp-scenery-debug-label';
      const depthLabel = layer.x != null && layer.y != null
        ? (layer.depth ?? depthFromY(layer.y))
        : (layer.depth ?? layer.zIndex);
      debugLabel.textContent = `${layer.id} · depth ${depthLabel}`;
      el.append(debugLabel);

      this.sceneryLayersEl.append(el);
    }
    this.renderPropToggles(space);
    this.ensureShedClosedVisual();
    this.syncBucketVisibility();
  }

  private renderPropToggles(space: PresenceSpace): void {
    this.propTogglesEl.innerHTML = '';
    const propLayers = space.scenery.layers.filter(
      (l) => l.src && l.x != null && l.y != null && !l.clipPolygon,
    );
    for (const layer of propLayers) {
      const label = document.createElement('label');
      label.className = 'wp-dev-prop-toggle';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.checked = true;
      input.addEventListener('change', () => {
        const el = this.sceneryLayersEl.querySelector(
          `[data-layer-id="${layer.id}"]`,
        ) as HTMLElement | null;
        if (el) el.hidden = !input.checked;
        const hotspot = this.propsEl.querySelector(
          `[data-scenery-id="${layer.id}"]`,
        ) as HTMLElement | null;
        if (hotspot) hotspot.hidden = !input.checked;
      });
      label.append(input, document.createTextNode(layer.id));
      this.propTogglesEl.append(label);
    }
  }

  private renderWorldDebug(space: PresenceSpace): void {
    this.debugEl.hidden = false;
    this.debugWorldBoundsEl.style.left = '0';
    this.debugWorldBoundsEl.style.top = '0';
    this.debugWorldBoundsEl.style.width = '100%';
    this.debugWorldBoundsEl.style.height = '100%';
    this.debugClickEl.hidden = true;
    this.debugClampEl.hidden = true;

    const bounds = space.walkBounds;
    this.debugWalkBoundsEl.style.left = `${bounds.xMin * 100}%`;
    this.debugWalkBoundsEl.style.top = `${bounds.yMin * 100}%`;
    this.debugWalkBoundsEl.style.width = `${(bounds.xMax - bounds.xMin) * 100}%`;
    this.debugWalkBoundsEl.style.height = `${(bounds.yMax - bounds.yMin) * 100}%`;

    this.debugWalkPolygonEl.innerHTML = '';
    this.debugVerticesEl.innerHTML = '';
    this.debugObstaclesEl.innerHTML = '';
    this.debugOriginsEl.innerHTML = '';
    if (space.walkPolygon && space.walkPolygon.length >= 3) {
      const poly = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'polygon',
      );
      poly.setAttribute(
        'points',
        space.walkPolygon.map((p) => `${p.x},${p.y}`).join(' '),
      );
      this.debugWalkPolygonEl.append(poly);
      this.debugWalkPolygonEl.style.display = '';

      space.walkPolygon.forEach((p, index) => {
        const vertex = document.createElement('div');
        vertex.className = 'wp-debug-vertex';
        vertex.style.left = `${p.x * 100}%`;
        vertex.style.top = `${p.y * 100}%`;
        vertex.textContent = String(index);
        this.debugVerticesEl.append(vertex);
      });
    } else {
      this.debugWalkPolygonEl.style.display = 'none';
    }

    for (const obstacle of space.walkObstacles ?? []) {
      if (obstacle.polygon.length < 3) continue;
      const poly = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'polygon',
      );
      poly.setAttribute(
        'points',
        obstacle.polygon.map((p) => `${p.x},${p.y}`).join(' '),
      );
      poly.dataset.obstacleId = obstacle.id;
      this.debugObstaclesEl.append(poly);
    }

    for (const layer of space.scenery.layers) {
      if (layer.x == null || layer.y == null || !layer.src) continue;
      if (layer.clipPolygon && layer.occlusionRole !== 'background') continue;
      const origin = document.createElement('div');
      origin.className = 'wp-debug-origin';
      origin.style.left = `${layer.x * 100}%`;
      origin.style.top = `${layer.y * 100}%`;
      const depth = layer.depth ?? depthFromY(layer.y);
      const scale =
        layer.width != null ? ` · w=${layer.width.toFixed(3)}` : '';
      origin.textContent =
        `${layer.id} · (${layer.x.toFixed(2)}, ${layer.y.toFixed(2)}) · ` +
        `depth ${depth}${scale}`;
      this.debugOriginsEl.append(origin);
    }

    this.debugReachMarkersEl.innerHTML = '';
    if (space.id === 'hubert') {
      for (const marker of BACKYARD_WALK_REACH_MARKERS) {
        const el = document.createElement('div');
        el.className = 'wp-debug-reach-marker';
        el.dataset.reachId = marker.id;
        el.style.left = `${marker.x * 100}%`;
        el.style.top = `${marker.y * 100}%`;
        el.textContent = marker.label;
        this.debugReachMarkersEl.append(el);
      }
    }

    this.debugObjectsEl.innerHTML = '';
    const aspect =
      space.worldAspectRatio && space.worldAspectRatio > 0
        ? space.worldAspectRatio
        : 16 / 9;

    for (const obj of space.interactiveObjects ?? []) {
      if (obj.hitRadius > 0) {
        const hitRing = document.createElement('div');
        hitRing.className = 'wp-debug-activation wp-debug-hotspot-ring';
        hitRing.style.left = `${obj.x * 100}%`;
        hitRing.style.top = `${obj.y * 100}%`;
        hitRing.style.width = `${obj.hitRadius * 2 * 100}%`;
        hitRing.style.height = `${obj.hitRadius * 2 * 100 * aspect}%`;
        hitRing.title = `${obj.title} hotspot`;
        this.debugObjectsEl.append(hitRing);
      }

      const hit = document.createElement('div');
      hit.className = 'wp-debug-object-point wp-debug-object-hit';
      hit.style.left = `${obj.x * 100}%`;
      hit.style.top = `${obj.y * 100}%`;
      hit.textContent = `${obj.title} hit`;
      this.debugObjectsEl.append(hit);

      const stand = document.createElement('div');
      stand.className = 'wp-debug-object-point';
      stand.style.left = `${obj.standX * 100}%`;
      stand.style.top = `${obj.standY * 100}%`;
      stand.textContent = `${obj.title} stand`;
      this.debugObjectsEl.append(stand);

      const zone = obj.activationZone;
      if (zone && zone.enabled !== false) {
        const zoneEl = document.createElement('div');
        zoneEl.className = 'wp-debug-activation wp-debug-activation-zone';
        zoneEl.style.left = `${zone.x * 100}%`;
        zoneEl.style.top = `${zone.y * 100}%`;
        if (zone.shape === 'circle') {
          const r = zone.radius ?? 0;
          zoneEl.style.width = `${r * 2 * 100}%`;
          zoneEl.style.height = `${r * 2 * 100 * aspect}%`;
          zoneEl.style.borderRadius = '50%';
        } else {
          zoneEl.style.width = `${(zone.width ?? 0) * 100}%`;
          zoneEl.style.height = `${(zone.height ?? 0) * 100}%`;
          zoneEl.style.borderRadius = '2px';
        }
        zoneEl.title = `${obj.title} activation (unused)`;
        zoneEl.dataset.unused = 'true';
        this.debugObjectsEl.append(zoneEl);
      }
    }

    // Occlusion crop rectangles (walk-behind strips).
    for (const layer of space.scenery.layers) {
      const rect = layer.occlusionRect;
      if (!rect || layer.occlusionRole !== 'occlusion') {
        if (
          layer.occlusionRole === 'occlusion' &&
          layer.clipPolygon &&
          layer.clipPolygon.length >= 3 &&
          !layer.occlusionRect
        ) {
          const poly = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'polygon',
          );
          poly.classList.add('wp-debug-occlusion-poly');
          poly.setAttribute(
            'points',
            layer.clipPolygon.map((p) => `${p.x},${p.y}`).join(' '),
          );
          this.debugObstaclesEl.append(poly);
        }
        continue;
      }
      const box = document.createElement('div');
      box.className = 'wp-debug-occlusion-rect';
      box.style.left = `${rect.x * 100}%`;
      box.style.top = `${rect.y * 100}%`;
      box.style.width = `${rect.width * 100}%`;
      box.style.height = `${rect.height * 100}%`;
      box.title = `${layer.id} occlusion`;
      box.textContent = 'occlusion';
      this.debugObjectsEl.append(box);
    }

    this.debugDestEl = this.debugObjectsEl.querySelector('.wp-debug-destination');
    if (!this.debugDestEl) {
      this.debugDestEl = document.createElement('div');
      this.debugDestEl.className = 'wp-debug-destination';
      this.debugDestEl.hidden = true;
      this.debugObjectsEl.append(this.debugDestEl);
    }
    this.renderFireflyHomeDebug(space);
  }

  /** Hubert firefly home rectangle + live fly positions (debug overlay). */
  private renderFireflyHomeDebug(space: PresenceSpace): void {
    this.debugFireflyHomeEl.hidden = true;
    this.debugFireflyPointsEl.innerHTML = '';
    if (!this.debugEnabled || space.id !== 'hubert') return;

    const bounds = HUBERT_FIREFLY_HOME_BOUNDS;
    this.debugFireflyHomeEl.hidden = false;
    this.debugFireflyHomeEl.style.left = `${bounds.minX * 100}%`;
    this.debugFireflyHomeEl.style.top = `${bounds.minY * 100}%`;
    this.debugFireflyHomeEl.style.width = `${(bounds.maxX - bounds.minX) * 100}%`;
    this.debugFireflyHomeEl.style.height = `${(bounds.maxY - bounds.minY) * 100}%`;
    this.debugFireflyHomeEl.title =
      `firefly home x[${bounds.minX.toFixed(4)}, ${bounds.maxX.toFixed(4)}] ` +
      `y[${bounds.minY.toFixed(4)}, ${bounds.maxY.toFixed(4)}]`;

    this.syncFireflyDebugOverlay();
  }

  private syncFireflyDebugOverlay(): void {
    const space = this.currentSpace;
    if (!this.debugEnabled || !space || space.id !== 'hubert') {
      this.debugFireflyHomeEl.hidden = true;
      this.debugFireflyPointsEl.innerHTML = '';
      return;
    }

    const bounds = HUBERT_FIREFLY_HOME_BOUNDS;
    this.debugFireflyHomeEl.hidden = false;
    this.debugFireflyHomeEl.style.left = `${bounds.minX * 100}%`;
    this.debugFireflyHomeEl.style.top = `${bounds.minY * 100}%`;
    this.debugFireflyHomeEl.style.width = `${(bounds.maxX - bounds.minX) * 100}%`;
    this.debugFireflyHomeEl.style.height = `${(bounds.maxY - bounds.minY) * 100}%`;

    this.debugFireflyPointsEl.innerHTML = '';
    const state = this.fireflyGroupState;
    if (!state) return;

    for (const fly of state.instances) {
      const dot = document.createElement('div');
      dot.className = 'wp-debug-firefly-point';
      dot.style.left = `${fly.x * 100}%`;
      dot.style.top = `${fly.y * 100}%`;
      dot.title = `${fly.id} (${fly.x.toFixed(4)}, ${fly.y.toFixed(4)})`;
      this.debugFireflyPointsEl.append(dot);

      const home = document.createElement('div');
      home.className = 'wp-debug-firefly-home-point';
      const homeX = state.homeX + fly.homeOffsetX;
      const homeY = state.homeY + fly.homeOffsetY;
      home.style.left = `${homeX * 100}%`;
      home.style.top = `${homeY * 100}%`;
      home.title = `${fly.id} home (${homeX.toFixed(4)}, ${homeY.toFixed(4)})`;
      this.debugFireflyPointsEl.append(home);
    }
  }

  private updateDebugWalkDestination(space: PresenceSpace): void {
    if (!this.debugEnabled || !this.presence) return;
    if (!this.debugDestEl) return;
    const state = this.presence.getState();
    const dx = state.destinationX;
    const dy = state.destinationY;
    if (dx == null || dy == null) {
      this.debugDestEl.hidden = true;
      return;
    }
    this.debugDestEl.hidden = false;
    this.debugDestEl.style.left = `${dx * 100}%`;
    this.debugDestEl.style.top = `${dy * 100}%`;
    const pending = this.pendingFocusId
      ? space.interactiveObjects?.find((o) => o.id === this.pendingFocusId)
      : null;
    this.debugDestEl.textContent = pending
      ? `dest → ${pending.title} (object)`
      : 'dest (ground)';
  }

  private updateDebugInteractionLabel(): void {
    if (!this.debugEnabled) return;
    const kind = this.lastInputKind ?? '—';
    const hit = this.lastHitObjectId ?? 'none';
    const pending = this.pendingFocusId ?? 'none';
    this.debugClickEl.dataset.inputKind = kind;
    if (this.debugClickEl.hidden) return;
    const raw = this.debugClickEl.textContent ?? '';
    const base = raw.split(' · ')[0] || raw || 'click';
    this.debugClickEl.textContent =
      `${base} · ${kind}` +
      (kind === 'object' ? ` hit=${hit}` : '') +
      ` pending=${pending}`;
  }

  private showDebugClickTargets(
    clicked: { x: number; y: number },
    clamped: { x: number; y: number },
  ): void {
    this.debugClickEl.hidden = false;
    this.debugClickEl.style.left = `${clicked.x * 100}%`;
    this.debugClickEl.style.top = `${clicked.y * 100}%`;
    this.debugClickEl.textContent = `click ${clicked.x.toFixed(2)}, ${clicked.y.toFixed(2)}`;

    this.debugClampEl.hidden = false;
    this.debugClampEl.style.left = `${clamped.x * 100}%`;
    this.debugClampEl.style.top = `${clamped.y * 100}%`;
    this.debugClampEl.textContent = `clamp ${clamped.x.toFixed(2)}, ${clamped.y.toFixed(2)}`;
    this.updateDebugInteractionLabel();
  }

  private setupPresence(space: PresenceSpace): void {
    this.teardownPresence();
    this.currentSpace = space;
    const from = this.previousLocationId;
    this.previousLocationId = null;
    const spawn =
      (from && space.arrivalSpawns?.[from]) || space.start;

    this.presence = new PresenceController(space, { spawn });
    this.seedPineconeRuntime(space);
    this.seedBucketRuntime(space);
    this.syncExploreZoom(space);
    this.focusZoom = this.exploreZoom;
    this.unsubPresence = this.presence.subscribe((state) => {
      this.applyPresenceState(space, state);
    });
  }

  /** Capture authored pinecone placement into mutable runtime state (once per enter). */
  private seedPineconeRuntime(space: PresenceSpace): void {
    this.pineconeRuntime.clear();
    this.pineconeMotions.clear();
    this.pineconeThrows.clear();
    this.stopPineconeThrowLoop();
    this.carriedSlot.clear();
    this.characterEl.classList.remove(
      'is-carrying',
      'is-carrying-bucket',
      'is-throwing',
      'is-depositing',
    );
    for (const obj of space.interactiveObjects ?? []) {
      if (!isKickablePinecone(obj.id)) continue;
      const layer = space.scenery.layers.find(
        (l) => l.id === (obj.sceneryId ?? obj.id),
      );
      const x = layer?.x ?? obj.x;
      const y = layer?.y ?? obj.y;
      const burned = burnedPineconeIdsForYard(space.id).has(obj.id);
      this.pineconeRuntime.set(obj.id, {
        id: obj.id,
        x,
        y,
        rotation: layer?.rotationDeg ?? 0,
        state: burned ? 'burned' : 'ground',
        containerId: null,
        hitOffsetX: obj.x - x,
        hitOffsetY: obj.y - y,
        standOffsetX: obj.standX - x,
        standOffsetY: obj.standY - y,
        width: layer?.width ?? 0.014,
        height: layer?.height ?? 0.02,
      });
    }
  }

  /**
   * Seed bucket runtime. Gameplay starts storedInShed (hidden).
   * World Editor (?edit=1) keeps the authored yard pose visible for layout.
   */
  private seedBucketRuntime(space: PresenceSpace): void {
    this.bucketRuntime = null;
    this.hubertBucketCompleteFired = false;
    this.shedSequence = 'idle';
    const obj = space.interactiveObjects?.find((o) => isBucketObject(o.id));
    if (!obj) return;
    const layer = space.scenery.layers.find(
      (l) => l.id === (obj.sceneryId ?? obj.id),
    );
    const x = layer?.x ?? obj.x;
    const y = layer?.y ?? obj.y;
    const editing = this.isWorldEditorActive();
    this.bucketRuntime = {
      id: obj.id,
      state: editing ? 'ground' : 'storedInShed',
      x,
      y,
      rotation: layer?.rotationDeg ?? 0,
      authoredX: x,
      authoredY: y,
      authoredRotation: layer?.rotationDeg ?? 0,
      contents: [],
      hitOffsetX: obj.x - x,
      hitOffsetY: obj.y - y,
      standOffsetX: obj.standX - x,
      standOffsetY: obj.standY - y,
      width: layer?.width ?? 0.048,
      height: layer?.height ?? 0.065,
    };
  }

  private isWorldEditorActive(): boolean {
    try {
      return new URLSearchParams(window.location.search).get('edit') === '1';
    } catch {
      return false;
    }
  }

  private pineconeHitRadiusWorld(): number {
    const worldPx = Math.max(1, this.worldLayerEl.getBoundingClientRect().width);
    return PINECONE_HIT_MIN_CSS_PX / worldPx / 2;
  }

  /** Prefer the pinecone whose center is closest to the tap when hits overlap. */
  private resolvePineconeClickTarget(
    event: MouseEvent,
    fallback: InteractiveObject,
  ): InteractiveObject {
    if (!this.presence || !isKickablePinecone(fallback.id)) return fallback;
    const space = this.presence.getSpace();
    const worldPoint = this.clientToWorld(event.clientX, event.clientY);
    if (!worldPoint) return fallback;
    const aspect =
      space.worldAspectRatio && space.worldAspectRatio > 0
        ? space.worldAspectRatio
        : 16 / 9;
    return (
      resolveClosestPineconeAtPoint(
        worldPoint,
        space.interactiveObjects ?? [],
        aspect,
        this.pineconeHitRadiusWorld(),
      ) ?? fallback
    );
  }

  private renderProps(space: PresenceSpace): void {
    this.propsEl.innerHTML = '';
    // Frontmost (higher depth) last in DOM so it wins overlapping hit tests.
    const objects = [
      ...(space.interactiveObjects ?? []),
      ...this.replenishedPineconeObjects,
    ].sort(
      (a, b) => depthFromY(a.y) - depthFromY(b.y),
    );
    const worldRect = this.worldLayerEl.getBoundingClientRect();
    const worldPx = Math.max(1, worldRect.width);

    for (const obj of objects) {
      if (obj.hitRadius <= 0) continue;
      // Carried / in-flight / stored / contained objects have no ground hotspot.
      // Exception: carried bucket keeps a tap target so kids can put it down.
      if (this.carriedSlot.isCarryingId(obj.id) && !isBucketObject(obj.id)) continue;
      if (isKickablePinecone(obj.id)) {
        const pr = this.pineconeRuntime.get(obj.id);
        if (pr && (pr.state === 'thrown' || pr.state === 'bucket' || pr.state === 'carried' || pr.state === 'burned')) {
          continue;
        }
      }
      if (isKickablePinecone(obj.id) && this.carriedSlot.isCarryingBucket()) continue;
      if (isBucketObject(obj.id)) {
        const br = this.bucketRuntime;
        // Stored-in-shed bucket has no ground hotspot; carried keeps a tap target
        // so kids can put it down by tapping the bucket itself.
        if (!br || br.state === 'storedInShed') continue;
      }
      const aspect =
        space.worldAspectRatio && space.worldAspectRatio > 0
          ? space.worldAspectRatio
          : 16 / 9;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `wp-prop wp-prop-${obj.shape} wp-prop-hotspot-btn`;
      button.dataset.objectId = obj.id;
      if (obj.sceneryId) button.dataset.sceneryId = obj.sceneryId;
      // Prefer runtime pose for movable toys.
      let hx = obj.x;
      let hy = obj.y;
      if (isBucketObject(obj.id) && this.bucketRuntime) {
        hx = this.bucketRuntime.x + this.bucketRuntime.hitOffsetX;
        hy = this.bucketRuntime.y + this.bucketRuntime.hitOffsetY;
      } else if (isKickablePinecone(obj.id)) {
        const pr = this.pineconeRuntime.get(obj.id);
        if (pr) {
          hx = pr.x + pr.hitOffsetX;
          hy = pr.y + pr.hitOffsetY;
        }
      } else if (isGardenObject(obj.id)) {
        const layer = space.scenery.layers.find(
          (l) => l.id === (obj.sceneryId ?? obj.id),
        );
        const layout = gardenHotspotLayout(obj, layer ?? null, aspect, worldPx);
        hx = layout.x;
        hy = layout.y;
      }
      button.style.left = `${hx * 100}%`;
      button.style.top = `${hy * 100}%`;
      button.style.zIndex = String(depthFromY(hy));
      button.setAttribute('aria-label', obj.title);

      // Tight invisible hotspots from authored hitRadius — not asset bounds.
      // Pinecones / bucket get generous touch targets for young children.
      let hitW = obj.hitRadius * 2;
      let hitH =
        space.worldAspectRatio && space.worldAspectRatio > 0
          ? obj.hitRadius * 2 * space.worldAspectRatio
          : hitW;
      if (isKickablePinecone(obj.id)) {
        const minFrac = PINECONE_HIT_MIN_CSS_PX / worldPx;
        hitW = Math.max(hitW, minFrac);
        hitH = Math.max(hitH, minFrac);
      }
      if (isBucketObject(obj.id) || isShedObject(obj.id)) {
        const minFrac = BUCKET_HIT_MIN_CSS_PX / worldPx;
        hitW = Math.max(hitW, minFrac);
        hitH = Math.max(hitH, minFrac);
      }
      if (isGardenObject(obj.id)) {
        const layer = space.scenery.layers.find(
          (l) => l.id === (obj.sceneryId ?? obj.id),
        );
        const layout = gardenHotspotLayout(obj, layer ?? null, aspect, worldPx);
        hitW = layout.hitW;
        hitH = layout.hitH;
      }
      button.style.width = `${hitW * 100}%`;
      button.style.height = `${hitH * 100}%`;

      if (obj.shape === 'mailbox') {
        button.innerHTML = `
          <span class="wp-prop-mailbox-post" aria-hidden="true"></span>
          <span class="wp-prop-mailbox-box" aria-hidden="true"></span>
          <span class="wp-prop-mailbox-flag" aria-hidden="true"></span>
        `;
      } else {
        button.innerHTML = `<span class="wp-prop-hotspot" aria-hidden="true"></span>`;
      }

      button.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        if (isKickablePinecone(obj.id)) {
          this.beginPineconeLongPress(event, obj);
          return;
        }
      });
      button.addEventListener('pointermove', (event) => {
        this.updatePineconeLongPress(event);
      });
      button.addEventListener('pointerup', () => {
        this.endPineconeLongPress();
      });
      button.addEventListener('pointercancel', () => {
        this.clearPineconeLongPress();
      });
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.pineconeLongPressConsumed) {
          this.pineconeLongPressConsumed = false;
          return;
        }
        if (isGardenObject(obj.id)) {
          this.logGardenDebug('garden pointerdown (hotspot click)');
        }
        const target = this.resolvePineconeClickTarget(event, obj);
        this.lastInputKind = 'object';
        this.lastHitObjectId = target.id;
        if (this.playerInteraction.isPerforming()) {
          this.postKickCommand = { kind: 'object', obj: target };
          this.updateDebugInteractionLabel();
          return;
        }
        this.approachObject(target);
        this.updateDebugInteractionLabel();
      });

      this.propsEl.append(button);
    }
    this.syncBucketVisibility();
    this.setupPineconeTreeHit(space);
  }

  /**
   * Build the ambient rabbit for yards that have one. The rabbit is scenery
   * with a tap target — it is not an interactive object, so it stays out of
   * hotspot resolution, the World Editor, and the carry system.
   */
  private setupRabbit(space: PresenceSpace): void {
    this.teardownRabbit();
    const config = rabbitConfigForYard(space.id);
    if (!config) return;

    if (isGardenFoodYard(space.id)) {
      reconcileGardenFoodIdleState(space.id);
    }

    this.rabbitConfig = config;
    this.rabbitState = rabbitStateForYard(space.id);

    const visual = document.createElement('div');
    visual.className = 'wp-creature wp-creature-rabbit';
    visual.setAttribute('aria-hidden', 'true');
    const shadow = document.createElement('span');
    shadow.className = 'wp-creature-shadow';
    const lift = document.createElement('span');
    lift.className = 'wp-creature-lift';
    const body = document.createElement('img');
    body.className = 'wp-creature-body';
    body.alt = '';
    body.draggable = false;
    body.decoding = 'async';
    lift.append(body);
    visual.append(shadow, lift);

    const hit = document.createElement('button');
    hit.type = 'button';
    hit.className = 'wp-creature wp-creature-hit';
    hit.setAttribute('aria-label', 'Rabbit');
    hit.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.onRabbitTapped();
    });

    this.creaturesEl.append(visual, hit);
    this.rabbitVisualEl = visual;
    this.rabbitBodyEl = body;
    this.rabbitHitEl = hit;
    this.rabbitPoseSrc = '';

    this.syncRabbitVisual();
    this.setupGardenFoodVisual(space);
    this.startRabbitLoop();
  }

  private setupGardenFoodVisual(space: PresenceSpace): void {
    this.teardownGardenFoodVisual();
    if (!isGardenFoodYard(space.id)) return;
    const kind = gardenFoodStateForYard(space.id)?.kind;
    if (!kind) return;

    const el = document.createElement('span');
    el.className = 'wp-garden-food';
    el.hidden = true;
    el.setAttribute('aria-hidden', 'true');
    const img = document.createElement('img');
    img.src = GARDEN_FOOD_SRC[kind];
    img.alt = '';
    img.draggable = false;
    img.decoding = 'async';
    el.append(img);
    this.creaturesEl.append(el);
    this.gardenFoodEl = el;
    this.gardenFoodImgEl = img;
    this.syncGardenFoodVisual(space);
  }

  private teardownGardenFoodVisual(): void {
    this.stopGardenFoodThrowLoop();
    this.gardenFoodThrow = null;
    this.gardenFoodEl?.remove();
    this.gardenFoodEl = null;
    this.gardenFoodImgEl = null;
  }

  /**
   * Drop the rabbit's DOM and loop. Its behavior state deliberately survives in
   * the per-yard registry so timers and cooldowns continue where they left off.
   */
  private teardownRabbit(): void {
    this.stopRabbitLoop();
    this.rabbitVisualEl?.remove();
    this.rabbitHitEl?.remove();
    this.teardownGardenFoodVisual();
    this.rabbitVisualEl = null;
    this.rabbitBodyEl = null;
    this.rabbitHitEl = null;
    this.rabbitConfig = null;
    this.rabbitState = null;
    this.rabbitPoseSrc = '';
  }

  /**
   * The rabbit needs its own frame loop: PresenceController parks its loop once
   * the player stands still and the camera settles.
   */
  private startRabbitLoop(): void {
    if (this.rabbitRaf != null) return;
    this.rabbitLastTs = null;
    const tick = (ts: number) => {
      if (!this.rabbitState) {
        this.rabbitRaf = null;
        return;
      }
      this.rabbitRaf = window.requestAnimationFrame(tick);
      const last = this.rabbitLastTs;
      this.rabbitLastTs = ts;
      if (last == null) return;
      this.stepRabbitFrame(ts - last);
    };
    this.rabbitRaf = window.requestAnimationFrame(tick);
  }

  private stopRabbitLoop(): void {
    if (this.rabbitRaf != null) {
      cancelAnimationFrame(this.rabbitRaf);
      this.rabbitRaf = null;
    }
    this.rabbitLastTs = null;
  }

  private stepRabbitFrame(dtMs: number): void {
    const space = this.currentSpace;
    const state = this.rabbitState;
    const config = this.rabbitConfig;
    if (!space || !state || !config) return;

    tickGardenFoodCooldown(space.id, dtMs);

    stepRabbit(state, dtMs, {
      config,
      player: this.presence?.getCharacterPosition() ?? null,
      worldAspect: space.worldAspectRatio ?? 1,
      obstacles: space.walkObstacles,
      gardenFood: this.gardenFoodTargetForRabbit(space),
    });
    this.syncRabbitVisual();
    this.syncGardenFoodVisual(space);
  }

  private gardenFoodTargetForRabbit(
    space: PresenceSpace,
  ): GardenFoodTarget | null {
    const foodState = gardenFoodStateForYard(space.id);
    if (!foodState || !isGardenFoodActiveForRabbit(space.id)) return null;
    return {
      x: foodState.x,
      y: foodState.y,
      available: isGardenFoodAvailableToRabbit(space.id),
      onTargeted: () => markGardenFoodTargeted(space.id),
      onBeginEat: () => beginGardenEat(space.id),
      onEatComplete: () => {
        finishGardenEat(space.id);
        this.syncGardenFoodVisual(space);
      },
    };
  }

  private syncGardenFoodVisual(space: PresenceSpace): void {
    const el = this.gardenFoodEl;
    const img = this.gardenFoodImgEl;
    const foodState = gardenFoodStateForYard(space.id);
    if (!el || !img || !foodState) return;

    const showOnGround =
      foodState.phase === 'landed' ||
      foodState.phase === 'targeted' ||
      foodState.phase === 'eating';
    if (!showOnGround) {
      el.hidden = true;
      return;
    }

    const kind = foodState.kind;
    const meta = GARDEN_FOOD_IMAGE[kind];
    const worldAspect = space.worldAspectRatio ?? 1;
    const cal = yardScaleFor(space.id);
    const heightFrac = feetToHeightFrac(GARDEN_FOOD_HEIGHT_FEET, foodState.y, cal);
    const widthFrac =
      heightFrac / ((meta.imageHeight / meta.imageWidth) * worldAspect);

    el.hidden = false;
    el.style.left = `${foodState.x * 100}%`;
    el.style.top = `${foodState.y * 100}%`;
    el.style.width = `${widthFrac * 100}%`;
    el.style.height = `${heightFrac * 100}%`;
    el.style.zIndex = String(depthFromY(foodState.y));
    el.style.setProperty('--wp-prop-rot', `${foodState.rotation}deg`);
    el.style.removeProperty('--wp-throw-lift');
    el.classList.remove('is-thrown');
  }

  /** Position, size, pose, facing, and depth for the current rabbit state. */
  private syncRabbitVisual(): void {
    const space = this.currentSpace;
    const state = this.rabbitState;
    const config = this.rabbitConfig;
    const visual = this.rabbitVisualEl;
    const body = this.rabbitBodyEl;
    const hit = this.rabbitHitEl;
    if (!space || !state || !config || !visual || !body || !hit) return;

    if (!isRabbitVisible(state)) {
      visual.hidden = true;
      hit.hidden = true;
      return;
    }

    const worldAspect = space.worldAspectRatio ?? 1;
    // Sized in feet against the yard's fence calibration, so the rabbit reads
    // as a real backyard rabbit next to the child and shares her perspective.
    const heightFrac = feetToHeightFrac(
      RABBIT_CANVAS_FEET,
      state.y,
      yardScaleFor(space.id),
    );
    const widthFrac =
      heightFrac /
      ((RABBIT_POSE_IMAGE.imageHeight / RABBIT_POSE_IMAGE.imageWidth) *
        worldAspect);

    visual.hidden = false;
    visual.style.left = `${state.x * 100}%`;
    visual.style.top = `${state.y * 100}%`;
    visual.style.width = `${widthFrac * 100}%`;
    visual.style.height = `${heightFrac * 100}%`;
    visual.style.zIndex = String(depthFromY(state.y));
    visual.style.setProperty('--wp-creature-lift', String(state.liftT));
    visual.style.setProperty(
      '--wp-creature-flip-x',
      state.facing === 'left' ? '-1' : '1',
    );
    visual.dataset.pose = state.pose;
    visual.dataset.phase = state.phase;

    const poseSrc = RABBIT_POSE_SRC[state.pose];
    if (poseSrc !== this.rabbitPoseSrc) {
      body.src = poseSrc;
      this.rabbitPoseSrc = poseSrc;
    }

    // Tap target: at least RABBIT_HIT_MIN_CSS_PX so small fingers can find it,
    // centered on the body rather than the feet.
    const worldPx = Math.max(1, this.worldLayerEl.getBoundingClientRect().width);
    const minFrac = RABBIT_HIT_MIN_CSS_PX / worldPx;
    const hitW = Math.max(widthFrac, minFrac);
    const hitH = Math.max(heightFrac, minFrac * worldAspect);
    hit.hidden = false;
    hit.style.left = `${state.x * 100}%`;
    hit.style.top = `${(state.y - heightFrac * 0.5) * 100}%`;
    hit.style.width = `${hitW * 100}%`;
    hit.style.height = `${hitH * 100}%`;
    hit.style.zIndex = String(depthFromY(state.y));
  }

  /**
   * Tap / click the rabbit. Close up it spooks; from a distance it just looks
   * up while the child walks over. Never picks it up, never opens a card.
   */
  private onRabbitTapped(): void {
    const space = this.currentSpace;
    const state = this.rabbitState;
    if (!space || !state || !isRabbitVisible(state)) return;
    if (this.interactionLocked || this.navigator.isLocked()) return;
    if (this.shedSequence !== 'idle') return;
    if (!this.presence) return;

    const player = this.presence.getCharacterPosition();
    const worldAspect = space.worldAspectRatio ?? 1;
    const distance = rabbitDistance(state, player, worldAspect);

    if (distance <= RABBIT_NOTICE_RADIUS) {
      pokeRabbit(state);
    } else {
      noticeRabbit(state);
      this.walkPlayerTowardRabbit(state, player, worldAspect);
    }
    this.syncRabbitVisual();
  }

  /** Walk to a spot just short of the rabbit so the child never stands on it. */
  private walkPlayerTowardRabbit(
    state: RabbitState,
    player: SpawnPoint,
    worldAspect: number,
  ): void {
    if (!this.presence || !this.currentSpace) return;
    const dx = state.x - player.x;
    const dy = (state.y - player.y) / worldAspect;
    const distance = Math.hypot(dx, dy);
    if (distance <= 0.0001) return;

    const stopShort = Math.max(0, distance - RABBIT_COMFORT_RADIUS);
    const targetX = player.x + (dx / distance) * stopShort;
    const targetY = player.y + ((dy / distance) * stopShort) * worldAspect;

    this.presence.walkTo(targetX, targetY);
    this.playerInteraction.noteMoving(true);
    this.updateDebugWalkDestination(this.currentSpace);
  }

  /**
   * Build the nest and its bird for yards that have one. Like the rabbit, both
   * are scenery with a tap target rather than interactive objects, so they stay
   * out of hotspot resolution, the World Editor, and the carry system.
   */
  private setupBird(space: PresenceSpace): void {
    this.teardownBird();
    const authored = birdConfigForYard(space.id);
    if (!authored) return;
    // The nest itself is a scenery prop placed by the yard's scatter pack, so
    // the editor owns its position and size; the bird just guards it. Resolve
    // before creating state, or a first-time bird would spawn beside the
    // authored nest and have to walk to the real one.
    const config = resolveBirdConfig(authored, space);
    const state = birdStateForYard(space.id, config);
    if (!state) return;

    this.birdConfig = config;
    this.birdState = state;

    const visual = document.createElement('div');
    visual.className = 'wp-creature wp-creature-bird';
    visual.setAttribute('aria-hidden', 'true');
    const shadow = document.createElement('span');
    shadow.className = 'wp-creature-shadow';
    const lift = document.createElement('span');
    lift.className = 'wp-creature-lift';
    const body = document.createElement('img');
    body.className = 'wp-creature-body';
    body.alt = '';
    body.draggable = false;
    body.decoding = 'async';
    lift.append(body);
    visual.append(shadow, lift);

    const hit = document.createElement('button');
    hit.type = 'button';
    hit.className = 'wp-creature wp-creature-hit';
    hit.setAttribute('aria-label', 'Bird');
    hit.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.onBirdTapped();
    });

    this.creaturesEl.append(visual, hit);
    this.birdVisualEl = visual;
    this.birdBodyEl = body;
    this.birdHitEl = hit;
    this.birdPoseSrc = '';

    this.syncBirdVisual();
    this.startBirdLoop();
  }

  /** Drop the bird's DOM and loop; its behavior state survives per yard. */
  private teardownBird(): void {
    this.stopBirdLoop();
    this.birdVisualEl?.remove();
    this.birdHitEl?.remove();
    this.birdVisualEl = null;
    this.birdBodyEl = null;
    this.birdHitEl = null;
    this.birdConfig = null;
    this.birdState = null;
    this.birdPoseSrc = '';
  }

  private startBirdLoop(): void {
    if (this.birdRaf != null) return;
    this.birdLastTs = null;
    const tick = (ts: number) => {
      if (!this.birdState) {
        this.birdRaf = null;
        return;
      }
      this.birdRaf = window.requestAnimationFrame(tick);
      const last = this.birdLastTs;
      this.birdLastTs = ts;
      if (last == null) return;
      this.stepBirdFrame(ts - last);
    };
    this.birdRaf = window.requestAnimationFrame(tick);
  }

  private stopBirdLoop(): void {
    if (this.birdRaf != null) {
      cancelAnimationFrame(this.birdRaf);
      this.birdRaf = null;
    }
    this.birdLastTs = null;
  }

  private stepBirdFrame(dtMs: number): void {
    const space = this.currentSpace;
    const state = this.birdState;
    const authored = this.birdConfig;
    if (!space || !state || !authored) return;

    // Re-read the nest every frame so dragging it in the World Editor moves
    // the bird's whole world — guard spot, watch ring, chase, way home.
    const config = resolveBirdConfig(authored, space);
    this.birdConfig = config;

    stepBird(state, dtMs, {
      config,
      player: this.presence?.getCharacterPosition() ?? null,
      worldAspect: space.worldAspectRatio ?? 1,
      obstacles: space.walkObstacles,
    });
    this.syncBirdVisual();
  }

  /** Position, size, pose, facing, and depth for the current bird state. */
  private syncBirdVisual(): void {
    const space = this.currentSpace;
    const state = this.birdState;
    const visual = this.birdVisualEl;
    const body = this.birdBodyEl;
    const hit = this.birdHitEl;
    if (!space || !state || !visual || !body || !hit) return;

    const worldAspect = space.worldAspectRatio ?? 1;
    const heightFrac = feetToHeightFrac(
      BIRD_CANVAS_FEET,
      state.y,
      yardScaleFor(space.id),
    );
    const widthFrac =
      heightFrac /
      ((BIRD_POSE_IMAGE.imageHeight / BIRD_POSE_IMAGE.imageWidth) *
        worldAspect);

    visual.style.left = `${state.x * 100}%`;
    visual.style.top = `${state.y * 100}%`;
    visual.style.width = `${widthFrac * 100}%`;
    visual.style.height = `${heightFrac * 100}%`;
    visual.style.zIndex = String(depthFromY(state.y));
    visual.style.setProperty('--wp-creature-lift', String(state.liftT));
    visual.style.setProperty(
      '--wp-creature-flip-x',
      state.facing === 'left' ? '-1' : '1',
    );
    visual.dataset.pose = state.pose;
    visual.dataset.phase = state.phase;

    const poseSrc = BIRD_POSE_SRC[state.pose];
    if (poseSrc !== this.birdPoseSrc) {
      body.src = poseSrc;
      this.birdPoseSrc = poseSrc;
    }

    const worldPx = Math.max(1, this.worldLayerEl.getBoundingClientRect().width);
    const minFrac = BIRD_HIT_MIN_CSS_PX / worldPx;
    const hitW = Math.max(widthFrac, minFrac);
    const hitH = Math.max(heightFrac, minFrac * worldAspect);
    hit.style.left = `${state.x * 100}%`;
    hit.style.top = `${(state.y - heightFrac * 0.5) * 100}%`;
    hit.style.width = `${hitW * 100}%`;
    hit.style.height = `${hitH * 100}%`;
    hit.style.zIndex = String(depthFromY(state.y));
  }

  /**
   * Tap / click the bird. From across the yard it is a hello: the bird bristles
   * and the child walks over, at which point the nest's own protect ring takes
   * the joke from there. Never opens a card, never picks the bird up.
   */
  private onBirdTapped(): void {
    const space = this.currentSpace;
    const state = this.birdState;
    const config = this.birdConfig;
    if (!space || !state || !config) return;
    if (this.interactionLocked || this.navigator.isLocked()) return;
    if (this.shedSequence !== 'idle') return;
    if (!this.presence) return;

    const player = this.presence.getCharacterPosition();
    const worldAspect = space.worldAspectRatio ?? 1;

    pokeBird(state, { config, player, worldAspect });

    // Walk over only if the child is still outside the watch ring, and stop at
    // its edge so the approach itself triggers the reaction.
    const nestDistance = creatureDistance(config.nest, player, worldAspect);
    if (nestDistance > BIRD_RADIUS.watch) {
      this.walkPlayerTowardPoint(config.nest, player, worldAspect, BIRD_RADIUS.watch);
    }
    this.syncBirdVisual();
  }

  /** Walk toward `point`, stopping `stopShortOf` world-width units away. */
  private walkPlayerTowardPoint(
    point: SpawnPoint,
    player: SpawnPoint,
    worldAspect: number,
    stopShortOf: number,
  ): void {
    if (!this.presence || !this.currentSpace) return;
    const dx = point.x - player.x;
    const dy = (point.y - player.y) / worldAspect;
    const distance = Math.hypot(dx, dy);
    if (distance <= 0.0001) return;

    const stopShort = Math.max(0, distance - stopShortOf);
    const targetX = player.x + (dx / distance) * stopShort;
    const targetY = player.y + (dy / distance) * stopShort * worldAspect;

    this.presence.walkTo(targetX, targetY);
    this.playerInteraction.noteMoving(true);
    this.updateDebugWalkDestination(this.currentSpace);
  }

  /** Build two ambient frogs for yards that have them. */
  private setupFrogs(space: PresenceSpace): void {
    this.teardownFrogs();
    const config = frogConfigForYard(space.id);
    if (!config) return;

    this.frogConfig = config;
    this.frogStates = frogStatesForYard(space.id, config.frogs);
    const exclusionZones = frogExclusionZonesForYard(space.id);
    const sleepConfig = wildlifeSleepConfigForYard(space.id);
    if (exclusionZones.length > 0) {
      for (let i = 0; i < this.frogStates.length; i += 1) {
        const frog = this.frogStates[i]!;
        if (!pointInFrogExclusionZone(frog, exclusionZones)) continue;
        const fallback =
          sleepConfig != null
            ? frogSleepTarget(sleepConfig, i)
            : config.frogs[i]?.home ?? { x: frog.x, y: frog.y };
        frog.x = fallback.x;
        frog.y = fallback.y;
      }
    }

    for (const instance of config.frogs) {
      const visual = document.createElement('div');
      visual.className = 'wp-creature wp-creature-frog';
      visual.dataset.frogId = instance.id;
      visual.setAttribute('aria-hidden', 'true');
      const shadow = document.createElement('span');
      shadow.className = 'wp-creature-shadow';
      const lift = document.createElement('span');
      lift.className = 'wp-creature-lift';
      const body = document.createElement('img');
      body.className = 'wp-creature-body';
      body.alt = '';
      body.draggable = false;
      body.decoding = 'async';
      lift.append(body);
      visual.append(shadow, lift);

      const hit = document.createElement('button');
      hit.type = 'button';
      hit.className = 'wp-creature wp-creature-hit';
      hit.setAttribute('aria-label', 'Frog');
      hit.dataset.frogId = instance.id;
      hit.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.onFrogTapped(instance.id);
      });

      this.creaturesEl.append(visual, hit);
      this.frogVisuals.set(instance.id, { visual, body, hit, poseSrc: '' });
    }

    this.syncFrogVisuals();
    this.startFrogLoop();
  }

  private teardownFrogs(): void {
    const space = this.currentSpace;
    if (space && this.frogStates.length > 0) {
      cancelFrogHeadSequencesForYard(
        space.id,
        this.frogStates,
        this.frogStepContext(space),
      );
    }
    this.stopFrogLoop();
    for (const { visual, hit } of this.frogVisuals.values()) {
      visual.remove();
      hit.remove();
    }
    this.frogVisuals.clear();
    this.frogConfig = null;
    this.frogStates = [];
  }

  private startFrogLoop(): void {
    if (this.frogRaf != null) return;
    this.frogLastTs = null;
    const tick = (ts: number) => {
      if (this.frogStates.length === 0) {
        this.frogRaf = null;
        return;
      }
      this.frogRaf = window.requestAnimationFrame(tick);
      const last = this.frogLastTs;
      this.frogLastTs = ts;
      if (last == null) return;
      this.stepFrogFrame(ts - last);
    };
    this.frogRaf = window.requestAnimationFrame(tick);
  }

  private stopFrogLoop(): void {
    if (this.frogRaf != null) {
      cancelAnimationFrame(this.frogRaf);
      this.frogRaf = null;
    }
    this.frogLastTs = null;
  }

  private frogStepContext(space: PresenceSpace): FrogStepContext {
    const presence = this.presence;
    const state = presence?.getState();
    return {
      config: this.frogConfig!,
      player: presence?.getCharacterPosition() ?? null,
      playerFacing: state?.facing ?? null,
      worldAspect: space.worldAspectRatio ?? 1,
      obstacles: space.walkObstacles,
      exclusionZones: frogExclusionZonesForYard(space.id),
      ...frogHeadPerchCallbacks(space.id),
      headAnchor: () => this.frogHeadAnchorForSpace(space),
    };
  }

  private frogHeadAnchorForSpace(space: PresenceSpace): SpawnPoint | null {
    if (!this.presence) return null;
    const pose = this.presence.getState();
    const feet = this.presence.getCharacterPosition();
    const worldRect = this.worldLayerEl.getBoundingClientRect();
    const charRect = this.characterEl.getBoundingClientRect();
    const fromDom = characterHeadPerchAnchorFromDom({
      feet,
      worldRect,
      characterRect: charRect,
    });
    if (fromDom) return fromDom;
    return characterHeadPerchAnchor({
      feet,
      depthScale: pose.characterScale,
      character: getActivePlayableCharacter(),
      yardId: space.id,
      worldAspect: space.worldAspectRatio ?? 1,
    });
  }

  private cancelFrogHeadJumpSequences(): void {
    const space = this.currentSpace;
    if (!space || this.frogStates.length === 0) return;
    cancelFrogHeadSequencesForYard(
      space.id,
      this.frogStates,
      this.frogStepContext(space),
    );
    this.syncFrogVisuals();
  }

  private stepFrogFrame(dtMs: number): void {
    const space = this.currentSpace;
    const config = this.frogConfig;
    if (!space || !config || this.frogStates.length === 0) return;

    const ctx = this.frogStepContext(space);
    for (const state of this.frogStates) {
      stepFrog(state, dtMs, ctx);
    }
    this.syncFrogVisuals();
  }

  private syncFrogVisuals(): void {
    const space = this.currentSpace;
    const config = this.frogConfig;
    if (!space || !config) return;

    const worldAspect = space.worldAspectRatio ?? 1;
    const worldPx = Math.max(1, this.worldLayerEl.getBoundingClientRect().width);
    const minFrac = FROG_HIT_MIN_CSS_PX / worldPx;

    for (const state of this.frogStates) {
      const dom = this.frogVisuals.get(state.id);
      if (!dom) continue;
      const { visual, body, hit } = dom;

      const heightFrac = feetToHeightFrac(
        FROG_CANVAS_FEET,
        state.y,
        yardScaleFor(space.id),
      );
      const widthFrac =
        heightFrac /
        ((FROG_POSE_IMAGE.imageHeight / FROG_POSE_IMAGE.imageWidth) *
          worldAspect);

      visual.style.left = `${state.x * 100}%`;
      visual.style.top = `${state.y * 100}%`;
      visual.style.width = `${widthFrac * 100}%`;
      visual.style.height = `${heightFrac * 100}%`;
      visual.style.setProperty('--wp-creature-lift', String(state.liftT));
      visual.style.setProperty(
        '--wp-creature-flip-x',
        state.facing === 'left' ? '-1' : '1',
      );
      visual.style.setProperty(
        '--wp-creature-shadow-opacity',
        String(1 - state.liftT * 0.55),
      );
      visual.style.setProperty(
        '--wp-creature-shadow-scale',
        String(1 - state.liftT * 0.38),
      );
      visual.dataset.pose = state.pose;
      visual.dataset.phase = state.phase;
      visual.classList.toggle(
        'is-perched',
        state.phase === 'perched' || state.phase === 'headJumpTo',
      );

      const charY = this.presence?.getCharacterPosition().y ?? state.y;
      const baseDepth = depthFromY(
        state.phase === 'perched' || state.phase === 'headJumpTo'
          ? charY
          : state.y,
      );
      visual.style.zIndex = String(
        state.phase === 'perched' || state.phase === 'headJumpTo'
          ? baseDepth + 3
          : baseDepth,
      );
      hit.style.zIndex = String(
        isFrogHeadSequencePhase(state.phase) ? -1 : baseDepth,
      );
      hit.hidden = isFrogHeadSequencePhase(state.phase);

      const poseSrc = FROG_POSE_SRC[state.pose];
      if (poseSrc !== dom.poseSrc) {
        body.src = poseSrc;
        dom.poseSrc = poseSrc;
      }

      const hitW = Math.max(widthFrac, minFrac);
      const hitH = Math.max(heightFrac, minFrac * worldAspect);
      hit.style.left = `${state.x * 100}%`;
      hit.style.top = `${(state.y - heightFrac * 0.5) * 100}%`;
      hit.style.width = `${hitW * 100}%`;
      hit.style.height = `${hitH * 100}%`;
    }
  }

  private onFrogTapped(frogId: string): void {
    const space = this.currentSpace;
    const config = this.frogConfig;
    if (!space || !config) return;
    if (this.interactionLocked || this.navigator.isLocked()) return;
    if (this.shedSequence !== 'idle') return;

    const state = this.frogStates.find((f) => f.id === frogId);
    if (!state) return;

    if (!beginHeadJump(state, this.frogStepContext(space))) return;
    this.syncFrogVisuals();
  }

  private onDayNightTapped(): void {
    const space = this.currentSpace;
    if (!space || !isTimeOfDayYard(space.id)) return;
    toggleTimeOfDayForYard(space.id);
    this.syncTimeOfDayVisual(space);
    this.syncDayNightButton(space);
  }

  private syncDayNightButton(space: PresenceSpace | null): void {
    const show =
      space != null &&
      this.root.classList.contains('wp-is-presence') &&
      isTimeOfDayYard(space.id);
    this.dayNightBtnEl.hidden = !show;
    if (!show) return;
    const isNight = timeOfDayForYard(space.id) === 'night';
    this.dayNightBtnEl.setAttribute(
      'aria-label',
      isNight ? 'Switch to day' : 'Switch to night',
    );
  }

  private syncTimeOfDayVisual(space: PresenceSpace): void {
    if (!isTimeOfDayYard(space.id)) {
      this.root.classList.remove('wp-is-night');
      this.ambienceEl.hidden = true;
      this.moonEl.hidden = true;
      this.teardownFireflies(false);
      return;
    }
    const isNight = timeOfDayForYard(space.id) === 'night';
    this.ambienceEl.hidden = false;
    this.root.classList.toggle('wp-is-night', isNight);
    this.nightVeilEl.style.transition = prefersReducedMotion()
      ? 'none'
      : `opacity ${TIME_OF_DAY_TRANSITION_MS}ms ease`;
    this.moonEl.hidden = !isNight;
    this.moonEl.style.transition = prefersReducedMotion()
      ? 'none'
      : `opacity ${TIME_OF_DAY_TRANSITION_MS}ms ease`;
    if (isNight) {
      this.setupFireflies(space);
    } else {
      this.teardownFireflies(true);
      this.firefliesEl.style.transition = prefersReducedMotion()
        ? 'none'
        : `opacity ${TIME_OF_DAY_TRANSITION_MS}ms ease`;
      this.firefliesEl.style.opacity = '0';
      window.setTimeout(() => {
        if (timeOfDayForYard(space.id) === 'day') {
          this.firefliesEl.innerHTML = '';
          this.firefliesEl.style.opacity = '';
        }
      }, prefersReducedMotion() ? 0 : TIME_OF_DAY_TRANSITION_MS);
    }
    this.syncWildlifeTimeOfDay(space);
  }

  /** Rabbit, bird, and frogs tuck in at night and wake at dawn. */
  private syncWildlifeTimeOfDay(space: PresenceSpace): void {
    const sleepConfig = wildlifeSleepConfigForYard(space.id);
    if (!sleepConfig) return;
    const isNight = timeOfDayForYard(space.id) === 'night';
    const worldAspect = space.worldAspectRatio ?? 16 / 9;

    if (this.rabbitState && this.rabbitConfig) {
      if (isNight) {
        requestRabbitSleep(
          this.rabbitState,
          sleepConfig.rabbit,
          worldAspect,
        );
      } else {
        requestRabbitWake(this.rabbitState);
      }
      this.syncRabbitVisual();
    }

    if (this.birdState && this.birdConfig) {
      if (isNight) {
        const nest = this.birdConfig.nest;
        requestBirdSleep(
          this.birdState,
          {
            x: nest.x + sleepConfig.birdNestOffset.x,
            y: nest.y + sleepConfig.birdNestOffset.y,
          },
          worldAspect,
        );
      } else {
        requestBirdWake(this.birdState, this.birdConfig);
      }
      this.syncBirdVisual();
    }

    if (this.frogStates.length > 0 && this.frogConfig) {
      const ctx = this.frogStepContext(space);
      for (let i = 0; i < this.frogStates.length; i += 1) {
        const state = this.frogStates[i]!;
        if (isNight) {
          requestFrogSleep(
            state,
            frogSleepTarget(sleepConfig, i),
            worldAspect,
            ctx,
          );
        } else {
          requestFrogWake(state);
        }
      }
      this.syncFrogVisuals();
    }
  }

  private setupFireflies(space: PresenceSpace): void {
    const config = fireflyConfigForYard(space.id);
    if (!config) {
      this.teardownFireflies(false);
      return;
    }

    clearFireflyGroupForYard(space.id);
    this.fireflyGroupState = fireflyGroupForYard(space.id, config);
    this.firefliesEl.style.opacity = '1';

    for (const fly of this.fireflyGroupState.instances) {
      if (this.fireflyVisuals.has(fly.id)) continue;
      const el = document.createElement('span');
      el.className = 'wp-fly-sprite';
      el.dataset.flyId = fly.id;
      el.setAttribute('aria-hidden', 'true');
      this.firefliesEl.append(el);
      this.fireflyVisuals.set(fly.id, el);
    }

    this.syncFireflyVisuals(space);
    this.startFireflyLoop();
  }

  private teardownFireflies(clearRuntimeState: boolean): void {
    this.stopFireflyLoop();
    for (const el of this.fireflyVisuals.values()) {
      el.remove();
    }
    this.fireflyVisuals.clear();
    this.firefliesEl.innerHTML = '';
    this.fireflyGroupState = null;
    const yardId = this.currentSpace?.id;
    if (clearRuntimeState && yardId) {
      clearFireflyGroupForYard(yardId);
    }
  }

  private startFireflyLoop(): void {
    if (this.fireflyRaf != null) return;
    this.fireflyLastTs = null;
    const tick = (ts: number) => {
      if (!this.fireflyGroupState) {
        this.fireflyRaf = null;
        return;
      }
      this.fireflyRaf = window.requestAnimationFrame(tick);
      const last = this.fireflyLastTs;
      this.fireflyLastTs = ts;
      if (last == null) return;
      this.stepFireflyFrame(ts - last, ts);
    };
    this.fireflyRaf = window.requestAnimationFrame(tick);
  }

  private stopFireflyLoop(): void {
    if (this.fireflyRaf != null) {
      cancelAnimationFrame(this.fireflyRaf);
      this.fireflyRaf = null;
    }
    this.fireflyLastTs = null;
  }

  private stepFireflyFrame(dtMs: number, nowMs: number): void {
    const space = this.currentSpace;
    const state = this.fireflyGroupState;
    if (!space || !state) return;

    const player = this.presence?.getCharacterPosition() ?? null;
    const worldAspect = space.worldAspectRatio ?? 1;
    stepFireflyGroup(state, dtMs, player, worldAspect, nowMs);
    this.syncFireflyVisuals(space, nowMs);
  }

  private syncFireflyVisuals(_space: PresenceSpace, nowMs = performance.now()): void {
    const state = this.fireflyGroupState;
    if (!state) return;

    const worldRect = this.worldLayerEl.getBoundingClientRect();
    const worldW = Math.max(1, worldRect.width);

    for (const fly of state.instances) {
      const el = this.fireflyVisuals.get(fly.id);
      if (!el) continue;

      const sizeScale = fireflySizeScale(fly);
      const glowPx = Math.min(
        FIREFLY_GLOW_MAX_PX,
        Math.max(
          FIREFLY_GLOW_MIN_PX,
          worldW * FIREFLY_GLOW_BASE_FRAC * sizeScale,
        ),
      );

      el.style.left = `${fly.x * 100}%`;
      el.style.top = `${fly.y * 100}%`;
      el.style.width = `${glowPx}px`;
      el.style.height = `${glowPx}px`;
      el.style.zIndex = String(depthFromY(fly.y));
      const glowScale = fireflyGlowScale(state, fly, nowMs);
      const glowStrength = fireflyOpacity(state, fly, nowMs);
      el.style.setProperty('--wp-fly-glow', glowStrength.toFixed(3));
      el.style.setProperty('--wp-fly-breathe', glowScale.toFixed(3));
      el.style.setProperty('--wp-fly-size', `${glowPx}px`);
      el.classList.toggle('is-warning', state.phase === 'warningBlink');
      el.style.transform = `translate(-50%, -50%)`;
    }
    this.syncFireflyDebugOverlay();
  }

  private setupPineconeTreeHit(space: PresenceSpace): void {
    this.teardownPineconeTreeHit();
    if (space.id !== PINECONE_TREE_YARD_ID) return;
    const layer = space.scenery.layers.find((l) => l.id === PINECONE_TREE_ID);
    const treeX = layer?.x ?? 0.44266;
    const treeY = layer?.y ?? 0.40345;
    const treeW = layer?.width ?? 0.1242;
    const treeH = layer?.height ?? treeW * 1.35;
    const hit = document.createElement('button');
    hit.type = 'button';
    hit.className = 'wp-prop wp-pinecone-tree-hit wp-prop-hotspot-btn';
    hit.setAttribute('aria-label', 'Tree');
    hit.style.left = `${treeX * 100}%`;
    hit.style.top = `${(treeY - treeH * 0.28) * 100}%`;
    hit.style.width = `${Math.min(0.22, treeW * 1.15) * 100}%`;
    hit.style.height = `${Math.min(0.3, treeH * 0.78) * 100}%`;
    hit.style.transform = 'translate(-50%, -50%)';
    hit.style.zIndex = String(depthFromY(treeY - treeH * 0.18));
    hit.innerHTML = `<span class="wp-prop-hotspot" aria-hidden="true"></span>`;
    hit.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.onPineconeTreeTapped();
    });
    this.propsEl.append(hit);
    this.pineconeTreeHitEl = hit;
  }

  private teardownPineconeTreeHit(): void {
    this.pineconeTreeHitEl?.remove();
    this.pineconeTreeHitEl = null;
  }

  private onPineconeTreeTapped(): void {
    const space = this.currentSpace;
    if (!space || space.id !== PINECONE_TREE_YARD_ID) return;
    if (this.interactionLocked || this.navigator.isLocked()) return;
    if (pineconeTreeOnCooldown(space.id)) return;
    startPineconeTreeCooldown(space.id);
    this.shakePineconeTree();

    const eligible = replacementEligibleCount(this.pineconeRuntime);
    if (eligible <= 0) return;
    const count = replacementDropCount(eligible);
    const ids = pickUnusedPoolIds(this.pineconeRuntime, count);
    if (ids.length === 0) return;

    const drops = pineconeDropPositions(ids.length);
    const size = largestPineconeDisplaySize(this.pineconeRuntime);
    const layer = space.scenery.layers.find((l) => l.id === PINECONE_TREE_ID);
    const treeX = layer?.x ?? 0.44266;
    const treeY = layer?.y ?? 0.40345;
    const origins = pineconeCanopyOrigins(ids.length, treeX, treeY);
    ids.forEach((id, index) => {
      this.spawnReplenishedPinecone(
        space,
        id,
        origins[index]!,
        drops[index]!,
        size,
        index * PINECONE_TREE_FALL_STAGGER_MS,
      );
    });
  }

  private shakePineconeTree(): void {
    const treeEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${PINECONE_TREE_ID}"]`,
    ) as HTMLElement | null;
    if (!treeEl) return;
    treeEl.classList.add('is-shaking');
    window.setTimeout(() => {
      treeEl.classList.remove('is-shaking');
    }, PINECONE_TREE_SHAKE_MS);
  }

  private resolveScatterDef(poolId: string, space: PresenceSpace) {
    const base = BACKYARD_NATURE_SCATTER_DEFS.find((d) => d.id === poolId);
    if (!base) return null;
    const mode = this.backyardPresentationMode ?? 'desktop';
    const worldAspect = space.worldAspectRatio ?? 16 / 9;
    return withResolvedLayout(
      applyObjectLayoutOverride(base, space.id, mode, getLayoutOverrides()),
      mode,
      { sceneId: space.id, worldAspect },
    );
  }

  private spawnReplenishedPinecone(
    space: PresenceSpace,
    poolId: string,
    origin: SpawnPoint,
    land: SpawnPoint,
    size: { width: number; height: number },
    fallDelayMs = 0,
  ): void {
    const def = this.resolveScatterDef(poolId, space);
    if (!def) return;
    const placed = placeNatureScatter(
      { ...def, enabled: true, x: origin.x, y: origin.y },
      { worldAspect: space.worldAspectRatio ?? 16 / 9 },
    );
    const layer = placed.sceneryLayers[0];
    if (!layer) return;
    layer.width = size.width;
    layer.height = size.height;
    this.mountSceneryLayer(layer);
    const interactive = placed.interactiveObject;
    if (interactive) {
      this.replenishedPineconeObjects.push(interactive);
    }
    const rotation0 = layer.rotationDeg ?? 0;
    const runtime: PineconeInstanceRuntime = {
      id: poolId,
      x: origin.x,
      y: origin.y,
      rotation: rotation0,
      state: 'thrown',
      containerId: null,
      hitOffsetX: interactive ? interactive.x - origin.x : 0,
      hitOffsetY: interactive ? interactive.y - origin.y : 0,
      standOffsetX: interactive ? interactive.standX - origin.x : 0,
      standOffsetY: interactive ? interactive.standY - origin.y : 0,
      width: size.width,
      height: size.height,
    };
    this.pineconeRuntime.set(poolId, runtime);
    rememberReplenishedPinecone(space.id, runtime);

    if (prefersReducedMotion()) {
      this.settleThrownPinecone(poolId, land.x, land.y, rotation0);
      if (this.presence) this.renderProps(this.presence.getSpace());
      return;
    }

    this.pineconeFalls.set(poolId, {
      from: origin,
      land,
      rotation0,
      startedAt: performance.now() + fallDelayMs,
      durationMs: PINECONE_TREE_FALL_MS,
    });
    this.startPineconeFallLoop();
  }

  private startPineconeFallLoop(): void {
    if (this.pineconeFallRaf != null) return;
    const tick = (now: number) => {
      this.pineconeFallRaf = window.requestAnimationFrame(tick);
      if (this.pineconeFalls.size === 0) {
        this.stopPineconeFallLoop();
        return;
      }
      let landedAny = false;
      for (const [id, fall] of [...this.pineconeFalls.entries()]) {
        const elapsed = now - fall.startedAt;
        if (elapsed < 0) continue;
        const sample = samplePineconeTreeFall(
          fall.from,
          fall.land,
          elapsed,
          fall.durationMs,
        );
        this.applyThrownPineconePose(
          id,
          sample.x,
          sample.y,
          fall.rotation0 + sample.rotationDeg,
          0,
        );
        if (sample.landed) {
          this.pineconeFalls.delete(id);
          this.settleThrownPinecone(
            id,
            fall.land.x,
            fall.land.y,
            fall.rotation0 + sample.rotationDeg,
          );
          landedAny = true;
        }
      }
      if (landedAny && this.presence) {
        this.renderProps(this.presence.getSpace());
      }
      if (this.pineconeFalls.size === 0) {
        this.stopPineconeFallLoop();
      }
    };
    this.pineconeFallRaf = window.requestAnimationFrame(tick);
  }

  private stopPineconeFallLoop(): void {
    if (this.pineconeFallRaf != null) {
      cancelAnimationFrame(this.pineconeFallRaf);
      this.pineconeFallRaf = null;
    }
  }

  private restoreReplenishedPinecones(space: PresenceSpace): void {
    this.replenishedPineconeObjects.length = 0;
    if (space.id !== PINECONE_TREE_YARD_ID) return;
    for (const [id, saved] of replenishedPineconesForYard(space.id)) {
      if (saved.state === 'burned' || saved.state === 'bucket') continue;
      this.pineconeRuntime.set(id, { ...saved });
      const def = this.resolveScatterDef(id, space);
      if (!def) continue;
      const placed = placeNatureScatter(
        { ...def, enabled: true, x: saved.x, y: saved.y },
        { worldAspect: space.worldAspectRatio ?? 16 / 9 },
      );
      const layer = placed.sceneryLayers[0];
      if (layer) {
        layer.width = saved.width;
        layer.height = saved.height;
        layer.rotationDeg = saved.rotation;
        this.mountSceneryLayer(layer);
      }
      if (placed.interactiveObject) {
        this.replenishedPineconeObjects.push(placed.interactiveObject);
      }
    }
  }

  private mountSceneryLayer(layer: WorldSceneryLayer): void {
    if (this.sceneryLayersEl.querySelector(`[data-layer-id="${layer.id}"]`)) {
      return;
    }
    const el = document.createElement('div');
    el.className = 'wp-scenery-layer wp-scenery-layer-image wp-scenery-layer-prop wp-scenery-layer-nature-scatter';
    el.dataset.layerId = layer.id;
    el.dataset.layerKind = layer.kind ?? 'nature-scatter';
    if (layer.src) {
      el.style.backgroundImage = runtimeCssUrl(layer.src);
    }
    el.style.left = `${(layer.x ?? 0) * 100}%`;
    el.style.top = `${(layer.y ?? 0) * 100}%`;
    const paintDepth = layer.depth ?? depthFromY(layer.y ?? 0.5);
    el.style.zIndex = String(paintDepth);
    const rot = layer.rotationDeg ?? 0;
    el.style.setProperty('--wp-prop-rot', `${rot}deg`);
    if (layer.width != null) el.style.width = `${layer.width * 100}%`;
    if (layer.height != null) el.style.height = `${layer.height * 100}%`;
    if (layer.spriteFrame) {
      applySpriteFrameBackground(
        el,
        layer.spriteFrame,
        layer.spriteFrame.imageWidth,
        layer.spriteFrame.imageHeight,
      );
    }
    this.sceneryLayersEl.append(el);
  }

  private approachObject(obj: InteractiveObject): void {
    if (this.interactionLocked) return;
    if (!this.presence) return;
    if (this.shedSequence !== 'idle') return;

    if (isKickablePinecone(obj.id)) {
      // Carrying bucket: walk toward the cone so placement nearby stays possible.
      if (this.carriedSlot.isCarryingBucket()) {
        this.cancelPendingKick();
        this.dismissFocus();
        this.pendingFocusId = null;
        this.lastInputKind = 'ground';
        this.lastHitObjectId = null;
        this.presence.walkTo(obj.x, obj.y);
        this.playerInteraction.noteMoving(true);
        this.updateDebugWalkDestination(this.presence.getSpace());
        this.updateDebugInteractionLabel();
        return;
      }
      this.approachKickPinecone(obj);
      return;
    }

    if (isBucketObject(obj.id)) {
      this.approachBucket(obj);
      return;
    }

    if (isShedObject(obj.id)) {
      this.approachShed(obj);
      return;
    }

    if (isFirePitObject(obj.id)) {
      this.approachFirePit(obj);
      return;
    }

    if (isGardenObject(obj.id)) {
      this.approachGarden(obj);
      return;
    }

    if (this.carriedSlot.isCarryingBucket()) return;

    if (this.focusObject?.id === obj.id) return;

    this.cancelPendingKick();
    this.dismissFocus();
    this.pendingFocusId = obj.id;
    const space = this.presence.getSpace();
    this.showDebugClickTargets(
      { x: obj.x, y: obj.y },
      { x: obj.standX, y: obj.standY },
    );
    this.presence.walkTo(obj.standX, obj.standY, {
      faceTowards: { x: obj.x, y: obj.y },
      onArrive: () => {
        // Direct-click lock: only the selected object may focus on arrival.
        if (this.pendingFocusId !== obj.id) return;
        const state = this.presence?.getState();
        if (!state) {
          this.pendingFocusId = null;
          return;
        }
        // Stalled against collision short of the stand — do not open the card.
        const dist = Math.hypot(
          state.characterX - obj.standX,
          state.characterY - obj.standY,
        );
        if (dist > ARRIVE_FOCUS_SLOP) {
          this.pendingFocusId = null;
          this.updateDebugInteractionLabel();
          return;
        }
        this.beginFocus(obj);
      },
    });
    this.playerInteraction.noteMoving(true);
    this.updateDebugWalkDestination(space);
    this.updateDebugInteractionLabel();
  }

  private approachKickPinecone(obj: InteractiveObject): void {
    if (!this.presence) return;
    const runtime = this.pineconeRuntime.get(obj.id);
    // Carried / in-flight cones are not on the ground — ignore kick.
    if (
      runtime?.state === 'carried' ||
      runtime?.state === 'thrown' ||
      runtime?.state === 'burned' ||
      this.carriedSlot.isCarryingId(obj.id)
    ) {
      return;
    }
    this.dismissFocus(true);
    this.pendingFocusId = null;
    this.pendingPickupId = null;

    const target = runtime
      ? { x: runtime.x, y: runtime.y }
      : { x: obj.x, y: obj.y };
    const char = this.presence.getCharacterPosition();
    const aspect =
      this.presence.getSpace().worldAspectRatio &&
      this.presence.getSpace().worldAspectRatio! > 0
        ? this.presence.getSpace().worldAspectRatio!
        : 16 / 9;
    const stand = kickApproachPoint(
      char,
      target,
      KICK_APPROACH_DISTANCE,
      aspect,
    );

    this.pendingKickId = obj.id;
    this.playerInteraction.beginApproach('kickPinecone', obj.id);
    this.showDebugClickTargets(target, stand);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: target,
      onArrive: () => {
        if (this.pendingKickId !== obj.id) return;
        this.pendingKickId = null;
        this.performKickPinecone(obj.id);
      },
    });
    this.updateDebugWalkDestination(this.presence.getSpace());
    this.updateDebugInteractionLabel();
  }

  private approachPickUpPinecone(obj: InteractiveObject): void {
    if (!this.presence) return;
    // One carried object at a time — never silently replace.
    if (this.carriedSlot.isCarrying()) return;
    const runtime = this.pineconeRuntime.get(obj.id);
    if (!runtime || runtime.state !== 'ground') return;

    this.dismissFocus(true);
    this.pendingFocusId = null;
    this.pendingKickId = null;
    this.pineconeMotions.delete(obj.id);

    const target = { x: runtime.x, y: runtime.y };
    const char = this.presence.getCharacterPosition();
    const aspect =
      this.presence.getSpace().worldAspectRatio &&
      this.presence.getSpace().worldAspectRatio! > 0
        ? this.presence.getSpace().worldAspectRatio!
        : 16 / 9;
    const stand = kickApproachPoint(
      char,
      target,
      PICKUP_APPROACH_DISTANCE,
      aspect,
    );

    this.pendingPickupId = obj.id;
    this.playerInteraction.beginApproach('pickUpPinecone', obj.id);
    this.showDebugClickTargets(target, stand);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: target,
      onArrive: () => {
        if (this.pendingPickupId !== obj.id) return;
        this.pendingPickupId = null;
        this.performPickUpPinecone(obj.id);
      },
    });
    this.updateDebugWalkDestination(this.presence.getSpace());
    this.updateDebugInteractionLabel();
  }

  /** Tap bucket: deposit pinecone if carrying one; put down if carrying bucket; else pick up. */
  private approachBucket(obj: InteractiveObject): void {
    if (!this.presence) return;
    const bucket = this.bucketRuntime;
    if (!bucket) return;

    // Tap carried bucket → put it down at Big Sister's current feet.
    if (this.carriedSlot.isCarryingBucket() && bucket.state === 'carried') {
      this.putDownCarriedBucketHere();
      return;
    }

    if (bucket.state !== 'ground') return;

    if (this.carriedSlot.isCarryingPinecone()) {
      this.approachDepositPinecone(obj);
      return;
    }

    // Empty-handed single tap picks up the ground bucket (contents ok).
    if (this.carriedSlot.isCarrying()) return;
    this.approachPickUpBucket(obj);
  }

  /**
   * ONE authoritative shed tap handler (contextual):
   * - storedInShed → get bucket
   * - carried (empty) → put away
   * - ground (empty) → walk to bucket → pick up → walk to shed → put away
   * Never opens the generic "Look" focus card.
   */
  private approachShed(obj: InteractiveObject): void {
    if (!this.presence) return;

    // Walking phases can stall (HMR / interrupted walk). Re-tap recovers.
    if (
      this.shedSequence === 'walking' ||
      this.shedSequence === 'walkingToBucket' ||
      this.shedSequence === 'walkingToShed'
    ) {
      this.logShed('re-tap — aborting stalled walk phase', this.shedSequence);
      this.abortShedSequence();
    } else if (this.shedSequence !== 'idle') {
      this.logShed('tapped (ignored — sequence busy)', this.shedSequence);
      return;
    }

    const bucket = this.bucketRuntime;
    this.logShed('tapped', {
      bucketState: bucket?.state ?? null,
      carrying: this.carriedSlot.getKind(),
      contents: bucket?.contents.length ?? 0,
    });

    // Carrying a pinecone — do not steal/discard it for shed bucket actions.
    if (this.carriedSlot.isCarryingPinecone()) {
      this.logShed('no-op (carrying pinecone)');
      return;
    }

    if (this.carriedSlot.isCarryingBucket() && bucket) {
      this.startShedStoreSequence(obj);
      return;
    }

    if (bucket && bucket.state === 'storedInShed' && !this.carriedSlot.isCarrying()) {
      this.startShedRetrieveSequence(obj);
      return;
    }

    if (
      bucket &&
      bucket.state === 'ground' &&
      !this.carriedSlot.isCarrying()
    ) {
      this.startShedReturnFromGroundSequence(obj);
      return;
    }

    this.logShed('no-op (no bucket shed action)');
  }

  private startShedRetrieveSequence(obj: InteractiveObject): void {
    if (!this.presence) return;
    this.dismissFocus(true);
    this.pendingFocusId = null;
    this.pendingKickId = null;
    this.pendingPickupId = null;
    this.pendingBucketAction = null;
    this.shedSequence = 'walking';
    this.playerInteraction.beginApproach('retrieveBucket', SHED_OBJECT_ID);
    this.logShed('walking to interaction point');
    const stand = { x: obj.standX, y: obj.standY };
    this.showDebugClickTargets({ x: obj.x, y: obj.y }, stand);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: { x: obj.x, y: obj.y },
      onArrive: () => {
        if (this.shedSequence !== 'walking') return;
        this.logShed('arrived');
        this.runShedRetrieveChoreography();
      },
    });
    this.updateDebugWalkDestination(this.presence.getSpace());
    this.updateDebugInteractionLabel();
  }

  /**
   * Bucket is on the ground somewhere — one shed tap fetches it and puts it away:
   * walkToBucket → pickUp → walkToShed → store.
   */
  private startShedReturnFromGroundSequence(shedObj: InteractiveObject): void {
    if (!this.presence) return;
    const bucket = this.bucketRuntime;
    if (
      !bucket ||
      bucket.state !== 'ground' ||
      this.carriedSlot.isCarrying()
    ) {
      this.logShed('return aborted — invalid start state');
      return;
    }

    this.dismissFocus(true);
    this.pendingFocusId = null;
    this.pendingKickId = null;
    this.pendingPickupId = null;
    this.pendingBucketAction = null;
    this.shedSequence = 'walkingToBucket';
    this.playerInteraction.beginApproach('storeBucket', SHED_OBJECT_ID);
    this.logShed('return: walking to bucket');

    const target = { x: bucket.x, y: bucket.y };
    const char = this.presence.getCharacterPosition();
    const aspect =
      this.presence.getSpace().worldAspectRatio &&
      this.presence.getSpace().worldAspectRatio! > 0
        ? this.presence.getSpace().worldAspectRatio!
        : 16 / 9;
    const stand = kickApproachPoint(
      char,
      target,
      BUCKET_APPROACH_DISTANCE,
      aspect,
    );
    this.showDebugClickTargets(target, stand);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: target,
      onArrive: () => {
        if (this.shedSequence !== 'walkingToBucket') return;
        this.logShed('return: arrived at bucket');
        this.runShedReturnPickupThenWalkToShed(shedObj);
      },
    });
    this.updateDebugWalkDestination(this.presence.getSpace());
    this.updateDebugInteractionLabel();
  }

  /** Pickup during shed auto-return — must finish before walk-to-shed starts. */
  private runShedReturnPickupThenWalkToShed(shedObj: InteractiveObject): void {
    if (!this.presence) return;
    const bucket = this.bucketRuntime;
    if (
      !bucket ||
      bucket.state !== 'ground' ||
      this.carriedSlot.isCarrying()
    ) {
      this.logShed('return pickup aborted', {
        bucketState: bucket?.state,
        carrying: this.carriedSlot.getKind(),
      });
      this.finishShedSequence();
      return;
    }

    this.shedSequence = 'pickingUpBucket';
    this.logShed('return: picking up bucket');
    this.clearKickTimers();
    this.playerInteraction.beginPerformDirect('pickUpBucket', BUCKET_OBJECT_ID);
    this.characterEl.classList.add('is-picking-up');

    const reduced = prefersReducedMotion();
    const contact = window.setTimeout(() => {
      if (this.shedSequence !== 'pickingUpBucket') return;
      if (!this.carriedSlot.tryCarry(BUCKET_OBJECT_ID, 'bucket')) {
        this.logShed('return tryCarry failed');
        this.finishShedSequence();
        return;
      }
      bucket.state = 'carried';
      this.characterEl.classList.add('is-carrying', 'is-carrying-bucket');
      this.syncCarriedObjectVisual();
      this.syncBucketVisibility();
      if (this.presence) this.renderProps(this.presence.getSpace());
      this.playKickThump();
      this.logShed('return: bucket carried');
    }, reduced ? 0 : BUCKET_PICKUP_CONTACT_MS);
    this.kickTimers.push(contact);

    const done = window.setTimeout(() => {
      if (this.shedSequence !== 'pickingUpBucket') return;
      this.characterEl.classList.remove('is-picking-up');
      if (!this.carriedSlot.isCarryingBucket()) {
        this.finishShedSequence();
        return;
      }
      this.startShedStoreWalk(shedObj);
    }, reduced ? 0 : BUCKET_PICKUP_TOTAL_MS);
    this.kickTimers.push(done);
  }

  private startShedStoreSequence(obj: InteractiveObject): void {
    if (!this.presence) return;
    this.dismissFocus(true);
    this.pendingFocusId = null;
    this.pendingKickId = null;
    this.pendingPickupId = null;
    this.pendingBucketAction = null;
    this.startShedStoreWalk(obj);
  }

  /** Walk to shed interaction point, then open + store (shared by carry + auto-return). */
  private startShedStoreWalk(obj: InteractiveObject): void {
    if (!this.presence) return;
    if (!this.carriedSlot.isCarryingBucket()) {
      this.finishShedSequence();
      return;
    }
    this.shedSequence = 'walkingToShed';
    this.playerInteraction.beginApproach('storeBucket', SHED_OBJECT_ID);
    this.logShed('walking to store');
    const stand = { x: obj.standX, y: obj.standY };
    this.showDebugClickTargets({ x: obj.x, y: obj.y }, stand);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: { x: obj.x, y: obj.y },
      onArrive: () => {
        if (this.shedSequence !== 'walkingToShed') return;
        this.logShed('arrived (store)');
        this.runShedStoreChoreography();
      },
    });
    this.updateDebugWalkDestination(this.presence.getSpace());
    this.updateDebugInteractionLabel();
  }

  private runShedRetrieveChoreography(): void {
    if (!this.presence) return;
    const bucket = this.bucketRuntime;
    if (!bucket || bucket.state !== 'storedInShed' || this.carriedSlot.isCarrying()) {
      this.logShed('retrieve aborted — invalid state', {
        bucketState: bucket?.state,
        carrying: this.carriedSlot.getKind(),
      });
      this.finishShedSequence();
      return;
    }

    this.clearKickTimers();
    this.playerInteraction.beginPerformDirect('retrieveBucket', SHED_OBJECT_ID);
    const reduced = prefersReducedMotion();

    // 1) Interaction beat — shed art stays closed; bucket reveal follows.
    this.shedSequence = 'opening';
    this.logShed('opening');
    this.ensureShedClosedVisual();

    const revealAt = reduced ? 0 : SHED_DOOR_OPEN_MS;
    const attachAt = revealAt + (reduced ? 0 : SHED_BUCKET_REVEAL_MS);
    const closeAt = attachAt + (reduced ? 0 : SHED_RETRIEVE_ATTACH_MS);
    const doneAt = closeAt + (reduced ? 0 : SHED_DOOR_CLOSE_MS);

    // 2) Reveal bucket inside shed interior.
    this.kickTimers.push(
      window.setTimeout(() => {
        if (this.shedSequence !== 'opening') return;
        this.shedSequence = 'revealing';
        this.logShed('open');
        this.revealBucketInShed();
        this.logShed('bucket revealed');
      }, revealAt),
    );

    // 3) Attach real bucket to Big Sister.
    this.kickTimers.push(
      window.setTimeout(() => {
        if (this.shedSequence !== 'revealing') return;
        this.shedSequence = 'retrieving';
        this.logShed('retrieving');
        if (!this.carriedSlot.tryCarry(BUCKET_OBJECT_ID, 'bucket')) {
          this.logShed('tryCarry failed');
          this.finishShedSequence();
          return;
        }
        bucket.state = 'carried';
        this.logShed('bucket state storedInShed -> carried');
        this.characterEl.classList.add('is-carrying', 'is-carrying-bucket');
        const layerEl = this.sceneryLayersEl.querySelector(
          `[data-layer-id="${BUCKET_OBJECT_ID}"]`,
        ) as HTMLElement | null;
        layerEl?.classList.remove('is-shed-reveal');
        if (layerEl && bucket.width > 0) {
          layerEl.style.width = `${bucket.width * 100}%`;
        }
        this.syncCarriedObjectVisual();
        this.syncBucketVisibility();
        this.playKickThump();
        this.logShed('bucket attached to Big Sister');
      }, attachAt),
    );

    // 4) Close doors — restore exact closed shed art.
    this.kickTimers.push(
      window.setTimeout(() => {
        if (this.shedSequence !== 'retrieving') return;
        this.shedSequence = 'closing';
        this.logShed('closing');
        this.ensureShedClosedVisual();
      }, closeAt),
    );

    this.kickTimers.push(
      window.setTimeout(() => {
        this.logShed('closed');
        this.finishShedSequence();
        this.logShed('interaction complete');
      }, doneAt),
    );
  }

  private runShedStoreChoreography(): void {
    if (!this.presence) return;
    const bucket = this.bucketRuntime;
    if (!bucket || !this.carriedSlot.isCarryingBucket()) {
      this.finishShedSequence();
      return;
    }

    this.clearKickTimers();
    this.playerInteraction.beginPerformDirect('storeBucket', SHED_OBJECT_ID);
    const reduced = prefersReducedMotion();

    this.shedSequence = 'opening';
    this.logShed('opening (store)');
    this.ensureShedClosedVisual();

    const putAt = reduced ? 0 : SHED_DOOR_OPEN_MS;
    const closeAt = putAt + (reduced ? 0 : SHED_STORE_PUT_MS);
    const doneAt = closeAt + (reduced ? 0 : SHED_DOOR_CLOSE_MS);

    this.kickTimers.push(
      window.setTimeout(() => {
        if (this.shedSequence !== 'opening') return;
        this.shedSequence = 'storing';
        this.carriedSlot.clear();
        this.characterEl.classList.remove('is-carrying', 'is-carrying-bucket');
        bucket.state = 'storedInShed';
                this.syncBucketVisibility();
        if (this.presence) this.renderProps(this.presence.getSpace());
        this.playKickThump();
        this.logShed('bucket storedInShed');
      }, putAt),
    );

    this.kickTimers.push(
      window.setTimeout(() => {
        if (this.shedSequence !== 'storing') return;
        this.shedSequence = 'closing';
        this.logShed('closing (store)');
        this.ensureShedClosedVisual();
      }, closeAt),
    );

    this.kickTimers.push(
      window.setTimeout(() => {
        this.finishShedSequence();
        this.logShed('store complete');
      }, doneAt),
    );
  }

  private finishShedSequence(): void {
    this.clearKickTimers();
    // Always restore closed shed art — no overlay residue.
    this.ensureShedClosedVisual();
    this.shedSequence = 'idle';
    this.syncBucketVisibility();
    this.playerInteraction.complete();
    this.flushPostKickCommand();
    this.updateDebugInteractionLabel();
  }

  /** Cancel a stalled shed walk phase so a fresh shed tap can run. */
  private abortShedSequence(): void {
    this.clearKickTimers();
    this.ensureShedClosedVisual();
    this.characterEl.classList.remove('is-picking-up');
    this.shedSequence = 'idle';
    this.pendingBucketAction = null;
    this.pendingPickupId = null;
    this.playerInteraction.complete();
    this.syncBucketVisibility();
    this.updateDebugInteractionLabel();
  }

  private logShed(message: string, detail?: unknown): void {
    if (!this.isShedDebugEnabled()) return;
    if (detail !== undefined) {
      console.log(`[SHED] ${message}`, detail);
    } else {
      console.log(`[SHED] ${message}`);
    }
  }

  private isShedDebugEnabled(): boolean {
    if (import.meta.env.DEV) return true;
    try {
      return new URLSearchParams(window.location.search).has('debug');
    } catch {
      return false;
    }
  }

  /** Shed always shows closed art — never swap to the broken open texture. */
  private ensureShedClosedVisual(): void {
    const shedEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${SHED_OBJECT_ID}"]`,
    ) as HTMLElement | null;
    if (!shedEl) return;
    shedEl.querySelector('.wp-shed-door-overlay')?.remove();
    shedEl.classList.remove('is-door-open');
    const src = this.shedClosedSrc ?? shedEl.dataset.closedSrc ?? null;
    if (src) {
      if (!shedEl.dataset.closedSrc) {
        shedEl.dataset.closedSrc = src;
      }
      shedEl.style.backgroundImage = runtimeCssUrl(src);
    }
    shedEl.hidden = false;
    shedEl.style.visibility = '';
    shedEl.style.display = '';
    shedEl.removeAttribute('aria-hidden');
  }

  private decorateBucketPineconeTip(el: HTMLElement, slot: number): void {
    el.className = 'wp-bucket-pinecone-tip';
    el.style.setProperty(
      '--wp-tip-rot',
      `${BUCKET_PINECONE_TIP_ROTS[slot] ?? 0}deg`,
    );
    const f = BUCKET_PINECONE_FRAME;
    const sw = BUCKET_PINECONE_ATLAS_W;
    const sh = BUCKET_PINECONE_ATLAS_H;
    el.style.backgroundImage = runtimeCssUrl(BUCKET_PINECONE_ATLAS);
    el.style.backgroundRepeat = 'no-repeat';
    el.style.backgroundSize = `${(sw / f.width) * 100}% ${(sh / f.height) * 100}%`;
    const rangeX = sw - f.width;
    const rangeY = sh - f.height;
    el.style.backgroundPosition = `${rangeX > 0 ? (f.x / rangeX) * 100 : 0}% ${rangeY > 0 ? (f.y / rangeY) * 100 : 0}%`;
  }

  /** Brief beat: bucket visible in the open shed before attach. */
  private revealBucketInShed(): void {
    const bucket = this.bucketRuntime;
    const space = this.presence?.getSpace() ?? this.currentSpace;
    if (!bucket || !space) return;
    const shed =
      space.interactiveObjects?.find((o) => o.id === SHED_OBJECT_ID) ?? null;
    const shedLayer = space.scenery.layers.find((l) => l.id === SHED_OBJECT_ID);
    const sx = shedLayer?.x ?? shed?.x ?? 0.82;
    const sy = shedLayer?.y ?? shed?.y ?? 0.54;
    // Just inside the open doorway (door face is left of shed center).
    const pose = { x: sx - 0.048, y: sy - 0.01 };
    bucket.x = pose.x;
    bucket.y = pose.y;
    this.applyBucketRuntimePose(pose.x, pose.y, -6);
    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${BUCKET_OBJECT_ID}"]`,
    ) as HTMLElement | null;
    if (layerEl) {
      layerEl.hidden = false;
      layerEl.classList.remove('is-stored-in-shed', 'is-carried');
      layerEl.classList.add('is-shed-reveal');
      // Sit in the doorway opening, above shed plate.
      const depth = (shedLayer?.depth ?? 97) + 4;
      layerEl.style.zIndex = String(depth);
      // Slightly smaller while inside so it reads as "in the shed".
      const baseW = bucket.width > 0 ? bucket.width : 0.048;
      layerEl.style.width = `${baseW * 0.72 * 100}%`;
    }
  }

  private approachDepositPinecone(obj: InteractiveObject): void {
    if (!this.presence) return;
    if (!this.carriedSlot.isCarryingPinecone()) return;
    const bucket = this.bucketRuntime;
    if (!bucket || bucket.state !== 'ground') return;

    this.cancelPendingKick();
    this.dismissFocus(true);
    this.pendingFocusId = null;
    this.pendingDepositBucketId = obj.id;
    this.pendingBucketAction = 'deposit';

    const target = { x: bucket.x, y: bucket.y };
    const char = this.presence.getCharacterPosition();
    const aspect =
      this.presence.getSpace().worldAspectRatio &&
      this.presence.getSpace().worldAspectRatio! > 0
        ? this.presence.getSpace().worldAspectRatio!
        : 16 / 9;
    const stand = kickApproachPoint(
      char,
      target,
      BUCKET_APPROACH_DISTANCE,
      aspect,
    );
    this.playerInteraction.beginApproach('depositPinecone', obj.id);
    this.showDebugClickTargets(target, stand);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: target,
      onArrive: () => {
        if (this.pendingBucketAction !== 'deposit') return;
        if (this.pendingDepositBucketId !== obj.id) return;
        this.pendingBucketAction = null;
        this.pendingDepositBucketId = null;
        this.performDepositPinecone();
      },
    });
    this.updateDebugWalkDestination(this.presence.getSpace());
    this.updateDebugInteractionLabel();
  }

  private approachPickUpBucket(obj: InteractiveObject): void {
    if (!this.presence) return;
    if (this.shedSequence !== 'idle') return;
    if (this.carriedSlot.isCarrying()) return;
    const bucket = this.bucketRuntime;
    if (!bucket || bucket.state !== 'ground') return;

    this.dismissFocus(true);
    this.pendingFocusId = null;
    this.pendingKickId = null;
    this.pendingBucketAction = 'pickup';

    const target = { x: bucket.x, y: bucket.y };
    const char = this.presence.getCharacterPosition();
    const aspect =
      this.presence.getSpace().worldAspectRatio &&
      this.presence.getSpace().worldAspectRatio! > 0
        ? this.presence.getSpace().worldAspectRatio!
        : 16 / 9;
    const stand = kickApproachPoint(
      char,
      target,
      BUCKET_APPROACH_DISTANCE,
      aspect,
    );
    this.pendingPickupId = obj.id;
    this.playerInteraction.beginApproach('pickUpBucket', obj.id);
    this.showDebugClickTargets(target, stand);
    this.logShed('pickup: walking to bucket', target);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: target,
      onArrive: () => {
        if (this.pendingPickupId !== obj.id) return;
        if (this.pendingBucketAction !== 'pickup') return;
        this.pendingPickupId = null;
        this.pendingBucketAction = null;
        this.performPickUpBucket();
      },
    });
    this.updateDebugWalkDestination(this.presence.getSpace());
    this.updateDebugInteractionLabel();
  }

  /** Tap carried bucket → put down at Big Sister's current ground pose (no walk). */
  private putDownCarriedBucketHere(): void {
    if (!this.presence) return;
    if (!this.carriedSlot.isCarryingBucket()) return;
    if (this.shedSequence !== 'idle') return;
    if (this.playerInteraction.isPerforming()) return;
    const state = this.presence.getState();
    const space = this.presence.getSpace();
    const clamped = clampWalkTarget(state.characterX, state.characterY, space);
    this.pendingPlacePoint = null;
    this.pendingBucketAction = null;
    this.cancelPendingKick();
    this.dismissFocus(true);
    if (!this.isValidBucketPlacement(clamped.x, clamped.y)) return;
    this.performPlaceBucket(clamped.x, clamped.y);
  }


  /** True when a world point is walkable yard (not shed interior / major obstacles). */
  private isValidBucketPlacement(x: number, y: number): boolean {
    const space = this.presence?.getSpace();
    if (!space) return false;
    if (space.walkPolygon && space.walkPolygon.length >= 3) {
      if (!pointInPolygon(x, y, space.walkPolygon)) return false;
    } else if (space.walkBounds) {
      const b = space.walkBounds;
      if (x < b.xMin || x > b.xMax || y < b.yMin || y > b.yMax) return false;
    }
    if (pointInAnyObstacle(x, y, space.walkObstacles)) return false;
    // Keep clear of the shed footprint / interaction stand.
    const shed = space.interactiveObjects?.find((o) => isShedObject(o.id));
    if (shed) {
      const dx = x - shed.x;
      const dy = y - shed.y;
      if (Math.hypot(dx, dy) < 0.07) return false;
    }
    return true;
  }

  private placeCarriedBucketAt(desiredX: number, desiredY: number): void {
    if (!this.presence) return;
    if (!this.carriedSlot.isCarryingBucket()) return;
    if (!this.isValidBucketPlacement(desiredX, desiredY)) return;
    const space = this.presence.getSpace();
    const clamped = clampWalkTarget(desiredX, desiredY, space);
    this.pendingPlacePoint = clamped;
    this.pendingBucketAction = 'place';
    this.playerInteraction.beginApproach('placeBucket', BUCKET_OBJECT_ID);
    this.showDebugClickTargets({ x: desiredX, y: desiredY }, clamped);
    this.presence.walkTo(clamped.x, clamped.y, {
      onArrive: () => {
        if (this.pendingBucketAction !== 'place') return;
        const point = this.pendingPlacePoint;
        this.pendingBucketAction = null;
        this.pendingPlacePoint = null;
        if (!point) return;
        this.performPlaceBucket(point.x, point.y);
      },
    });
    this.playerInteraction.noteMoving(true);
    this.updateDebugWalkDestination(space);
  }

  private performPickUpBucket(): void {
    if (!this.presence) return;
    const bucket = this.bucketRuntime;
    if (!bucket || bucket.state !== 'ground') {
      this.playerInteraction.complete();
      return;
    }
    if (this.carriedSlot.isCarrying()) {
      this.playerInteraction.complete();
      return;
    }

    this.clearKickTimers();
    // Prefer direct perform so pickup still runs if approach state was raced.
    this.playerInteraction.beginPerformDirect('pickUpBucket', BUCKET_OBJECT_ID);
    this.characterEl.classList.add('is-picking-up');
    this.logShed('pickup: start', { x: bucket.x, y: bucket.y });

    const contact = window.setTimeout(() => {
      if (!this.carriedSlot.tryCarry(BUCKET_OBJECT_ID, 'bucket')) return;
      bucket.state = 'carried';
      this.characterEl.classList.add('is-carrying', 'is-carrying-bucket');
      this.syncCarriedObjectVisual();
      this.syncBucketVisibility();
      if (this.presence) this.renderProps(this.presence.getSpace());
      this.logShed('pickup: carried');
    }, prefersReducedMotion() ? 0 : BUCKET_PICKUP_CONTACT_MS);
    this.kickTimers.push(contact);

    const done = window.setTimeout(() => {
      this.characterEl.classList.remove('is-picking-up');
      this.playerInteraction.complete();
      this.flushPostKickCommand();
    }, prefersReducedMotion() ? 0 : BUCKET_PICKUP_TOTAL_MS);
    this.kickTimers.push(done);
  }

  private performPlaceBucket(x: number, y: number): void {
    if (!this.presence) return;
    const bucket = this.bucketRuntime;
    if (!bucket || !this.carriedSlot.isCarryingBucket()) {
      this.playerInteraction.complete();
      return;
    }

    this.clearKickTimers();
    this.playerInteraction.beginPerformDirect('placeBucket', BUCKET_OBJECT_ID);
    this.characterEl.classList.add('is-picking-up');

    const place = window.setTimeout(() => {
      this.carriedSlot.clear();
      this.characterEl.classList.remove('is-carrying', 'is-carrying-bucket');
      // Runtime ground pose only — remains fully interactive for re-pickup.
      bucket.state = 'ground';
      this.applyBucketRuntimePose(x, y, bucket.rotation);
      const layerEl = this.sceneryLayersEl.querySelector(
        `[data-layer-id="${BUCKET_OBJECT_ID}"]`,
      ) as HTMLElement | null;
      if (layerEl) {
        layerEl.hidden = false;
        layerEl.classList.remove('is-carried', 'is-shed-reveal', 'is-stored-in-shed');
        if (bucket.width > 0) {
          layerEl.style.width = `${bucket.width * 100}%`;
        }
      }
      this.syncBucketVisibility();
      // Recreate hotspot immediately so long-press works again (no one-shot place).
      if (this.presence) this.renderProps(this.presence.getSpace());
      this.playKickThump();
      this.logShed('place: ground', { x: bucket.x, y: bucket.y });
    }, prefersReducedMotion() ? 0 : Math.round(BUCKET_PLACE_TOTAL_MS * 0.45));
    this.kickTimers.push(place);

    const done = window.setTimeout(() => {
      this.characterEl.classList.remove('is-picking-up');
      this.playerInteraction.complete();
      this.flushPostKickCommand();
    }, prefersReducedMotion() ? 0 : BUCKET_PLACE_TOTAL_MS);
    this.kickTimers.push(done);
  }

  /**
   * Hubert firepit: light when off and empty-handed; feed a carried pinecone
   * when lit. Second tap on an already-lit fire with empty hands is a no-op.
   */
  private approachGarden(obj: InteractiveObject): void {
    this.logGardenDebug('garden handler entered');
    if (!this.presence) {
      this.logGardenDebug('garden blocked: no presence');
      return;
    }
    const space = this.presence.getSpace();
    if (!isGardenFoodYard(space.id)) {
      this.logGardenDebug(`garden blocked: yard ${space.id} has no food config`);
      return;
    }
    // Unstick interrupted harvest/eat desyncs before the one-active-food guard.
    if (
      this.rabbitState &&
      this.rabbitState.phase !== 'seekingFood' &&
      this.rabbitState.phase !== 'eatingFood'
    ) {
      reconcileGardenFoodIdleState(space.id);
    }
    this.logGardenDebug(`garden config = ${gardenFoodStateForYard(space.id)?.kind ?? 'none'}`);
    if (!canHarvestGarden(space.id)) {
      this.logGardenDebug('garden interaction allowed = false (active food / cooldown)');
      return;
    }
    const tod = timeOfDayForYard(space.id);
    if (tod === 'night') {
      this.logGardenDebug('garden blocked: night');
      return;
    }
    if (this.rabbitState && isRabbitSleepPhase(this.rabbitState.phase)) {
      this.logGardenDebug('garden blocked: rabbit sleeping');
      return;
    }
    if (this.carriedSlot.isCarrying()) {
      this.logGardenDebug('garden blocked: carrying object');
      return;
    }
    this.logGardenDebug('garden interaction allowed = true');

    this.dismissFocus(true);
    this.pendingFocusId = null;
    this.cancelPendingKick();
    this.pendingGardenHarvest = true;

    const target = { x: obj.x, y: obj.y };
    this.playerInteraction.beginApproach('harvestGarden', obj.id);
    this.showDebugClickTargets(target, { x: obj.standX, y: obj.standY });
    this.logGardenDebug('walking to garden');
    this.presence.walkTo(obj.standX, obj.standY, {
      faceTowards: target,
      onArrive: () => {
        this.logGardenDebug('garden arrival callback entered');
        if (!this.pendingGardenHarvest) {
          this.logGardenDebug('garden arrival aborted: pending flag cleared');
          return;
        }
        this.pendingGardenHarvest = false;
        if (!this.presence?.getState()) {
          this.logGardenDebug('garden arrival aborted: no player state');
          this.playerInteraction.complete();
          return;
        }
        // Direct toy — trust onArrive like kick/pickup (not focus-card slop).
        this.logGardenDebug('spawning crop');
        this.performGardenHarvest(obj);
      },
    });
    this.playerInteraction.noteMoving(true);
    this.updateDebugWalkDestination(space);
    this.updateDebugInteractionLabel();
  }

  private logGardenDebug(message: string): void {
    if (!this.gardenDebugEnabled) return;
    // eslint-disable-next-line no-console
    console.info(`[garden] ${message}`);
  }

  private performGardenHarvest(obj: InteractiveObject): void {
    if (!this.presence) return;
    const space = this.presence.getSpace();
    const kind = gardenFoodKindForYard(space.id);
    this.logGardenDebug(`garden crop type resolved = ${kind ?? 'none'}`);
    const rabbitConfig = this.rabbitConfig;
    if (!rabbitConfig) {
      this.logGardenDebug('garden harvest aborted: no rabbit config');
      reconcileGardenFoodIdleState(space.id);
      this.playerInteraction.complete();
      return;
    }
    if (!beginGardenHarvest(space.id)) {
      this.logGardenDebug('garden harvest aborted: beginGardenHarvest failed');
      this.playerInteraction.complete();
      return;
    }
    this.logGardenDebug(
      `garden crop asset key = ${kind ? GARDEN_FOOD_SRC[kind] : 'none'}`,
    );
    this.logGardenDebug('creating garden food');

    this.clearKickTimers();
    // Same pattern as bucket pickup — approach state may already be idle.
    this.playerInteraction.beginPerformDirect('harvestGarden', obj.id);
    this.characterEl.classList.add('is-depositing');
    this.showGardenFoodAtCharacter(space);
    this.logGardenDebug('garden food created');

    const throwAt = prefersReducedMotion() ? 0 : GARDEN_PULL_MS;
    const doneAt = prefersReducedMotion()
      ? 0
      : GARDEN_PULL_MS + GARDEN_THROW_CHAR_MS;

    const release = window.setTimeout(() => {
      if (!this.presence) return;
      const charState = this.presence.getState();
      const aspect =
        space.worldAspectRatio && space.worldAspectRatio > 0
          ? space.worldAspectRatio
          : 16 / 9;
      const from = carriedObjectWorldPose(
        { x: charState.characterX, y: charState.characterY },
        charState.facing,
      );
      const land = pickGardenThrowTarget(
        { x: obj.x, y: obj.y },
        rabbitConfig,
        this.rabbitState
          ? { x: this.rabbitState.x, y: this.rabbitState.y }
          : null,
        space.walkObstacles,
        aspect,
      );
      this.logGardenDebug(
        `throw target = ${land.x.toFixed(3)}, ${land.y.toFixed(3)}`,
      );
      const plan = createPineconeThrowPlan(
        from,
        land,
        aspect,
        (x, y) => clampWalkTarget(x, y, space),
      );
      beginGardenThrow(space.id);
      this.characterEl.classList.add('is-throwing');
      this.characterEl.dataset.facing =
        plan.land.x >= charState.characterX ? 'right' : 'left';
      this.gardenFoodThrow = {
        plan,
        releasedAt: performance.now(),
        yardId: space.id,
        landRot: plan.rotation0 + (Math.random() < 0.5 ? -1 : 1) * 28,
      };
      this.logGardenDebug('throw started');
      this.startGardenFoodThrowLoop();
    }, throwAt);
    this.kickTimers.push(release);

    const settle = window.setTimeout(() => {
      this.characterEl.classList.remove('is-depositing', 'is-throwing');
      if (prefersReducedMotion()) {
        const flight = this.gardenFoodThrow;
        if (flight) {
          landGardenFood(
            flight.yardId,
            flight.plan.land,
            flight.landRot,
          );
          this.gardenFoodThrow = null;
          this.notifyRabbitOfGardenFood(space);
          this.syncGardenFoodVisual(space);
        }
        this.playerInteraction.complete();
        this.flushPostKickCommand();
      }
    }, doneAt);
    this.kickTimers.push(settle);
  }

  /** Brief pull pose — crop visible at the character's hand before the toss. */
  private showGardenFoodAtCharacter(_space: PresenceSpace): void {
    if (!this.presence || !this.gardenFoodEl) return;
    const charState = this.presence.getState();
    const pose = carriedObjectWorldPose(
      { x: charState.characterX, y: charState.characterY },
      charState.facing,
    );
    this.applyGardenFoodThrowPose(pose.x, pose.y, 0, 0);
  }

  /** Wake the rabbit food-notice path after food lands on the grass. */
  private notifyRabbitOfGardenFood(space: PresenceSpace): void {
    if (!this.rabbitState || !isGardenFoodAvailableToRabbit(space.id)) return;
    this.rabbitState.foodNoticeMs = -1;
    this.logGardenDebug('rabbit food target set');
  }

  private startGardenFoodThrowLoop(): void {
    if (this.gardenFoodThrowRaf != null) return;
    const tick = (now: number) => {
      this.gardenFoodThrowRaf = window.requestAnimationFrame(tick);
      const flight = this.gardenFoodThrow;
      const space = this.currentSpace;
      if (!flight || !space) {
        this.stopGardenFoodThrowLoop();
        return;
      }

      const elapsed = now - flight.releasedAt;
      const sample = samplePineconeThrow(flight.plan, elapsed);
      this.applyGardenFoodThrowPose(
        sample.x,
        sample.y,
        sample.rotationDeg,
        sample.liftPx,
      );

      if (!sample.landed) return;

      landGardenFood(flight.yardId, flight.plan.land, flight.landRot);
      this.gardenFoodThrow = null;
      this.stopGardenFoodThrowLoop();
      this.logGardenDebug('throw completed');
      this.notifyRabbitOfGardenFood(space);
      this.syncGardenFoodVisual(space);
      this.playKickThump();
      if (this.playerInteraction.isPerforming()) {
        this.characterEl.classList.remove('is-throwing');
        this.playerInteraction.complete();
        this.flushPostKickCommand();
      }
    };
    this.gardenFoodThrowRaf = window.requestAnimationFrame(tick);
  }

  private stopGardenFoodThrowLoop(): void {
    if (this.gardenFoodThrowRaf != null) {
      cancelAnimationFrame(this.gardenFoodThrowRaf);
      this.gardenFoodThrowRaf = null;
    }
  }

  private applyGardenFoodThrowPose(
    x: number,
    y: number,
    rotationDeg: number,
    liftPx: number,
  ): void {
    const el = this.gardenFoodEl;
    const space = this.currentSpace;
    if (!el || !space) return;

    const foodState = gardenFoodStateForYard(space.id);
    const kind = foodState?.kind ?? 'corn';
    const meta = GARDEN_FOOD_IMAGE[kind];
    const worldAspect = space.worldAspectRatio ?? 1;
    const cal = yardScaleFor(space.id);
    const heightFrac = feetToHeightFrac(GARDEN_FOOD_HEIGHT_FEET, y, cal);
    const widthFrac =
      heightFrac / ((meta.imageHeight / meta.imageWidth) * worldAspect);

    el.hidden = false;
    el.classList.add('is-thrown');
    el.style.left = `${x * 100}%`;
    el.style.top = `${y * 100}%`;
    el.style.width = `${widthFrac * 100}%`;
    el.style.height = `${heightFrac * 100}%`;
    el.style.zIndex = String(Math.round(70 + y * 25) + 40);
    el.style.setProperty('--wp-prop-rot', `${rotationDeg}deg`);
    el.style.setProperty('--wp-throw-lift', `${liftPx}px`);
  }

  private approachFirePit(obj: InteractiveObject): void {
    if (!this.presence) return;
    const space = this.presence.getSpace();
    const fire = firepitStateForYard(space.id);
    if (!fire) return;
    if (this.carriedSlot.isCarryingBucket()) return;

    this.dismissFocus(true);
    this.pendingFocusId = null;

    if (this.carriedSlot.isCarryingPinecone()) {
      if (fire.phase === 'off') return;
      this.approachBurnPinecone(obj);
      return;
    }

    if (fire.phase !== 'off') return;

    this.cancelPendingKick();
    this.pendingFirepitAction = 'light';
    const target = { x: obj.x, y: obj.y };
    const char = this.presence.getCharacterPosition();
    const aspect =
      space.worldAspectRatio && space.worldAspectRatio > 0
        ? space.worldAspectRatio
        : 16 / 9;
    const stand = kickApproachPoint(
      char,
      target,
      FIRE_PIT_APPROACH_DISTANCE,
      aspect,
    );
    this.playerInteraction.beginApproach('lightFirepit', obj.id);
    this.showDebugClickTargets(target, stand);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: target,
      onArrive: () => {
        if (this.pendingFirepitAction !== 'light') return;
        this.pendingFirepitAction = null;
        this.performLightFirePit();
      },
    });
    this.updateDebugWalkDestination(space);
    this.updateDebugInteractionLabel();
  }

  private approachBurnPinecone(obj: InteractiveObject): void {
    if (!this.presence) return;
    if (!this.carriedSlot.isCarryingPinecone()) return;
    const fire = firepitStateForYard(this.presence.getSpace().id);
    if (!fire || fire.phase === 'off') return;

    this.cancelPendingKick();
    this.pendingFirepitAction = 'burn';
    const target = { x: obj.x, y: obj.y };
    const char = this.presence.getCharacterPosition();
    const aspect =
      this.presence.getSpace().worldAspectRatio &&
      this.presence.getSpace().worldAspectRatio! > 0
        ? this.presence.getSpace().worldAspectRatio!
        : 16 / 9;
    const stand = kickApproachPoint(
      char,
      target,
      FIRE_PIT_APPROACH_DISTANCE,
      aspect,
    );
    this.playerInteraction.beginApproach('burnPinecone', obj.id);
    this.showDebugClickTargets(target, stand);
    this.presence.walkTo(stand.x, stand.y, {
      faceTowards: target,
      onArrive: () => {
        if (this.pendingFirepitAction !== 'burn') return;
        this.pendingFirepitAction = null;
        this.performBurnPinecone();
      },
    });
    this.updateDebugWalkDestination(this.presence.getSpace());
    this.updateDebugInteractionLabel();
  }

  private performLightFirePit(): void {
    if (!this.presence) return;
    const space = this.presence.getSpace();
    const fire = firepitStateForYard(space.id);
    if (!fire || fire.phase !== 'off') {
      this.playerInteraction.complete();
      return;
    }

    this.clearKickTimers();
    this.playerInteraction.beginPerform();
    this.characterEl.classList.add('is-lighting-fire');

    const ignite = window.setTimeout(() => {
      fire.phase = 'lit';
      this.syncFirepitVisual(space);
      this.playKickThump();
    }, prefersReducedMotion() ? 0 : Math.round(LIGHT_FIRE_MS * 0.45));
    this.kickTimers.push(ignite);

    const done = window.setTimeout(() => {
      this.characterEl.classList.remove('is-lighting-fire');
      this.playerInteraction.complete();
      this.flushPostKickCommand();
    }, prefersReducedMotion() ? 0 : LIGHT_FIRE_MS);
    this.kickTimers.push(done);
  }

  private performBurnPinecone(): void {
    if (!this.presence) return;
    const space = this.presence.getSpace();
    const fire = firepitStateForYard(space.id);
    const carriedId = this.carriedSlot.getId();
    if (
      !fire ||
      fire.phase === 'off' ||
      !carriedId ||
      !this.carriedSlot.isCarryingPinecone()
    ) {
      this.playerInteraction.complete();
      return;
    }
    const pine = this.pineconeRuntime.get(carriedId);
    if (!pine || pine.state !== 'carried') {
      this.playerInteraction.complete();
      return;
    }

    this.clearKickTimers();
    this.playerInteraction.beginPerform();
    this.characterEl.classList.add('is-depositing');

    const drop = window.setTimeout(() => {
      this.carriedSlot.clear();
      this.characterEl.classList.remove('is-carrying');
      pine.state = 'burned';
      markPineconeBurned(space.id, carriedId);
      const layerEl = this.sceneryLayersEl.querySelector(
        `[data-layer-id="${carriedId}"]`,
      ) as HTMLElement | null;
      if (layerEl) {
        layerEl.classList.remove('is-carried');
        layerEl.classList.add('is-burned');
        layerEl.hidden = true;
      }
      this.triggerFirepitFlare(space, fire);
      this.spawnFirepitSmoke();
      if (this.presence) this.renderProps(this.presence.getSpace());
      this.playKickThump();
    }, prefersReducedMotion() ? 0 : Math.round(BURN_PINECONE_MS * 0.4));
    this.kickTimers.push(drop);

    const done = window.setTimeout(() => {
      this.characterEl.classList.remove('is-depositing');
      this.playerInteraction.complete();
      this.flushPostKickCommand();
    }, prefersReducedMotion() ? 0 : BURN_PINECONE_MS);
    this.kickTimers.push(done);
  }

  /** Attach flame layers once, then mirror the module fire state onto the pit. */
  private syncFirepitVisual(space: PresenceSpace): void {
    const fire = firepitStateForYard(space.id);
    if (!fire) return;
    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${FIRE_PIT_OBJECT_ID}"]`,
    ) as HTMLElement | null;
    if (!layerEl) return;

    let fx = layerEl.querySelector('.wp-firepit-fx') as HTMLElement | null;
    if (!fx) {
      fx = document.createElement('div');
      fx.className = 'wp-firepit-fx';
      fx.setAttribute('aria-hidden', 'true');
      const glow = document.createElement('span');
      glow.className = 'wp-firepit-glow';
      const flames = document.createElement('span');
      flames.className = 'wp-firepit-flames';
      for (const n of ['1', '2', '3']) {
        const flame = document.createElement('span');
        flame.className = `wp-firepit-flame wp-firepit-flame-${n}`;
        flames.append(flame);
      }
      fx.append(glow, flames);
      layerEl.append(fx);
    }

    layerEl.classList.remove('is-fire-off', 'is-fire-lit', 'is-fire-flaring');
    layerEl.classList.add(
      fire.phase === 'off'
        ? 'is-fire-off'
        : fire.phase === 'flaring'
          ? 'is-fire-flaring'
          : 'is-fire-lit',
    );
  }

  private syncBurnedPineconeLayers(): void {
    for (const pine of this.pineconeRuntime.values()) {
      if (pine.state !== 'burned') continue;
      const layerEl = this.sceneryLayersEl.querySelector(
        `[data-layer-id="${pine.id}"]`,
      ) as HTMLElement | null;
      if (!layerEl) continue;
      layerEl.classList.add('is-burned');
      layerEl.hidden = true;
    }
  }

  private triggerFirepitFlare(space: PresenceSpace, fire: FirepitState): void {
    if (this.firepitFlareTimer != null) {
      window.clearTimeout(this.firepitFlareTimer);
      this.firepitFlareTimer = null;
    }
    fire.phase = 'flaring';
    this.syncFirepitVisual(space);
    this.firepitFlareTimer = window.setTimeout(() => {
      fire.phase = 'lit';
      this.firepitFlareTimer = null;
      if (this.currentSpace?.id === space.id) {
        this.syncFirepitVisual(space);
      }
    }, prefersReducedMotion() ? 0 : FIRE_FLARE_MS);
  }

  private spawnFirepitSmoke(): void {
    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${FIRE_PIT_OBJECT_ID}"]`,
    ) as HTMLElement | null;
    const fx = layerEl?.querySelector('.wp-firepit-fx') as HTMLElement | null;
    if (!fx) return;
    const puff = document.createElement('span');
    puff.className = 'wp-firepit-smoke-puff';
    for (let i = 0; i < 4; i += 1) {
      const bit = document.createElement('span');
      bit.className = 'wp-firepit-smoke-bit';
      const drift = (Math.random() - 0.5) * 48;
      bit.style.setProperty('--sx', `${drift}px`);
      bit.style.setProperty('--delay', `${i * 70}ms`);
      puff.append(bit);
    }
    fx.append(puff);
    window.setTimeout(() => puff.remove(), 2_300);
  }

  private performDepositPinecone(): void {
    if (!this.presence) return;
    const bucket = this.bucketRuntime;
    const carriedId = this.carriedSlot.getId();
    if (
      !bucket ||
      bucket.state !== 'ground' ||
      !carriedId ||
      !this.carriedSlot.isCarryingPinecone()
    ) {
      this.playerInteraction.complete();
      return;
    }
    const pine = this.pineconeRuntime.get(carriedId);
    if (!pine || pine.state !== 'carried') {
      this.playerInteraction.complete();
      return;
    }

    this.clearKickTimers();
    this.playerInteraction.beginPerform();
    this.characterEl.classList.add('is-depositing');

    const drop = window.setTimeout(() => {
      this.carriedSlot.clear();
      this.characterEl.classList.remove('is-carrying');
      pine.state = 'bucket';
      pine.containerId = bucket.id;
      if (!bucket.contents.includes(carriedId)) {
        bucket.contents.push(carriedId);
      }
      // Hide pinecone instance — identity preserved in bucket.contents.
      const layerEl = this.sceneryLayersEl.querySelector(
        `[data-layer-id="${carriedId}"]`,
      ) as HTMLElement | null;
      if (layerEl) {
        layerEl.classList.remove('is-carried');
        layerEl.classList.add('is-in-bucket');
        layerEl.hidden = true;
      }
      this.pulseBucketDepositFeedback();
      this.syncBucketVisibility();
      this.maybeCompleteHubertBucket();
      if (this.presence) this.renderProps(this.presence.getSpace());
      this.playKickThump();
    }, prefersReducedMotion() ? 0 : Math.round(DEPOSIT_PINECONE_MS * 0.4));
    this.kickTimers.push(drop);

    const done = window.setTimeout(() => {
      this.characterEl.classList.remove('is-depositing');
      this.playerInteraction.complete();
      this.flushPostKickCommand();
    }, prefersReducedMotion() ? 0 : DEPOSIT_PINECONE_MS);
    this.kickTimers.push(done);
  }


  /** One-shot Hubert completion when all 5 pinecones are in the bucket. */
  private maybeCompleteHubertBucket(): void {
    const bucket = this.bucketRuntime;
    if (!bucket || this.hubertBucketCompleteFired) return;
    if (bucket.contents.length < 5) return;
    this.hubertBucketCompleteFired = true;
    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${BUCKET_OBJECT_ID}"]`,
    ) as HTMLElement | null;
    layerEl?.classList.add('is-hubert-complete');
    this.playKickThump();
    this.logShed('hubert bucket complete (5/5)');
  }

  private pulseBucketDepositFeedback(): void {
    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${BUCKET_OBJECT_ID}"]`,
    ) as HTMLElement | null;
    if (!layerEl) return;
    layerEl.classList.remove('is-deposit-pulse');
    // Restart CSS animation.
    void layerEl.offsetWidth;
    layerEl.classList.add('is-deposit-pulse');
    window.setTimeout(() => layerEl.classList.remove('is-deposit-pulse'), 420);
  }

  /**
   * Update gameplay (runtime) pose for the bucket.
   * Authored editor defaults stay on bucket.authored* — never overwritten here.
   * Live scenery/interactive clones update only for in-session paint/hit tests.
   */
  private applyBucketRuntimePose(
    centerX: number,
    centerY: number,
    rotationDeg: number,
  ): void {
    const bucket = this.bucketRuntime;
    const space = this.presence?.getSpace() ?? this.currentSpace;
    if (!bucket || !space) return;
    bucket.x = centerX;
    bucket.y = centerY;
    bucket.rotation = rotationDeg;

    const layer = space.scenery.layers.find((l) => l.id === bucket.id);
    if (layer) {
      const live = layer as {
        x?: number;
        y?: number;
        rotationDeg?: number;
        depth?: number;
        zIndex: number;
      };
      live.x = centerX;
      live.y = centerY;
      live.rotationDeg = rotationDeg;
      live.depth = Math.round(70 + centerY * 25) + 8;
      live.zIndex = live.depth;
    }

    const obj = space.interactiveObjects?.find((o) => o.id === bucket.id);
    if (obj) {
      const liveObj = obj as InteractiveObject;
      liveObj.x = centerX + bucket.hitOffsetX;
      liveObj.y = centerY + bucket.hitOffsetY;
      liveObj.standX = centerX + bucket.standOffsetX;
      liveObj.standY = centerY + bucket.standOffsetY;
    }

    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${bucket.id}"]`,
    ) as HTMLElement | null;
    if (layerEl) {
      layerEl.style.left = `${centerX * 100}%`;
      layerEl.style.top = `${centerY * 100}%`;
      layerEl.style.setProperty('--wp-prop-rot', `${rotationDeg}deg`);
      layerEl.style.zIndex = String(Math.round(70 + centerY * 25) + 8);
    }
  }

  /** Hide / show bucket layer + filled pinecone tips from runtime state. */
  private syncBucketVisibility(): void {
    const bucket = this.bucketRuntime;
    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${BUCKET_OBJECT_ID}"]`,
    ) as HTMLElement | null;
    if (!bucket || !layerEl) return;

    const editing = this.isWorldEditorActive();
    const stored = bucket.state === 'storedInShed' && !editing;
    const carried = bucket.state === 'carried';
    const revealing = this.shedSequence === 'revealing';
    layerEl.hidden = stored && !revealing;
    layerEl.classList.toggle('is-stored-in-shed', stored && !revealing);
    layerEl.classList.toggle('is-carried', carried);
    layerEl.classList.toggle('is-shed-reveal', revealing);
    const count = bucket.contents.length;
    layerEl.classList.toggle('has-pinecones', count > 0);
    layerEl.dataset.pineconeCount = String(count);
    const fill =
      count <= 0 ? '0' : count <= 2 ? '1-2' : count <= 4 ? '3-4' : '5';
    layerEl.dataset.fill = fill;
    layerEl.classList.toggle('is-fill-0', fill === '0');
    layerEl.classList.toggle('is-fill-1-2', fill === '1-2');
    layerEl.classList.toggle('is-fill-3-4', fill === '3-4');
    layerEl.classList.toggle('is-fill-5', fill === '5');

    // Simple filled feedback: tip markers by band (0 / 1–2 / 3–4 / 5).
    let tips = layerEl.querySelector('.wp-bucket-contents') as HTMLElement | null;
    if (count === 0) {
      tips?.remove();
    } else {
      if (!tips) {
        tips = document.createElement('div');
        tips.className = 'wp-bucket-contents';
        tips.setAttribute('aria-hidden', 'true');
        layerEl.append(tips);
      }
      const show = count <= 2 ? count : count <= 4 ? 3 : 5;
      tips.replaceChildren();
      for (let i = 0; i < show; i++) {
        const tip = document.createElement('span');
        this.decorateBucketPineconeTip(tip, i);
        tips.append(tip);
      }
    }

    // Contained pinecones stay hidden.
    for (const id of bucket.contents) {
      const pineEl = this.sceneryLayersEl.querySelector(
        `[data-layer-id="${id}"]`,
      ) as HTMLElement | null;
      if (pineEl) {
        pineEl.hidden = true;
        pineEl.classList.add('is-in-bucket');
      }
    }
  }

  private beginPineconeLongPress(
    event: PointerEvent,
    obj: InteractiveObject,
  ): void {
    this.clearPineconeLongPress();
    if (!isKickablePinecone(obj.id)) return;
    if (this.carriedSlot.isCarrying()) return;
    const runtime = this.pineconeRuntime.get(obj.id);
    if (runtime && runtime.state !== 'ground') return;

    const target = this.resolvePineconeClickTarget(event, obj);
    this.pineconeLongPressStart = {
      x: event.clientX,
      y: event.clientY,
      objectId: target.id,
    };
    try {
      (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    } catch {
      /* ignore */
    }
    this.pineconeLongPressTimer = window.setTimeout(() => {
      const start = this.pineconeLongPressStart;
      this.pineconeLongPressTimer = null;
      if (!start) return;
      this.pineconeLongPressConsumed = true;
      this.pineconeLongPressStart = null;
      const space = this.presence?.getSpace();
      const live = space?.interactiveObjects?.find((o) => o.id === start.objectId);
      if (!live) return;
      if (this.playerInteraction.isPerforming()) {
        this.postKickCommand = { kind: 'pickup', obj: live };
        return;
      }
      this.cancelPendingKick();
      this.approachPickUpPinecone(live);
      this.updateDebugInteractionLabel();
    }, PINECONE_LONG_PRESS_MS);
  }

  private updatePineconeLongPress(event: PointerEvent): void {
    const start = this.pineconeLongPressStart;
    if (!start || this.pineconeLongPressTimer == null) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.hypot(dx, dy) > PINECONE_LONG_PRESS_MOVE_CANCEL_PX) {
      this.clearPineconeLongPress();
    }
  }

  private endPineconeLongPress(): void {
    // Released before threshold → allow click to kick.
    if (this.pineconeLongPressTimer != null) {
      window.clearTimeout(this.pineconeLongPressTimer);
      this.pineconeLongPressTimer = null;
    }
    this.pineconeLongPressStart = null;
  }

  private clearPineconeLongPress(): void {
    if (this.pineconeLongPressTimer != null) {
      window.clearTimeout(this.pineconeLongPressTimer);
      this.pineconeLongPressTimer = null;
    }
    this.pineconeLongPressStart = null;
  }

  private cancelPendingKick(): void {
    // Never interrupt an in-flight shed↔bucket sequence from other cancels.
    if (this.shedSequence !== 'idle') return;
    this.pendingKickId = null;
    this.pendingPickupId = null;
    this.pendingBucketAction = null;
    this.pendingDepositBucketId = null;
    this.pendingPlacePoint = null;
    this.playerInteraction.cancelApproach();
  }

  private clearKickTimers(): void {
    for (const id of this.kickTimers) window.clearTimeout(id);
    this.kickTimers = [];
  }

  private performKickPinecone(objectId: string): void {
    if (!this.presence) return;
    const runtime = this.pineconeRuntime.get(objectId);
    if (!runtime || runtime.state === 'carried' || runtime.state === 'thrown') {
      this.playerInteraction.complete();
      return;
    }

    this.clearKickTimers();
    this.playerInteraction.beginPerform();
    this.characterEl.classList.add('is-kicking');
    runtime.state = 'ground';

    const char = this.presence.getCharacterPosition();
    const aspect =
      this.presence.getSpace().worldAspectRatio &&
      this.presence.getSpace().worldAspectRatio! > 0
        ? this.presence.getSpace().worldAspectRatio!
        : 16 / 9;
    const target = { x: runtime.x, y: runtime.y };

    const anticipation = window.setTimeout(() => {
      // Contact frame — launch only this instance + soft feedback.
      this.playKickThump();
      this.spawnKickGrassBurst(target.x, target.y);
      const launch = createPineconeKickLaunch(char, target, aspect);
      this.pineconeMotions.set(objectId, {
        id: objectId,
        x: runtime.x,
        y: runtime.y,
        vx: launch.vx,
        vy: launch.vy,
        rotationDeg: runtime.rotation,
        tumbleDegPerSec: launch.tumbleDegPerSec,
        active: true,
      });
      this.startPineconeMotionLoop();
    }, prefersReducedMotion() ? 0 : KICK_ANTICIPATION_MS);
    this.kickTimers.push(anticipation);

    const done = window.setTimeout(() => {
      this.characterEl.classList.remove('is-kicking');
      this.playerInteraction.complete();
      this.flushPostKickCommand();
    }, prefersReducedMotion() ? 0 : KICK_TOTAL_MS);
    this.kickTimers.push(done);
  }

  private performPickUpPinecone(objectId: string): void {
    if (!this.presence) return;
    const runtime = this.pineconeRuntime.get(objectId);
    if (!runtime || runtime.state !== 'ground') {
      this.playerInteraction.complete();
      return;
    }
    // Hard guard — never replace an existing carried object.
    if (this.carriedSlot.isCarrying() && !this.carriedSlot.isCarryingId(objectId)) {
      this.playerInteraction.complete();
      return;
    }

    this.clearKickTimers();
    this.playerInteraction.beginPerform();
    this.characterEl.classList.add('is-picking-up');
    this.pineconeMotions.delete(objectId);

    const contact = window.setTimeout(() => {
      if (!this.carriedSlot.tryCarry(objectId, 'pinecone')) {
        return;
      }
      runtime.state = 'carried';
      this.characterEl.classList.add('is-carrying');
      this.syncCarriedObjectVisual();
    }, prefersReducedMotion() ? 0 : PICKUP_CONTACT_MS);
    this.kickTimers.push(contact);

    // Anticipation lean starts immediately via CSS class.
    void PICKUP_ANTICIPATION_MS;

    const done = window.setTimeout(() => {
      this.characterEl.classList.remove('is-picking-up');
      this.playerInteraction.complete();
      this.flushPostKickCommand();
    }, prefersReducedMotion() ? 0 : PICKUP_TOTAL_MS);
    this.kickTimers.push(done);
  }

  /** Soft thump hook — no asset yet, keep non-blocking. */
  private playKickThump(): void {
    // Future: playSfx('pinecone-tok') when AudioManager + asset exist.
  }

  private spawnKickGrassBurst(x: number, y: number): void {
    if (prefersReducedMotion()) return;
    const burst = document.createElement('div');
    burst.className = 'wp-kick-grass-burst';
    burst.style.left = `${x * 100}%`;
    burst.style.top = `${y * 100}%`;
    for (let i = 0; i < 5; i++) {
      const fleck = document.createElement('span');
      const angle = (Math.PI * 2 * i) / 5 + Math.random() * 0.4;
      const dist = 8 + Math.random() * 14;
      fleck.style.setProperty('--gx', `${Math.cos(angle) * dist}px`);
      fleck.style.setProperty('--gy', `${Math.sin(angle) * dist - 4}px`);
      burst.append(fleck);
    }
    this.worldLayerEl.append(burst);
    window.setTimeout(() => burst.remove(), 480);
  }

  private startPineconeMotionLoop(): void {
    if (this.pineconeMotionRaf != null) return;
    let last = performance.now();
    const tick = (now: number) => {
      this.pineconeMotionRaf = window.requestAnimationFrame(tick);
      const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
      last = now;
      if (!this.presence || this.pineconeMotions.size === 0) {
        this.stopPineconeMotionLoop();
        return;
      }
      const space = this.presence.getSpace();
      for (const [id, motion] of [...this.pineconeMotions.entries()]) {
        if (!motion.active) {
          this.pineconeMotions.delete(id);
          continue;
        }
        const next = stepPineconeMotion(motion, dt, (x, y) =>
          clampWalkTarget(x, y, space),
        );
        this.applyPineconeRuntimePose(id, next.x, next.y, next.rotationDeg);
        if (!next.active) {
          this.pineconeMotions.delete(id);
        } else {
          this.pineconeMotions.set(id, { ...next, id });
        }
      }
      if (this.pineconeMotions.size === 0) {
        this.stopPineconeMotionLoop();
      }
    };
    this.pineconeMotionRaf = window.requestAnimationFrame(tick);
  }

  private stopPineconeMotionLoop(): void {
    if (this.pineconeMotionRaf != null) {
      cancelAnimationFrame(this.pineconeMotionRaf);
      this.pineconeMotionRaf = null;
    }
  }

  /**
   * Write one pinecone instance pose into in-memory space + DOM.
   * Does not touch authored nature-scatter definition files.
   */
  private applyPineconeRuntimePose(
    objectId: string,
    centerX: number,
    centerY: number,
    rotationDeg: number,
  ): void {
    const runtime = this.pineconeRuntime.get(objectId);
    const space = this.presence?.getSpace() ?? this.currentSpace;
    if (!runtime || !space) return;

    runtime.x = centerX;
    runtime.y = centerY;
    runtime.rotation = rotationDeg;
    // Preserve carried state — kick slides stay on ground.

    const layer = space.scenery.layers.find((l) => l.id === objectId);
    // Scenery layers are readonly in the type — mutate the live object for gameplay.
    if (layer) {
      const live = layer as {
        x?: number;
        y?: number;
        rotationDeg?: number;
        depth?: number;
        zIndex: number;
      };
      live.x = centerX;
      live.y = centerY;
      live.rotationDeg = rotationDeg;
      live.depth = Math.round(70 + centerY * 25);
      live.zIndex = live.depth;
    }

    const obj = space.interactiveObjects?.find((o) => o.id === objectId);
    if (obj) {
      const liveObj = obj as InteractiveObject;
      liveObj.x = centerX + runtime.hitOffsetX;
      liveObj.y = centerY + runtime.hitOffsetY;
      liveObj.standX = centerX + runtime.standOffsetX;
      liveObj.standY = centerY + runtime.standOffsetY;
    }

    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${objectId}"]`,
    ) as HTMLElement | null;
    if (layerEl) {
      layerEl.style.left = `${centerX * 100}%`;
      layerEl.style.top = `${centerY * 100}%`;
      layerEl.style.setProperty('--wp-prop-rot', `${rotationDeg}deg`);
      layerEl.style.zIndex = String(Math.round(70 + centerY * 25));
    }

    const hitBtn = this.propsEl.querySelector(
      `[data-object-id="${objectId}"]`,
    ) as HTMLElement | null;
    if (hitBtn && obj) {
      hitBtn.style.left = `${obj.x * 100}%`;
      hitBtn.style.top = `${obj.y * 100}%`;
      hitBtn.style.zIndex = String(depthFromY(obj.y));
    }

    const replenished = this.replenishedPineconeObjects.find(
      (o) => o.id === objectId,
    );
    if (replenished) {
      replenished.x = centerX + runtime.hitOffsetX;
      replenished.y = centerY + runtime.hitOffsetY;
      replenished.standX = centerX + runtime.standOffsetX;
      replenished.standY = centerY + runtime.standOffsetY;
    }
    syncReplenishedPineconePose(space.id, runtime);
  }

  private flushPostKickCommand(): void {
    const cmd = this.postKickCommand;
    this.postKickCommand = null;
    if (!cmd || !this.presence) return;
    if (cmd.kind === 'throw') {
      this.throwCarriedPinecone(cmd.x, cmd.y);
      return;
    }
    if (cmd.kind === 'placeBucket') {
      this.placeCarriedBucketAt(cmd.x, cmd.y);
      return;
    }
    if (cmd.kind === 'ground') {
      this.cancelPendingKick();
      this.dismissFocus();
      this.pendingFocusId = null;
      this.lastInputKind = 'ground';
      this.presence.walkTo(cmd.x, cmd.y);
      this.playerInteraction.noteMoving(true);
      this.updateDebugWalkDestination(this.presence.getSpace());
      return;
    }
    if (cmd.kind === 'pickup') {
      if (isBucketObject(cmd.obj.id)) {
        this.approachPickUpBucket(cmd.obj);
      } else {
        this.approachPickUpPinecone(cmd.obj);
      }
      return;
    }
    this.approachObject(cmd.obj);
  }

  /**
   * Throw the carried pinecone toward a world tap.
   * Object taps (bucket/shed/UI) never reach here — they override via .wp-prop.
   */
  private throwCarriedPinecone(desiredX: number, desiredY: number): void {
    if (!this.presence) return;
    if (!this.carriedSlot.isCarryingPinecone()) return;
    const carriedId = this.carriedSlot.getId();
    if (!carriedId) return;
    const runtime = this.pineconeRuntime.get(carriedId);
    if (!runtime || runtime.state !== 'carried') return;

    const space = this.presence.getSpace();
    const aspect =
      space.worldAspectRatio && space.worldAspectRatio > 0
        ? space.worldAspectRatio
        : 16 / 9;
    const state = this.presence.getState();
    // Release from the visible carry pose — not from feet.
    const release = carriedObjectWorldPose(
      { x: state.characterX, y: state.characterY },
      state.facing,
      getActivePlayableCharacter().pineconeCarryAnchor,
    );
    const plan = createPineconeThrowPlan(
      release,
      { x: desiredX, y: desiredY },
      aspect,
      (x, y) => clampWalkTarget(x, y, space),
    );
    this.showDebugClickTargets({ x: desiredX, y: desiredY }, plan.land);

    this.cancelPendingKick();
    this.playerInteraction.beginPerformDirect('throwPinecone', carriedId);
    this.characterEl.classList.add('is-throwing');
    // Face throw direction for the release (presence overwrites on next walk).
    this.characterEl.dataset.facing =
      plan.land.x >= state.characterX ? 'right' : 'left';

    const releaseTimer = window.setTimeout(() => {
      // Detach from hands at the carry pose.
      this.carriedSlot.clear();
      this.characterEl.classList.remove('is-carrying');
      runtime.state = 'thrown';
      runtime.x = release.x;
      runtime.y = release.y;
      const layerEl = this.sceneryLayersEl.querySelector(
        `[data-layer-id="${carriedId}"]`,
      ) as HTMLElement | null;
      layerEl?.classList.remove('is-carried');
      layerEl?.classList.add('is-thrown');
      this.pineconeThrows.set(carriedId, {
        plan,
        releasedAt: performance.now(),
        bouncing: false,
      });
      this.startPineconeThrowLoop();
      this.playKickThump();
    }, prefersReducedMotion() ? 0 : THROW_ANTICIPATION_MS);
    this.kickTimers.push(releaseTimer);

    const done = window.setTimeout(() => {
      this.characterEl.classList.remove('is-throwing');
      // Interaction may still be in flight — complete after land settles
      // via settleThrownPinecone. If reduced motion lands instantly, settle there.
      if (prefersReducedMotion()) {
        this.settleThrownPinecone(carriedId, plan.land.x, plan.land.y, plan.rotation0);
        this.playerInteraction.complete();
        this.flushPostKickCommand();
      }
    }, prefersReducedMotion() ? 0 : THROW_TOTAL_CHAR_MS);
    this.kickTimers.push(done);
  }

  private startPineconeThrowLoop(): void {
    if (this.pineconeThrowRaf != null) return;
    type Flight = {
      plan: PineconeThrowPlan;
      releasedAt: number;
      bouncing: boolean;
      landX?: number;
      landY?: number;
      landRot?: number;
    };
    const tick = (now: number) => {
      this.pineconeThrowRaf = window.requestAnimationFrame(tick);
      if (this.pineconeThrows.size === 0) {
        this.stopPineconeThrowLoop();
        return;
      }
      for (const [id, raw] of [...this.pineconeThrows.entries()]) {
        const flight = raw as Flight;
        const elapsed = now - flight.releasedAt;
        if (!flight.bouncing) {
          const sample = samplePineconeThrow(flight.plan, elapsed);
          this.applyThrownPineconePose(
            id,
            sample.x,
            sample.y,
            sample.rotationDeg,
            sample.liftPx,
          );
          if (sample.landed) {
            flight.bouncing = true;
            flight.releasedAt = now;
            flight.landX = sample.x;
            flight.landY = sample.y;
            flight.landRot = sample.rotationDeg;
            this.pineconeThrows.set(id, flight);
            this.playKickThump();
            this.spawnKickGrassBurst(sample.x, sample.y);
          }
          continue;
        }
        const bounceElapsed = elapsed;
        const landX = flight.landX ?? flight.plan.land.x;
        const landY = flight.landY ?? flight.plan.land.y;
        const landRot = flight.landRot ?? flight.plan.rotation0;
        if (bounceElapsed < THROW_BOUNCE_MS) {
          const bt = bounceElapsed / THROW_BOUNCE_MS;
          const hop = Math.sin(Math.PI * bt) * (flight.plan.peakLiftPx * 0.22);
          this.applyThrownPineconePose(
            id,
            landX,
            landY,
            landRot + bt * 25,
            hop,
          );
        } else {
          this.pineconeThrows.delete(id);
          this.settleThrownPinecone(id, landX, landY, landRot);
          if (
            this.playerInteraction.getStatus().interactionType ===
            'throwPinecone'
          ) {
            this.characterEl.classList.remove('is-throwing');
            this.playerInteraction.complete();
            this.flushPostKickCommand();
          }
        }
      }
      if (this.pineconeThrows.size === 0) {
        this.stopPineconeThrowLoop();
      }
    };
    this.pineconeThrowRaf = window.requestAnimationFrame(tick);
  }

  private stopPineconeThrowLoop(): void {
    if (this.pineconeThrowRaf != null) {
      cancelAnimationFrame(this.pineconeThrowRaf);
      this.pineconeThrowRaf = null;
    }
  }

  private applyThrownPineconePose(
    objectId: string,
    x: number,
    y: number,
    rotationDeg: number,
    liftPx: number,
  ): void {
    const runtime = this.pineconeRuntime.get(objectId);
    const space = this.presence?.getSpace() ?? this.currentSpace;
    if (!runtime || !space) return;
    runtime.x = x;
    runtime.y = y;
    runtime.rotation = rotationDeg;
    runtime.state = 'thrown';

    const depth = Math.round(70 + y * 25) + 40; // above ground while airborne
    const layer = space.scenery.layers.find((l) => l.id === objectId);
    if (layer) {
      const live = layer as {
        x?: number;
        y?: number;
        rotationDeg?: number;
        depth?: number;
        zIndex: number;
      };
      live.x = x;
      live.y = y;
      live.rotationDeg = rotationDeg;
      live.depth = depth;
      live.zIndex = depth;
    }

    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${objectId}"]`,
    ) as HTMLElement | null;
    if (layerEl) {
      layerEl.classList.add('is-thrown');
      layerEl.style.left = `${x * 100}%`;
      layerEl.style.top = `${y * 100}%`;
      layerEl.style.setProperty('--wp-prop-rot', `${rotationDeg}deg`);
      layerEl.style.setProperty('--wp-throw-lift', `${liftPx}px`);
      layerEl.style.zIndex = String(depth);
    }
  }

  private settleThrownPinecone(
    objectId: string,
    x: number,
    y: number,
    rotationDeg: number,
  ): void {
    const runtime = this.pineconeRuntime.get(objectId);
    if (!runtime) return;
    runtime.state = 'ground';
    this.applyPineconeRuntimePose(objectId, x, y, rotationDeg);

    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${objectId}"]`,
    ) as HTMLElement | null;
    if (layerEl) {
      layerEl.classList.remove('is-thrown', 'is-carried');
      layerEl.style.setProperty('--wp-throw-lift', '0px');
    }

    // Restore ground hotspot so kick / long-press work again.
    if (this.presence) {
      this.renderProps(this.presence.getSpace());
    }
  }

  /** Attach carried pinecone or bucket to the active player's carry anchor. */
  private syncCarriedObjectVisual(): void {
    const carriedId = this.carriedSlot.getId();
    const kind = this.carriedSlot.getKind();
    if (!carriedId || !kind || !this.presence) return;

    const state = this.presence.getState();
    const anchor = kind === 'bucket'
      ? getActivePlayableCharacter().bucketCarryAnchor
      : getActivePlayableCharacter().pineconeCarryAnchor;
    const pose = carriedObjectWorldPose(
      { x: state.characterX, y: state.characterY },
      state.facing,
      anchor,
    );
    const playerDepth = depthFromY(state.characterY);
    const space = this.presence.getSpace();

    if (kind === 'bucket') {
      const bucket = this.bucketRuntime;
      if (!bucket || bucket.state !== 'carried') return;
      bucket.x = pose.x;
      bucket.y = pose.y;
      bucket.rotation = state.facing === 'left' ? -6 : 6;
      const layer = space.scenery.layers.find((l) => l.id === carriedId);
      if (layer) {
        const live = layer as {
          x?: number;
          y?: number;
          rotationDeg?: number;
          depth?: number;
          zIndex: number;
        };
        live.x = pose.x;
        live.y = pose.y;
        live.rotationDeg = bucket.rotation;
        live.depth = playerDepth + 2;
        live.zIndex = playerDepth + 2;
      }
      const layerEl = this.sceneryLayersEl.querySelector(
        `[data-layer-id="${carriedId}"]`,
      ) as HTMLElement | null;
      if (layerEl) {
        layerEl.hidden = false;
        layerEl.classList.add('is-carried');
        layerEl.style.left = `${pose.x * 100}%`;
        layerEl.style.top = `${pose.y * 100}%`;
        layerEl.style.setProperty('--wp-prop-rot', `${bucket.rotation}deg`);
        layerEl.style.zIndex = String(playerDepth + 2);
      }
      // Keep the put-down hotspot glued to the carried bucket.
      const hot = this.propsEl.querySelector(
        `[data-object-id="${BUCKET_OBJECT_ID}"]`,
      ) as HTMLElement | null;
      if (hot) {
        const hx = pose.x + bucket.hitOffsetX;
        const hy = pose.y + bucket.hitOffsetY;
        hot.style.left = `${hx * 100}%`;
        hot.style.top = `${hy * 100}%`;
        hot.style.zIndex = String(playerDepth + 3);
      }
      return;
    }

    const runtime = this.pineconeRuntime.get(carriedId);
    if (!runtime || runtime.state !== 'carried') return;
    runtime.x = pose.x;
    runtime.y = pose.y;
    runtime.rotation = state.facing === 'left' ? -12 : 12;

    const layer = space.scenery.layers.find((l) => l.id === carriedId);
    if (layer) {
      const live = layer as {
        x?: number;
        y?: number;
        rotationDeg?: number;
        depth?: number;
        zIndex: number;
      };
      live.x = pose.x;
      live.y = pose.y;
      live.rotationDeg = runtime.rotation;
      live.depth = playerDepth + 1;
      live.zIndex = playerDepth + 1;
    }

    const layerEl = this.sceneryLayersEl.querySelector(
      `[data-layer-id="${carriedId}"]`,
    ) as HTMLElement | null;
    if (layerEl) {
      layerEl.classList.add('is-carried');
      layerEl.style.left = `${pose.x * 100}%`;
      layerEl.style.top = `${pose.y * 100}%`;
      layerEl.style.setProperty('--wp-prop-rot', `${runtime.rotation}deg`);
      layerEl.style.zIndex = String(playerDepth + 1);
    }

    const hitBtn = this.propsEl.querySelector(
      `[data-object-id="${carriedId}"]`,
    ) as HTMLElement | null;
    if (hitBtn) {
      hitBtn.hidden = true;
      hitBtn.dataset.carried = '1';
    }
  }

  private beginFocus(obj: InteractiveObject): void {
    this.focusObject = obj;
    this.pendingFocusId = null;
    this.root.classList.add('wp-object-focus');
    this.showInteractCard(obj);
    this.animateFocusZoom(this.focusZoomTarget);
    this.statusEl.textContent = obj.title;
    this.updateDebugInteractionLabel();
  }

  private dismissFocus(immediate = false): void {
    if (!this.focusObject && Math.abs(this.focusZoom - this.exploreZoom) < 0.001) {
      this.hideInteractCard();
      return;
    }
    this.focusObject = null;
    this.root.classList.remove('wp-object-focus');
    this.hideInteractCard();
    if (immediate) {
      this.clearFocusAnim();
      this.focusZoom = this.exploreZoom;
      if (this.currentSpace && this.lastPose) {
        this.applyCamera(this.currentSpace, this.lastPose);
      }
      return;
    }
    this.animateFocusZoom(this.exploreZoom);
  }

  private showInteractCard(obj: InteractiveObject): void {
    this.interactCardEl.hidden = false;
    this.interactCardEl.innerHTML = `
      <p class="wp-interact-title">${obj.icon} ${obj.title}</p>
      <button type="button" class="wp-interact-action">${obj.actionLabel}</button>
    `;
    // Position near the object in screen space (approx via world %).
    this.interactCardEl.style.left = `${obj.x * 100}%`;
    this.interactCardEl.style.top = `${Math.max(8, obj.y * 100 - 14)}%`;

    const action = this.interactCardEl.querySelector('.wp-interact-action');
    action?.addEventListener('click', (event) => {
      event.stopPropagation();
      // Placeholder — interaction does nothing yet.
    });
  }

  private hideInteractCard(): void {
    this.interactCardEl.hidden = true;
    this.interactCardEl.innerHTML = '';
  }

  private animateFocusZoom(target: number): void {
    this.clearFocusAnim();
    const reduced = prefersReducedMotion();
    if (reduced) {
      this.focusZoom = target;
      if (this.currentSpace && this.lastPose) {
        this.applyCamera(this.currentSpace, this.lastPose);
      }
      return;
    }

    const start = this.focusZoom;
    const delta = target - start;
    const t0 = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - t0) / FOCUS_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      this.focusZoom = start + delta * eased;
      if (this.currentSpace && this.lastPose) {
        this.applyCamera(this.currentSpace, this.lastPose);
      }
      if (t < 1) {
        this.focusAnim = requestAnimationFrame(tick);
      } else {
        this.focusAnim = null;
        this.focusZoom = target;
      }
    };
    this.focusAnim = requestAnimationFrame(tick);
  }

  private clearFocusAnim(): void {
    if (this.focusAnim != null) {
      cancelAnimationFrame(this.focusAnim);
      this.focusAnim = null;
    }
  }

  /** Match the active player to the yard fence calibration (physical feet × depth). */
  private syncCharacterBaseWidth(space: PresenceSpace, depthScale = 1): void {
    const aspect =
      space.worldAspectRatio && space.worldAspectRatio > 0
        ? space.worldAspectRatio
        : 16 / 9;
    const active = getActivePlayableCharacter();
    const yard = yardScaleFor(space.id);
    const base = characterBaseWidthFrac(
      {
        ...yard,
        characterHeightFeet: active.heightFeet,
        characterDisplayScale: active.displayScale,
      },
      aspect,
      active.imageHeight / Math.max(1, active.imageWidth),
    );
    const widthFrac = base * Math.max(0.01, depthScale);
    this.characterEl.style.setProperty('--wp-char-width', `${widthFrac * 100}%`);
    this.characterEl.style.aspectRatio = `${active.imageWidth} / ${active.imageHeight}`;
  }

  private applyPresenceState(space: PresenceSpace, state: PresenceState): void {
    this.syncCharacterBaseWidth(space, state.characterScale);
    this.characterEl.style.left = `${state.characterX * 100}%`;
    this.characterEl.style.top = `${state.characterY * 100}%`;
    this.characterEl.style.zIndex = String(depthFromY(state.characterY));
    this.characterEl.dataset.facing = state.facing;
    this.characterEl.classList.toggle(
      'is-walking',
      state.walking && !this.playerInteraction.isPerforming(),
    );
    this.playerInteraction.noteMoving(state.walking);
    this.syncCarriedObjectVisual();
    this.debugCharacterEl.style.left = `${state.characterX * 100}%`;
    this.debugCharacterEl.style.top = `${state.characterY * 100}%`;
    this.debugCharacterEl.textContent =
      `pos ${state.characterX.toFixed(2)}, ${state.characterY.toFixed(2)} · ` +
      `scale ${state.characterScale.toFixed(2)}`;
    this.updateDebugCameraViewport();

    this.updatePorchBand(space, state.characterY);
    this.lastPose = state.camera;
    this.applyCamera(space, state.camera);
    if (import.meta.env.DEV) {
      const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
      this.updateModeBadge(playW, playH);
    }
  }

  private updatePorchBand(space: PresenceSpace, characterY: number): void {
    const band = space.porchStandingBand;
    if (!band) {
      this.porchEl.hidden = true;
      this.root.classList.remove('wp-porch-visible');
      return;
    }
    const onPorch = characterY >= band.yMax - 0.18;
    this.porchEl.hidden = !onPorch;
    this.root.classList.toggle('wp-porch-visible', onPorch);
    this.porchLabelEl.textContent = band.label ?? '';
  }

  private applyCamera(space: PresenceSpace, pose: CameraPose): void {
    // Cover/follow must pan 1:1 with camera center; legacy damp was for the
    // old letterboxed phone crop and under-pans a filled viewport.
    const damp =
      this.exploreMode === 'follow' || this.lockedFrameCenter
        ? 1
        : phoneCameraDamp();
    let centerX = pose.centerX;
    let centerY = pose.centerY;

    // Landscape phones lock framing (patio bias) — no character follow pan.
    if (this.lockedFrameCenter && !this.focusObject) {
      centerX = this.lockedFrameCenter.x;
      centerY = this.lockedFrameCenter.y;
    }

    // While focused, gently bias framing toward the object.
    if (this.focusObject) {
      const span = this.focusZoomTarget - this.exploreZoom || 1;
      const t = (this.focusZoom - this.exploreZoom) / span;
      centerX = lerp(centerX, this.focusObject.x, 0.35 * clamp01(t));
      centerY = lerp(centerY, this.focusObject.y, 0.3 * clamp01(t));
    }

    // Desktop fit keeps the full yard framed; phone/tablet still pan/follow.
    // Landscape locked frame applies full pan strength at the fixed center.
    const pan =
      this.lockedFrameCenter && !this.focusObject
        ? 1
        : this.focusObject
          ? Math.max(0.35, explorePanFactor(this.exploreMode))
          : explorePanFactor(this.exploreMode);
    const panW = this.worldPanWidth || space.worldWidth;
    const panH = this.worldPanHeight || space.worldHeight;
    const strength = damp * pan;
    let offsetX: number;
    let offsetY: number;

    if (this.activeSceneWorld) {
      // Fixed-pixel SceneWorld: % is of the world plate; scale applies after
      // translate — do not multiply offsets by zoom or framing factors.
      const { halfW, halfH } = visibleWorldHalfExtents(
        this.focusZoom,
        panW,
        panH,
      );
      centerX = Math.min(1 - halfW, Math.max(halfW, centerX));
      centerY = Math.min(1 - halfH, Math.max(halfH, centerY));

      // Mobile: pixel translate avoids Safari calc(%)+scale subpixel gutters.
      // Desktop keeps the locked % pan path.
      if (this.backyardPresentationMode === 'mobile') {
        const { playW, playH } = measurePlayfieldSize(this.playfieldEl);
        const world = this.activeSceneWorld;
        const z = this.focusZoom;
        const maxPanX = Math.max(0, (world.widthPx * z - playW) / 2);
        const maxPanY = Math.max(0, (world.heightPx * z - playH) / 2);
        const rawX = (0.5 - centerX) * world.widthPx * z * strength;
        const rawY = (0.5 - centerY) * world.heightPx * z * strength;
        const tx = Math.min(maxPanX, Math.max(-maxPanX, rawX));
        const ty = Math.min(maxPanY, Math.max(-maxPanY, rawY));
        this.worldLayerEl.style.left = '50%';
        this.worldLayerEl.style.top = '50%';
        this.worldLayerEl.style.transform =
          `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${z})`;
        this.layoutArtworkBleed();
        this.updateDebugCameraViewport();
        return;
      }

      offsetX = clampFixedWorldPanOffsetPercent(
        fixedWorldPanOffsetPercent(centerX, strength),
        panW,
        this.focusZoom,
      );
      offsetY = clampFixedWorldPanOffsetPercent(
        fixedWorldPanOffsetPercent(centerY, strength),
        panH,
        this.focusZoom,
      );
    } else {
      // Legacy playfield-relative layers still need zoom-scaled % pan.
      const zoomPan =
        this.exploreMode === 'follow' || this.lockedFrameCenter
          ? this.focusZoom
          : 1;
      offsetX = clampCoverPanOffsetPercent(
        cameraPanTranslatePercent(centerX, panW, zoomPan, strength),
        panW,
        this.focusZoom,
      );
      offsetY = clampCoverPanOffsetPercent(
        cameraPanTranslatePercent(centerY, panH, zoomPan, strength),
        panH,
        this.focusZoom,
      );
    }
    this.worldLayerEl.style.transform =
      `translate(calc(-50% + ${offsetX}%), calc(-50% + ${offsetY}%)) scale(${this.focusZoom})`;
    this.layoutArtworkBleed();
    this.updateDebugCameraViewport();
  }

  private updateDebugCameraViewport(): void {
    if (!this.debugEnabled) return;
    const play = this.playfieldEl.getBoundingClientRect();
    const world = this.worldLayerEl.getBoundingClientRect();
    if (play.width <= 0 || play.height <= 0 || world.width <= 0 || world.height <= 0) {
      return;
    }
    const left = (play.left - world.left) / world.width;
    const top = (play.top - world.top) / world.height;
    const width = play.width / world.width;
    const height = play.height / world.height;
    this.debugCameraViewportEl.style.left = `${left * 100}%`;
    this.debugCameraViewportEl.style.top = `${top * 100}%`;
    this.debugCameraViewportEl.style.width = `${width * 100}%`;
    this.debugCameraViewportEl.style.height = `${height * 100}%`;
  }

  private renderUtility(
    utilities: readonly {
      id: string;
      label: string;
      targetLocationId: LocationId;
    }[],
  ): void {
    this.utilityEl.innerHTML = '';
    const world = this.navigator.getWorld();
    for (const utility of utilities) {
      const destName = getLocationDisplayName(world, utility.targetLocationId);
      const onActivate = () => {
        this.previousLocationId = this.navigator.getCurrentLocationId();
        this.navigator.activateUtilityTravel(utility.id);
      };

      if (isBackToMapUtility(utility)) {
        const button = createBackToMapButton({
          title: `${utility.label} → ${destName}`,
          onActivate,
        });
        this.utilityEl.append(button);
        continue;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wp-utility-btn';
      button.textContent = utility.label;
      button.title = `${utility.label} → ${destName}`;
      button.addEventListener('click', onActivate);
      this.utilityEl.append(button);
    }
  }

  private async renderWithTravelFade(): Promise<void> {
    if (this.navigator.isLocked()) return;
    this.navigator.setLocked(true);
    markTravelPhase('route_transition_start');

    const reduced = prefersReducedMotion();
    const destSpace = this.navigator.getCurrentSpace();
    const isBackyard = isBackyardLocationId(destSpace?.id);
    // Prioritize the backyard plate; decorative props attach after first frame.
    this.deferDecorativeArt = Boolean(isBackyard);

    if (!reduced) {
      this.sceneEl.classList.add('is-fading');
    }
    await wait(reduced ? 0 : FADE_MS);
    markTravelPhase('fade_out_done');

    markTravelPhase('scene_creation_start', destSpace?.id ?? 'hub');
    this.render();
    markTravelPhase('scene_objects_created');

    if (isBackyard) {
      try {
        const timing = await waitForImageReady(this.worldArtworkImgEl);
        markTravelPhase(
          'background_downloaded',
          `${timing.downloadedMs.toFixed(0)}ms`,
        );
        markTravelPhase(
          'background_decoded',
          `${timing.decodedMs.toFixed(0)}ms`,
        );
      } catch {
        markTravelPhase('background_decoded', 'error');
      }
      // First paint with master + character before decorative competition.
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      markTravelPhase('first_backyard_frame');
    } else {
      markTravelPhase('first_backyard_frame', 'non-backyard');
    }

    this.sceneEl.classList.remove('is-fading');
    await wait(reduced ? 40 : TRAVEL_SOFT_FADE_MS);
    this.navigator.setLocked(false);
    this.gestureConsumed = false;
    markTravelPhase('movement_enabled');

    if (this.deferDecorativeArt) {
      markTravelPhase('prop_assets_requested');
      this.attachDeferredDecorativeArt();
      this.deferDecorativeArt = false;
      markTravelPhase('prop_assets_downloaded', 'requested');
    }

    finishTravelLoad();
  }

  /** Assign deferred scenery layer images after the backyard plate is visible. */
  private attachDeferredDecorativeArt(): void {
    const pending = this.sceneryLayersEl.querySelectorAll<HTMLElement>(
      '[data-pending-src]',
    );
    for (const el of pending) {
      if (el.dataset.layerId === SHED_OBJECT_ID) {
        const src =
          el.dataset.pendingSrc ??
          el.dataset.closedSrc ??
          this.shedClosedSrc ??
          null;
        delete el.dataset.pendingSrc;
        if (src) {
          el.dataset.closedSrc = src;
          el.style.backgroundImage = runtimeCssUrl(src);
        }
        el.hidden = false;
        continue;
      }
      const src = el.dataset.pendingSrc;
      if (!src) continue;
      el.style.backgroundImage = runtimeCssUrl(src);
      delete el.dataset.pendingSrc;
    }
  }
}

function phoneCameraDamp(): number {
  if (typeof window === 'undefined' || !window.matchMedia) return 1;
  if (window.matchMedia('(max-width: 767px)').matches) return 0.7;
  if (window.matchMedia('(max-width: 1024px)').matches) return 0.85;
  return 1;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * When a space locks world aspect to master artwork, CSS height follows width.
 * Fit-zoom math must use that effective height factor vs the playfield.
 */
function resolveWorldSizeFactors(
  space: PresenceSpace,
  playfieldWidth: number,
  playfieldHeight: number,
): { worldWidth: number; worldHeight: number } {
  if (!space.worldAspectRatio || space.worldAspectRatio <= 0) {
    return {
      worldWidth: space.worldWidth,
      worldHeight: space.worldHeight,
    };
  }
  const playAspect = playfieldWidth / Math.max(1, playfieldHeight);
  return {
    worldWidth: space.worldWidth,
    worldHeight:
      (space.worldWidth * playAspect) / space.worldAspectRatio,
  };
}

/**
 * Playfield size for camera math.
 * Prefer the playfield layout box so width-only and height-only resizes
 * both register. On mobile, clamp to visualViewport so Safari toolbars
 * cannot leave the shell taller than the usable surface (cream gutters).
 * Do not substitute window.inner* on desktop — that can disagree with the
 * actual game surface during continuous edge drags.
 */
function measurePlayfieldSize(playfieldEl: HTMLElement): {
  playW: number;
  playH: number;
} {
  const rect = playfieldEl.getBoundingClientRect();
  let playW = Math.max(1, rect.width);
  let playH = Math.max(1, rect.height);
  const vv = window.visualViewport;
  if (!vv) return { playW, playH };

  const isPhonePlayfield = isMobilePresentationPlayfield(playW, playH);
  if (isPhonePlayfield) {
    // Portrait + landscape phones: usable area is visualViewport.
    playW = Math.max(1, Math.min(playW, vv.width));
    playH = Math.max(1, Math.min(playH, vv.height));
  }
  return { playW, playH };
}

function clampWalkTarget(
  x: number,
  y: number,
  space: PresenceSpace,
): { x: number; y: number } {
  if (space.walkPolygon && space.walkPolygon.length >= 3) {
    return clampToPolygon(x, y, space.walkPolygon);
  }
  return clampToBounds(x, y, space.walkBounds);
}
